/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const oe = globalThis, Be = oe.ShadowRoot && (oe.ShadyCSS === void 0 || oe.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Oe = Symbol(), Xe = /* @__PURE__ */ new WeakMap();
let yi = class {
  constructor(e, i, o) {
    if (this._$cssResult$ = !0, o !== Oe) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = i;
  }
  get styleSheet() {
    let e = this.o;
    const i = this.t;
    if (Be && e === void 0) {
      const o = i !== void 0 && i.length === 1;
      o && (e = Xe.get(i)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), o && Xe.set(i, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const H = (t) => new yi(typeof t == "string" ? t : t + "", void 0, Oe), k = (t, ...e) => {
  const i = t.length === 1 ? t[0] : e.reduce((o, s, r) => o + ((n) => {
    if (n._$cssResult$ === !0) return n.cssText;
    if (typeof n == "number") return n;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + n + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(s) + t[r + 1], t[0]);
  return new yi(i, t, Oe);
}, to = (t, e) => {
  if (Be) t.adoptedStyleSheets = e.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet);
  else for (const i of e) {
    const o = document.createElement("style"), s = oe.litNonce;
    s !== void 0 && o.setAttribute("nonce", s), o.textContent = i.cssText, t.appendChild(o);
  }
}, Qe = Be ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let i = "";
  for (const o of e.cssRules) i += o.cssText;
  return H(i);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: eo, defineProperty: io, getOwnPropertyDescriptor: oo, getOwnPropertyNames: so, getOwnPropertySymbols: ro, getPrototypeOf: no } = Object, ht = globalThis, Ze = ht.trustedTypes, ao = Ze ? Ze.emptyScript : "", $e = ht.reactiveElementPolyfillSupport, Ut = (t, e) => t, ae = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? ao : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let i = t;
  switch (e) {
    case Boolean:
      i = t !== null;
      break;
    case Number:
      i = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        i = JSON.parse(t);
      } catch {
        i = null;
      }
  }
  return i;
} }, Ue = (t, e) => !eo(t, e), ti = { attribute: !0, type: String, converter: ae, reflect: !1, useDefault: !1, hasChanged: Ue };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), ht.litPropertyMetadata ?? (ht.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let Et = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, i = ti) {
    if (i.state && (i.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(e) && ((i = Object.create(i)).wrapped = !0), this.elementProperties.set(e, i), !i.noAccessor) {
      const o = Symbol(), s = this.getPropertyDescriptor(e, o, i);
      s !== void 0 && io(this.prototype, e, s);
    }
  }
  static getPropertyDescriptor(e, i, o) {
    const { get: s, set: r } = oo(this.prototype, e) ?? { get() {
      return this[i];
    }, set(n) {
      this[i] = n;
    } };
    return { get: s, set(n) {
      const a = s == null ? void 0 : s.call(this);
      r == null || r.call(this, n), this.requestUpdate(e, a, o);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? ti;
  }
  static _$Ei() {
    if (this.hasOwnProperty(Ut("elementProperties"))) return;
    const e = no(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(Ut("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(Ut("properties"))) {
      const i = this.properties, o = [...so(i), ...ro(i)];
      for (const s of o) this.createProperty(s, i[s]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const i = litPropertyMetadata.get(e);
      if (i !== void 0) for (const [o, s] of i) this.elementProperties.set(o, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [i, o] of this.elementProperties) {
      const s = this._$Eu(i, o);
      s !== void 0 && this._$Eh.set(s, i);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const i = [];
    if (Array.isArray(e)) {
      const o = new Set(e.flat(1 / 0).reverse());
      for (const s of o) i.unshift(Qe(s));
    } else e !== void 0 && i.push(Qe(e));
    return i;
  }
  static _$Eu(e, i) {
    const o = i.attribute;
    return o === !1 ? void 0 : typeof o == "string" ? o : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((i) => this.enableUpdating = i), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((i) => i(this));
  }
  addController(e) {
    var i;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((i = e.hostConnected) == null || i.call(e));
  }
  removeController(e) {
    var i;
    (i = this._$EO) == null || i.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), i = this.constructor.elementProperties;
    for (const o of i.keys()) this.hasOwnProperty(o) && (e.set(o, this[o]), delete this[o]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return to(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostConnected) == null ? void 0 : o.call(i);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostDisconnected) == null ? void 0 : o.call(i);
    });
  }
  attributeChangedCallback(e, i, o) {
    this._$AK(e, o);
  }
  _$ET(e, i) {
    var r;
    const o = this.constructor.elementProperties.get(e), s = this.constructor._$Eu(e, o);
    if (s !== void 0 && o.reflect === !0) {
      const n = (((r = o.converter) == null ? void 0 : r.toAttribute) !== void 0 ? o.converter : ae).toAttribute(i, o.type);
      this._$Em = e, n == null ? this.removeAttribute(s) : this.setAttribute(s, n), this._$Em = null;
    }
  }
  _$AK(e, i) {
    var r, n;
    const o = this.constructor, s = o._$Eh.get(e);
    if (s !== void 0 && this._$Em !== s) {
      const a = o.getPropertyOptions(s), l = typeof a.converter == "function" ? { fromAttribute: a.converter } : ((r = a.converter) == null ? void 0 : r.fromAttribute) !== void 0 ? a.converter : ae;
      this._$Em = s, this[s] = l.fromAttribute(i, a.type) ?? ((n = this._$Ej) == null ? void 0 : n.get(s)) ?? null, this._$Em = null;
    }
  }
  requestUpdate(e, i, o) {
    var s;
    if (e !== void 0) {
      const r = this.constructor, n = this[e];
      if (o ?? (o = r.getPropertyOptions(e)), !((o.hasChanged ?? Ue)(n, i) || o.useDefault && o.reflect && n === ((s = this._$Ej) == null ? void 0 : s.get(e)) && !this.hasAttribute(r._$Eu(e, o)))) return;
      this.C(e, i, o);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(e, i, { useDefault: o, reflect: s, wrapped: r }, n) {
    o && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(e) && (this._$Ej.set(e, n ?? i ?? this[e]), r !== !0 || n !== void 0) || (this._$AL.has(e) || (this.hasUpdated || o || (i = void 0), this._$AL.set(e, i)), s === !0 && this._$Em !== e && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(e));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (i) {
      Promise.reject(i);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var o;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [r, n] of this._$Ep) this[r] = n;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [r, n] of s) {
        const { wrapped: a } = n, l = this[r];
        a !== !0 || this._$AL.has(r) || l === void 0 || this.C(r, void 0, n, l);
      }
    }
    let e = !1;
    const i = this._$AL;
    try {
      e = this.shouldUpdate(i), e ? (this.willUpdate(i), (o = this._$EO) == null || o.forEach((s) => {
        var r;
        return (r = s.hostUpdate) == null ? void 0 : r.call(s);
      }), this.update(i)) : this._$EM();
    } catch (s) {
      throw e = !1, this._$EM(), s;
    }
    e && this._$AE(i);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var i;
    (i = this._$EO) == null || i.forEach((o) => {
      var s;
      return (s = o.hostUpdated) == null ? void 0 : s.call(o);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((i) => this._$ET(i, this[i]))), this._$EM();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
Et.elementStyles = [], Et.shadowRootOptions = { mode: "open" }, Et[Ut("elementProperties")] = /* @__PURE__ */ new Map(), Et[Ut("finalized")] = /* @__PURE__ */ new Map(), $e == null || $e({ ReactiveElement: Et }), (ht.reactiveElementVersions ?? (ht.reactiveElementVersions = [])).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ft = globalThis, le = Ft.trustedTypes, ei = le ? le.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, ji = "$lit$", dt = `lit$${Math.random().toFixed(9).slice(2)}$`, ki = "?" + dt, lo = `<${ki}>`, jt = document, Ht = () => jt.createComment(""), qt = (t) => t === null || typeof t != "object" && typeof t != "function", Fe = Array.isArray, co = (t) => Fe(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", ze = `[ 	
\f\r]`, Bt = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ii = /-->/g, oi = />/g, vt = RegExp(`>|${ze}(?:([^\\s"'>=/]+)(${ze}*=${ze}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), si = /'/g, ri = /"/g, $i = /^(?:script|style|textarea|title)$/i, ho = (t) => (e, ...i) => ({ _$litType$: t, strings: e, values: i }), u = ho(1), Ct = Symbol.for("lit-noChange"), R = Symbol.for("lit-nothing"), ni = /* @__PURE__ */ new WeakMap(), mt = jt.createTreeWalker(jt, 129);
function zi(t, e) {
  if (!Fe(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return ei !== void 0 ? ei.createHTML(e) : e;
}
const po = (t, e) => {
  const i = t.length - 1, o = [];
  let s, r = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", n = Bt;
  for (let a = 0; a < i; a++) {
    const l = t[a];
    let d, h, p = -1, w = 0;
    for (; w < l.length && (n.lastIndex = w, h = n.exec(l), h !== null); ) w = n.lastIndex, n === Bt ? h[1] === "!--" ? n = ii : h[1] !== void 0 ? n = oi : h[2] !== void 0 ? ($i.test(h[2]) && (s = RegExp("</" + h[2], "g")), n = vt) : h[3] !== void 0 && (n = vt) : n === vt ? h[0] === ">" ? (n = s ?? Bt, p = -1) : h[1] === void 0 ? p = -2 : (p = n.lastIndex - h[2].length, d = h[1], n = h[3] === void 0 ? vt : h[3] === '"' ? ri : si) : n === ri || n === si ? n = vt : n === ii || n === oi ? n = Bt : (n = vt, s = void 0);
    const x = n === vt && t[a + 1].startsWith("/>") ? " " : "";
    r += n === Bt ? l + lo : p >= 0 ? (o.push(d), l.slice(0, p) + ji + l.slice(p) + dt + x) : l + dt + (p === -2 ? a : x);
  }
  return [zi(t, r + (t[i] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), o];
};
class Vt {
  constructor({ strings: e, _$litType$: i }, o) {
    let s;
    this.parts = [];
    let r = 0, n = 0;
    const a = e.length - 1, l = this.parts, [d, h] = po(e, i);
    if (this.el = Vt.createElement(d, o), mt.currentNode = this.el.content, i === 2 || i === 3) {
      const p = this.el.content.firstChild;
      p.replaceWith(...p.childNodes);
    }
    for (; (s = mt.nextNode()) !== null && l.length < a; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const p of s.getAttributeNames()) if (p.endsWith(ji)) {
          const w = h[n++], x = s.getAttribute(p).split(dt), f = /([.?@])?(.*)/.exec(w);
          l.push({ type: 1, index: r, name: f[2], strings: x, ctor: f[1] === "." ? go : f[1] === "?" ? fo : f[1] === "@" ? wo : he }), s.removeAttribute(p);
        } else p.startsWith(dt) && (l.push({ type: 6, index: r }), s.removeAttribute(p));
        if ($i.test(s.tagName)) {
          const p = s.textContent.split(dt), w = p.length - 1;
          if (w > 0) {
            s.textContent = le ? le.emptyScript : "";
            for (let x = 0; x < w; x++) s.append(p[x], Ht()), mt.nextNode(), l.push({ type: 2, index: ++r });
            s.append(p[w], Ht());
          }
        }
      } else if (s.nodeType === 8) if (s.data === ki) l.push({ type: 2, index: r });
      else {
        let p = -1;
        for (; (p = s.data.indexOf(dt, p + 1)) !== -1; ) l.push({ type: 7, index: r }), p += dt.length - 1;
      }
      r++;
    }
  }
  static createElement(e, i) {
    const o = jt.createElement("template");
    return o.innerHTML = e, o;
  }
}
function Dt(t, e, i = t, o) {
  var n, a;
  if (e === Ct) return e;
  let s = o !== void 0 ? (n = i._$Co) == null ? void 0 : n[o] : i._$Cl;
  const r = qt(e) ? void 0 : e._$litDirective$;
  return (s == null ? void 0 : s.constructor) !== r && ((a = s == null ? void 0 : s._$AO) == null || a.call(s, !1), r === void 0 ? s = void 0 : (s = new r(t), s._$AT(t, i, o)), o !== void 0 ? (i._$Co ?? (i._$Co = []))[o] = s : i._$Cl = s), s !== void 0 && (e = Dt(t, s._$AS(t, e.values), s, o)), e;
}
class uo {
  constructor(e, i) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = i;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: i }, parts: o } = this._$AD, s = ((e == null ? void 0 : e.creationScope) ?? jt).importNode(i, !0);
    mt.currentNode = s;
    let r = mt.nextNode(), n = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (n === l.index) {
        let d;
        l.type === 2 ? d = new Jt(r, r.nextSibling, this, e) : l.type === 1 ? d = new l.ctor(r, l.name, l.strings, this, e) : l.type === 6 && (d = new vo(r, this, e)), this._$AV.push(d), l = o[++a];
      }
      n !== (l == null ? void 0 : l.index) && (r = mt.nextNode(), n++);
    }
    return mt.currentNode = jt, s;
  }
  p(e) {
    let i = 0;
    for (const o of this._$AV) o !== void 0 && (o.strings !== void 0 ? (o._$AI(e, o, i), i += o.strings.length - 2) : o._$AI(e[i])), i++;
  }
}
class Jt {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, i, o, s) {
    this.type = 2, this._$AH = R, this._$AN = void 0, this._$AA = e, this._$AB = i, this._$AM = o, this.options = s, this._$Cv = (s == null ? void 0 : s.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const i = this._$AM;
    return i !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = i.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, i = this) {
    e = Dt(this, e, i), qt(e) ? e === R || e == null || e === "" ? (this._$AH !== R && this._$AR(), this._$AH = R) : e !== this._$AH && e !== Ct && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : co(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== R && qt(this._$AH) ? this._$AA.nextSibling.data = e : this.T(jt.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var r;
    const { values: i, _$litType$: o } = e, s = typeof o == "number" ? this._$AC(e) : (o.el === void 0 && (o.el = Vt.createElement(zi(o.h, o.h[0]), this.options)), o);
    if (((r = this._$AH) == null ? void 0 : r._$AD) === s) this._$AH.p(i);
    else {
      const n = new uo(s, this), a = n.u(this.options);
      n.p(i), this.T(a), this._$AH = n;
    }
  }
  _$AC(e) {
    let i = ni.get(e.strings);
    return i === void 0 && ni.set(e.strings, i = new Vt(e)), i;
  }
  k(e) {
    Fe(this._$AH) || (this._$AH = [], this._$AR());
    const i = this._$AH;
    let o, s = 0;
    for (const r of e) s === i.length ? i.push(o = new Jt(this.O(Ht()), this.O(Ht()), this, this.options)) : o = i[s], o._$AI(r), s++;
    s < i.length && (this._$AR(o && o._$AB.nextSibling, s), i.length = s);
  }
  _$AR(e = this._$AA.nextSibling, i) {
    var o;
    for ((o = this._$AP) == null ? void 0 : o.call(this, !1, !0, i); e && e !== this._$AB; ) {
      const s = e.nextSibling;
      e.remove(), e = s;
    }
  }
  setConnected(e) {
    var i;
    this._$AM === void 0 && (this._$Cv = e, (i = this._$AP) == null || i.call(this, e));
  }
}
class he {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, i, o, s, r) {
    this.type = 1, this._$AH = R, this._$AN = void 0, this.element = e, this.name = i, this._$AM = s, this.options = r, o.length > 2 || o[0] !== "" || o[1] !== "" ? (this._$AH = Array(o.length - 1).fill(new String()), this.strings = o) : this._$AH = R;
  }
  _$AI(e, i = this, o, s) {
    const r = this.strings;
    let n = !1;
    if (r === void 0) e = Dt(this, e, i, 0), n = !qt(e) || e !== this._$AH && e !== Ct, n && (this._$AH = e);
    else {
      const a = e;
      let l, d;
      for (e = r[0], l = 0; l < r.length - 1; l++) d = Dt(this, a[o + l], i, l), d === Ct && (d = this._$AH[l]), n || (n = !qt(d) || d !== this._$AH[l]), d === R ? e = R : e !== R && (e += (d ?? "") + r[l + 1]), this._$AH[l] = d;
    }
    n && !s && this.j(e);
  }
  j(e) {
    e === R ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class go extends he {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === R ? void 0 : e;
  }
}
class fo extends he {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== R);
  }
}
class wo extends he {
  constructor(e, i, o, s, r) {
    super(e, i, o, s, r), this.type = 5;
  }
  _$AI(e, i = this) {
    if ((e = Dt(this, e, i, 0) ?? R) === Ct) return;
    const o = this._$AH, s = e === R && o !== R || e.capture !== o.capture || e.once !== o.once || e.passive !== o.passive, r = e !== R && (o === R || s);
    s && this.element.removeEventListener(this.name, this, o), r && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var i;
    typeof this._$AH == "function" ? this._$AH.call(((i = this.options) == null ? void 0 : i.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class vo {
  constructor(e, i, o) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = i, this.options = o;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    Dt(this, e);
  }
}
const Te = Ft.litHtmlPolyfillSupport;
Te == null || Te(Vt, Jt), (Ft.litHtmlVersions ?? (Ft.litHtmlVersions = [])).push("3.3.0");
const mo = (t, e, i) => {
  const o = (i == null ? void 0 : i.renderBefore) ?? e;
  let s = o._$litPart$;
  if (s === void 0) {
    const r = (i == null ? void 0 : i.renderBefore) ?? null;
    o._$litPart$ = s = new Jt(e.insertBefore(Ht(), r), r, void 0, i ?? {});
  }
  return s._$AI(t), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const xt = globalThis;
class A extends Et {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var i;
    const e = super.createRenderRoot();
    return (i = this.renderOptions).renderBefore ?? (i.renderBefore = e.firstChild), e;
  }
  update(e) {
    const i = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = mo(i, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return Ct;
  }
}
var xi;
A._$litElement$ = !0, A.finalized = !0, (xi = xt.litElementHydrateSupport) == null || xi.call(xt, { LitElement: A });
const Ee = xt.litElementPolyfillSupport;
Ee == null || Ee({ LitElement: A });
(xt.litElementVersions ?? (xt.litElementVersions = [])).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const _ = (t) => (e, i) => {
  i !== void 0 ? i.addInitializer(() => {
    customElements.define(t, e);
  }) : customElements.define(t, e);
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const bo = { attribute: !0, type: String, converter: ae, reflect: !1, hasChanged: Ue }, xo = (t = bo, e, i) => {
  const { kind: o, metadata: s } = i;
  let r = globalThis.litPropertyMetadata.get(s);
  if (r === void 0 && globalThis.litPropertyMetadata.set(s, r = /* @__PURE__ */ new Map()), o === "setter" && ((t = Object.create(t)).wrapped = !0), r.set(i.name, t), o === "accessor") {
    const { name: n } = i;
    return { set(a) {
      const l = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(n, l, t);
    }, init(a) {
      return a !== void 0 && this.C(n, void 0, t, a), a;
    } };
  }
  if (o === "setter") {
    const { name: n } = i;
    return function(a) {
      const l = this[n];
      e.call(this, a), this.requestUpdate(n, l, t);
    };
  }
  throw Error("Unsupported decorator location: " + o);
};
function g(t) {
  return (e, i) => typeof i == "object" ? xo(t, e, i) : ((o, s, r) => {
    const n = s.hasOwnProperty(r);
    return s.constructor.createProperty(r, o), n ? Object.getOwnPropertyDescriptor(s, r) : void 0;
  })(t, e, i);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function m(t) {
  return g({ ...t, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const yo = (t, e, i) => (i.configurable = !0, i.enumerable = !0, Reflect.decorate && typeof e != "object" && Object.defineProperty(t, e, i), i);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function ft(t, e) {
  return (i, o, s) => {
    const r = (n) => {
      var a;
      return ((a = n.renderRoot) == null ? void 0 : a.querySelector(t)) ?? null;
    };
    return yo(i, o, { get() {
      return r(this);
    } });
  };
}
function Ti(t, e) {
  return function() {
    return t.apply(e, arguments);
  };
}
const { toString: jo } = Object.prototype, { getPrototypeOf: Ne } = Object, { iterator: pe, toStringTag: Ei } = Symbol, ue = /* @__PURE__ */ ((t) => (e) => {
  const i = jo.call(e);
  return t[i] || (t[i] = i.slice(8, -1).toLowerCase());
})(/* @__PURE__ */ Object.create(null)), Q = (t) => (t = t.toLowerCase(), (e) => ue(e) === t), ge = (t) => (e) => typeof e === t, { isArray: Rt } = Array, Kt = ge("undefined");
function ko(t) {
  return t !== null && !Kt(t) && t.constructor !== null && !Kt(t.constructor) && B(t.constructor.isBuffer) && t.constructor.isBuffer(t);
}
const Si = Q("ArrayBuffer");
function $o(t) {
  let e;
  return typeof ArrayBuffer < "u" && ArrayBuffer.isView ? e = ArrayBuffer.isView(t) : e = t && t.buffer && Si(t.buffer), e;
}
const zo = ge("string"), B = ge("function"), Ii = ge("number"), fe = (t) => t !== null && typeof t == "object", To = (t) => t === !0 || t === !1, se = (t) => {
  if (ue(t) !== "object")
    return !1;
  const e = Ne(t);
  return (e === null || e === Object.prototype || Object.getPrototypeOf(e) === null) && !(Ei in t) && !(pe in t);
}, Eo = Q("Date"), So = Q("File"), Io = Q("Blob"), Co = Q("FileList"), Do = (t) => fe(t) && B(t.pipe), Ro = (t) => {
  let e;
  return t && (typeof FormData == "function" && t instanceof FormData || B(t.append) && ((e = ue(t)) === "formdata" || // detect form-data instance
  e === "object" && B(t.toString) && t.toString() === "[object FormData]"));
}, Ao = Q("URLSearchParams"), [_o, Lo, Po, Mo] = ["ReadableStream", "Request", "Response", "Headers"].map(Q), Bo = (t) => t.trim ? t.trim() : t.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, "");
function Wt(t, e, { allOwnKeys: i = !1 } = {}) {
  if (t === null || typeof t > "u")
    return;
  let o, s;
  if (typeof t != "object" && (t = [t]), Rt(t))
    for (o = 0, s = t.length; o < s; o++)
      e.call(null, t[o], o, t);
  else {
    const r = i ? Object.getOwnPropertyNames(t) : Object.keys(t), n = r.length;
    let a;
    for (o = 0; o < n; o++)
      a = r[o], e.call(null, t[a], a, t);
  }
}
function Ci(t, e) {
  e = e.toLowerCase();
  const i = Object.keys(t);
  let o = i.length, s;
  for (; o-- > 0; )
    if (s = i[o], e === s.toLowerCase())
      return s;
  return null;
}
const bt = typeof globalThis < "u" ? globalThis : typeof self < "u" ? self : typeof window < "u" ? window : global, Di = (t) => !Kt(t) && t !== bt;
function De() {
  const { caseless: t } = Di(this) && this || {}, e = {}, i = (o, s) => {
    const r = t && Ci(e, s) || s;
    se(e[r]) && se(o) ? e[r] = De(e[r], o) : se(o) ? e[r] = De({}, o) : Rt(o) ? e[r] = o.slice() : e[r] = o;
  };
  for (let o = 0, s = arguments.length; o < s; o++)
    arguments[o] && Wt(arguments[o], i);
  return e;
}
const Oo = (t, e, i, { allOwnKeys: o } = {}) => (Wt(e, (s, r) => {
  i && B(s) ? t[r] = Ti(s, i) : t[r] = s;
}, { allOwnKeys: o }), t), Uo = (t) => (t.charCodeAt(0) === 65279 && (t = t.slice(1)), t), Fo = (t, e, i, o) => {
  t.prototype = Object.create(e.prototype, o), t.prototype.constructor = t, Object.defineProperty(t, "super", {
    value: e.prototype
  }), i && Object.assign(t.prototype, i);
}, No = (t, e, i, o) => {
  let s, r, n;
  const a = {};
  if (e = e || {}, t == null) return e;
  do {
    for (s = Object.getOwnPropertyNames(t), r = s.length; r-- > 0; )
      n = s[r], (!o || o(n, t, e)) && !a[n] && (e[n] = t[n], a[n] = !0);
    t = i !== !1 && Ne(t);
  } while (t && (!i || i(t, e)) && t !== Object.prototype);
  return e;
}, Ho = (t, e, i) => {
  t = String(t), (i === void 0 || i > t.length) && (i = t.length), i -= e.length;
  const o = t.indexOf(e, i);
  return o !== -1 && o === i;
}, qo = (t) => {
  if (!t) return null;
  if (Rt(t)) return t;
  let e = t.length;
  if (!Ii(e)) return null;
  const i = new Array(e);
  for (; e-- > 0; )
    i[e] = t[e];
  return i;
}, Vo = /* @__PURE__ */ ((t) => (e) => t && e instanceof t)(typeof Uint8Array < "u" && Ne(Uint8Array)), Ko = (t, e) => {
  const o = (t && t[pe]).call(t);
  let s;
  for (; (s = o.next()) && !s.done; ) {
    const r = s.value;
    e.call(t, r[0], r[1]);
  }
}, Go = (t, e) => {
  let i;
  const o = [];
  for (; (i = t.exec(e)) !== null; )
    o.push(i);
  return o;
}, Jo = Q("HTMLFormElement"), Wo = (t) => t.toLowerCase().replace(
  /[-_\s]([a-z\d])(\w*)/g,
  function(i, o, s) {
    return o.toUpperCase() + s;
  }
), ai = (({ hasOwnProperty: t }) => (e, i) => t.call(e, i))(Object.prototype), Yo = Q("RegExp"), Ri = (t, e) => {
  const i = Object.getOwnPropertyDescriptors(t), o = {};
  Wt(i, (s, r) => {
    let n;
    (n = e(s, r, t)) !== !1 && (o[r] = n || s);
  }), Object.defineProperties(t, o);
}, Xo = (t) => {
  Ri(t, (e, i) => {
    if (B(t) && ["arguments", "caller", "callee"].indexOf(i) !== -1)
      return !1;
    const o = t[i];
    if (B(o)) {
      if (e.enumerable = !1, "writable" in e) {
        e.writable = !1;
        return;
      }
      e.set || (e.set = () => {
        throw Error("Can not rewrite read-only method '" + i + "'");
      });
    }
  });
}, Qo = (t, e) => {
  const i = {}, o = (s) => {
    s.forEach((r) => {
      i[r] = !0;
    });
  };
  return Rt(t) ? o(t) : o(String(t).split(e)), i;
}, Zo = () => {
}, ts = (t, e) => t != null && Number.isFinite(t = +t) ? t : e;
function es(t) {
  return !!(t && B(t.append) && t[Ei] === "FormData" && t[pe]);
}
const is = (t) => {
  const e = new Array(10), i = (o, s) => {
    if (fe(o)) {
      if (e.indexOf(o) >= 0)
        return;
      if (!("toJSON" in o)) {
        e[s] = o;
        const r = Rt(o) ? [] : {};
        return Wt(o, (n, a) => {
          const l = i(n, s + 1);
          !Kt(l) && (r[a] = l);
        }), e[s] = void 0, r;
      }
    }
    return o;
  };
  return i(t, 0);
}, os = Q("AsyncFunction"), ss = (t) => t && (fe(t) || B(t)) && B(t.then) && B(t.catch), Ai = ((t, e) => t ? setImmediate : e ? ((i, o) => (bt.addEventListener("message", ({ source: s, data: r }) => {
  s === bt && r === i && o.length && o.shift()();
}, !1), (s) => {
  o.push(s), bt.postMessage(i, "*");
}))(`axios@${Math.random()}`, []) : (i) => setTimeout(i))(
  typeof setImmediate == "function",
  B(bt.postMessage)
), rs = typeof queueMicrotask < "u" ? queueMicrotask.bind(bt) : typeof process < "u" && process.nextTick || Ai, ns = (t) => t != null && B(t[pe]), c = {
  isArray: Rt,
  isArrayBuffer: Si,
  isBuffer: ko,
  isFormData: Ro,
  isArrayBufferView: $o,
  isString: zo,
  isNumber: Ii,
  isBoolean: To,
  isObject: fe,
  isPlainObject: se,
  isReadableStream: _o,
  isRequest: Lo,
  isResponse: Po,
  isHeaders: Mo,
  isUndefined: Kt,
  isDate: Eo,
  isFile: So,
  isBlob: Io,
  isRegExp: Yo,
  isFunction: B,
  isStream: Do,
  isURLSearchParams: Ao,
  isTypedArray: Vo,
  isFileList: Co,
  forEach: Wt,
  merge: De,
  extend: Oo,
  trim: Bo,
  stripBOM: Uo,
  inherits: Fo,
  toFlatObject: No,
  kindOf: ue,
  kindOfTest: Q,
  endsWith: Ho,
  toArray: qo,
  forEachEntry: Ko,
  matchAll: Go,
  isHTMLForm: Jo,
  hasOwnProperty: ai,
  hasOwnProp: ai,
  // an alias to avoid ESLint no-prototype-builtins detection
  reduceDescriptors: Ri,
  freezeMethods: Xo,
  toObjectSet: Qo,
  toCamelCase: Wo,
  noop: Zo,
  toFiniteNumber: ts,
  findKey: Ci,
  global: bt,
  isContextDefined: Di,
  isSpecCompliantForm: es,
  toJSONObject: is,
  isAsyncFn: os,
  isThenable: ss,
  setImmediate: Ai,
  asap: rs,
  isIterable: ns
};
function y(t, e, i, o, s) {
  Error.call(this), Error.captureStackTrace ? Error.captureStackTrace(this, this.constructor) : this.stack = new Error().stack, this.message = t, this.name = "AxiosError", e && (this.code = e), i && (this.config = i), o && (this.request = o), s && (this.response = s, this.status = s.status ? s.status : null);
}
c.inherits(y, Error, {
  toJSON: function() {
    return {
      // Standard
      message: this.message,
      name: this.name,
      // Microsoft
      description: this.description,
      number: this.number,
      // Mozilla
      fileName: this.fileName,
      lineNumber: this.lineNumber,
      columnNumber: this.columnNumber,
      stack: this.stack,
      // Axios
      config: c.toJSONObject(this.config),
      code: this.code,
      status: this.status
    };
  }
});
const _i = y.prototype, Li = {};
[
  "ERR_BAD_OPTION_VALUE",
  "ERR_BAD_OPTION",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_NETWORK",
  "ERR_FR_TOO_MANY_REDIRECTS",
  "ERR_DEPRECATED",
  "ERR_BAD_RESPONSE",
  "ERR_BAD_REQUEST",
  "ERR_CANCELED",
  "ERR_NOT_SUPPORT",
  "ERR_INVALID_URL"
  // eslint-disable-next-line func-names
].forEach((t) => {
  Li[t] = { value: t };
});
Object.defineProperties(y, Li);
Object.defineProperty(_i, "isAxiosError", { value: !0 });
y.from = (t, e, i, o, s, r) => {
  const n = Object.create(_i);
  return c.toFlatObject(t, n, function(l) {
    return l !== Error.prototype;
  }, (a) => a !== "isAxiosError"), y.call(n, t.message, e, i, o, s), n.cause = t, n.name = t.name, r && Object.assign(n, r), n;
};
const as = null;
function Re(t) {
  return c.isPlainObject(t) || c.isArray(t);
}
function Pi(t) {
  return c.endsWith(t, "[]") ? t.slice(0, -2) : t;
}
function li(t, e, i) {
  return t ? t.concat(e).map(function(s, r) {
    return s = Pi(s), !i && r ? "[" + s + "]" : s;
  }).join(i ? "." : "") : e;
}
function ls(t) {
  return c.isArray(t) && !t.some(Re);
}
const ds = c.toFlatObject(c, {}, null, function(e) {
  return /^is[A-Z]/.test(e);
});
function we(t, e, i) {
  if (!c.isObject(t))
    throw new TypeError("target must be an object");
  e = e || new FormData(), i = c.toFlatObject(i, {
    metaTokens: !0,
    dots: !1,
    indexes: !1
  }, !1, function(b, v) {
    return !c.isUndefined(v[b]);
  });
  const o = i.metaTokens, s = i.visitor || h, r = i.dots, n = i.indexes, l = (i.Blob || typeof Blob < "u" && Blob) && c.isSpecCompliantForm(e);
  if (!c.isFunction(s))
    throw new TypeError("visitor must be a function");
  function d(f) {
    if (f === null) return "";
    if (c.isDate(f))
      return f.toISOString();
    if (c.isBoolean(f))
      return f.toString();
    if (!l && c.isBlob(f))
      throw new y("Blob is not supported. Use a Buffer instead.");
    return c.isArrayBuffer(f) || c.isTypedArray(f) ? l && typeof Blob == "function" ? new Blob([f]) : Buffer.from(f) : f;
  }
  function h(f, b, v) {
    let S = f;
    if (f && !v && typeof f == "object") {
      if (c.endsWith(b, "{}"))
        b = o ? b : b.slice(0, -2), f = JSON.stringify(f);
      else if (c.isArray(f) && ls(f) || (c.isFileList(f) || c.endsWith(b, "[]")) && (S = c.toArray(f)))
        return b = Pi(b), S.forEach(function(D, ot) {
          !(c.isUndefined(D) || D === null) && e.append(
            // eslint-disable-next-line no-nested-ternary
            n === !0 ? li([b], ot, r) : n === null ? b : b + "[]",
            d(D)
          );
        }), !1;
    }
    return Re(f) ? !0 : (e.append(li(v, b, r), d(f)), !1);
  }
  const p = [], w = Object.assign(ds, {
    defaultVisitor: h,
    convertValue: d,
    isVisitable: Re
  });
  function x(f, b) {
    if (!c.isUndefined(f)) {
      if (p.indexOf(f) !== -1)
        throw Error("Circular reference detected in " + b.join("."));
      p.push(f), c.forEach(f, function(S, C) {
        (!(c.isUndefined(S) || S === null) && s.call(
          e,
          S,
          c.isString(C) ? C.trim() : C,
          b,
          w
        )) === !0 && x(S, b ? b.concat(C) : [C]);
      }), p.pop();
    }
  }
  if (!c.isObject(t))
    throw new TypeError("data must be an object");
  return x(t), e;
}
function di(t) {
  const e = {
    "!": "%21",
    "'": "%27",
    "(": "%28",
    ")": "%29",
    "~": "%7E",
    "%20": "+",
    "%00": "\0"
  };
  return encodeURIComponent(t).replace(/[!'()~]|%20|%00/g, function(o) {
    return e[o];
  });
}
function He(t, e) {
  this._pairs = [], t && we(t, this, e);
}
const Mi = He.prototype;
Mi.append = function(e, i) {
  this._pairs.push([e, i]);
};
Mi.toString = function(e) {
  const i = e ? function(o) {
    return e.call(this, o, di);
  } : di;
  return this._pairs.map(function(s) {
    return i(s[0]) + "=" + i(s[1]);
  }, "").join("&");
};
function cs(t) {
  return encodeURIComponent(t).replace(/%3A/gi, ":").replace(/%24/g, "$").replace(/%2C/gi, ",").replace(/%20/g, "+").replace(/%5B/gi, "[").replace(/%5D/gi, "]");
}
function Bi(t, e, i) {
  if (!e)
    return t;
  const o = i && i.encode || cs;
  c.isFunction(i) && (i = {
    serialize: i
  });
  const s = i && i.serialize;
  let r;
  if (s ? r = s(e, i) : r = c.isURLSearchParams(e) ? e.toString() : new He(e, i).toString(o), r) {
    const n = t.indexOf("#");
    n !== -1 && (t = t.slice(0, n)), t += (t.indexOf("?") === -1 ? "?" : "&") + r;
  }
  return t;
}
class ci {
  constructor() {
    this.handlers = [];
  }
  /**
   * Add a new interceptor to the stack
   *
   * @param {Function} fulfilled The function to handle `then` for a `Promise`
   * @param {Function} rejected The function to handle `reject` for a `Promise`
   *
   * @return {Number} An ID used to remove interceptor later
   */
  use(e, i, o) {
    return this.handlers.push({
      fulfilled: e,
      rejected: i,
      synchronous: o ? o.synchronous : !1,
      runWhen: o ? o.runWhen : null
    }), this.handlers.length - 1;
  }
  /**
   * Remove an interceptor from the stack
   *
   * @param {Number} id The ID that was returned by `use`
   *
   * @returns {Boolean} `true` if the interceptor was removed, `false` otherwise
   */
  eject(e) {
    this.handlers[e] && (this.handlers[e] = null);
  }
  /**
   * Clear all interceptors from the stack
   *
   * @returns {void}
   */
  clear() {
    this.handlers && (this.handlers = []);
  }
  /**
   * Iterate over all the registered interceptors
   *
   * This method is particularly useful for skipping over any
   * interceptors that may have become `null` calling `eject`.
   *
   * @param {Function} fn The function to call for each interceptor
   *
   * @returns {void}
   */
  forEach(e) {
    c.forEach(this.handlers, function(o) {
      o !== null && e(o);
    });
  }
}
const Oi = {
  silentJSONParsing: !0,
  forcedJSONParsing: !0,
  clarifyTimeoutError: !1
}, hs = typeof URLSearchParams < "u" ? URLSearchParams : He, ps = typeof FormData < "u" ? FormData : null, us = typeof Blob < "u" ? Blob : null, gs = {
  isBrowser: !0,
  classes: {
    URLSearchParams: hs,
    FormData: ps,
    Blob: us
  },
  protocols: ["http", "https", "file", "blob", "url", "data"]
}, qe = typeof window < "u" && typeof document < "u", Ae = typeof navigator == "object" && navigator || void 0, fs = qe && (!Ae || ["ReactNative", "NativeScript", "NS"].indexOf(Ae.product) < 0), ws = typeof WorkerGlobalScope < "u" && // eslint-disable-next-line no-undef
self instanceof WorkerGlobalScope && typeof self.importScripts == "function", vs = qe && window.location.href || "http://localhost", ms = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  hasBrowserEnv: qe,
  hasStandardBrowserEnv: fs,
  hasStandardBrowserWebWorkerEnv: ws,
  navigator: Ae,
  origin: vs
}, Symbol.toStringTag, { value: "Module" })), L = {
  ...ms,
  ...gs
};
function bs(t, e) {
  return we(t, new L.classes.URLSearchParams(), Object.assign({
    visitor: function(i, o, s, r) {
      return L.isNode && c.isBuffer(i) ? (this.append(o, i.toString("base64")), !1) : r.defaultVisitor.apply(this, arguments);
    }
  }, e));
}
function xs(t) {
  return c.matchAll(/\w+|\[(\w*)]/g, t).map((e) => e[0] === "[]" ? "" : e[1] || e[0]);
}
function ys(t) {
  const e = {}, i = Object.keys(t);
  let o;
  const s = i.length;
  let r;
  for (o = 0; o < s; o++)
    r = i[o], e[r] = t[r];
  return e;
}
function Ui(t) {
  function e(i, o, s, r) {
    let n = i[r++];
    if (n === "__proto__") return !0;
    const a = Number.isFinite(+n), l = r >= i.length;
    return n = !n && c.isArray(s) ? s.length : n, l ? (c.hasOwnProp(s, n) ? s[n] = [s[n], o] : s[n] = o, !a) : ((!s[n] || !c.isObject(s[n])) && (s[n] = []), e(i, o, s[n], r) && c.isArray(s[n]) && (s[n] = ys(s[n])), !a);
  }
  if (c.isFormData(t) && c.isFunction(t.entries)) {
    const i = {};
    return c.forEachEntry(t, (o, s) => {
      e(xs(o), s, i, 0);
    }), i;
  }
  return null;
}
function js(t, e, i) {
  if (c.isString(t))
    try {
      return (e || JSON.parse)(t), c.trim(t);
    } catch (o) {
      if (o.name !== "SyntaxError")
        throw o;
    }
  return (i || JSON.stringify)(t);
}
const Yt = {
  transitional: Oi,
  adapter: ["xhr", "http", "fetch"],
  transformRequest: [function(e, i) {
    const o = i.getContentType() || "", s = o.indexOf("application/json") > -1, r = c.isObject(e);
    if (r && c.isHTMLForm(e) && (e = new FormData(e)), c.isFormData(e))
      return s ? JSON.stringify(Ui(e)) : e;
    if (c.isArrayBuffer(e) || c.isBuffer(e) || c.isStream(e) || c.isFile(e) || c.isBlob(e) || c.isReadableStream(e))
      return e;
    if (c.isArrayBufferView(e))
      return e.buffer;
    if (c.isURLSearchParams(e))
      return i.setContentType("application/x-www-form-urlencoded;charset=utf-8", !1), e.toString();
    let a;
    if (r) {
      if (o.indexOf("application/x-www-form-urlencoded") > -1)
        return bs(e, this.formSerializer).toString();
      if ((a = c.isFileList(e)) || o.indexOf("multipart/form-data") > -1) {
        const l = this.env && this.env.FormData;
        return we(
          a ? { "files[]": e } : e,
          l && new l(),
          this.formSerializer
        );
      }
    }
    return r || s ? (i.setContentType("application/json", !1), js(e)) : e;
  }],
  transformResponse: [function(e) {
    const i = this.transitional || Yt.transitional, o = i && i.forcedJSONParsing, s = this.responseType === "json";
    if (c.isResponse(e) || c.isReadableStream(e))
      return e;
    if (e && c.isString(e) && (o && !this.responseType || s)) {
      const n = !(i && i.silentJSONParsing) && s;
      try {
        return JSON.parse(e);
      } catch (a) {
        if (n)
          throw a.name === "SyntaxError" ? y.from(a, y.ERR_BAD_RESPONSE, this, null, this.response) : a;
      }
    }
    return e;
  }],
  /**
   * A timeout in milliseconds to abort a request. If set to 0 (default) a
   * timeout is not created.
   */
  timeout: 0,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
  maxContentLength: -1,
  maxBodyLength: -1,
  env: {
    FormData: L.classes.FormData,
    Blob: L.classes.Blob
  },
  validateStatus: function(e) {
    return e >= 200 && e < 300;
  },
  headers: {
    common: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": void 0
    }
  }
};
c.forEach(["delete", "get", "head", "post", "put", "patch"], (t) => {
  Yt.headers[t] = {};
});
const ks = c.toObjectSet([
  "age",
  "authorization",
  "content-length",
  "content-type",
  "etag",
  "expires",
  "from",
  "host",
  "if-modified-since",
  "if-unmodified-since",
  "last-modified",
  "location",
  "max-forwards",
  "proxy-authorization",
  "referer",
  "retry-after",
  "user-agent"
]), $s = (t) => {
  const e = {};
  let i, o, s;
  return t && t.split(`
`).forEach(function(n) {
    s = n.indexOf(":"), i = n.substring(0, s).trim().toLowerCase(), o = n.substring(s + 1).trim(), !(!i || e[i] && ks[i]) && (i === "set-cookie" ? e[i] ? e[i].push(o) : e[i] = [o] : e[i] = e[i] ? e[i] + ", " + o : o);
  }), e;
}, hi = Symbol("internals");
function Ot(t) {
  return t && String(t).trim().toLowerCase();
}
function re(t) {
  return t === !1 || t == null ? t : c.isArray(t) ? t.map(re) : String(t);
}
function zs(t) {
  const e = /* @__PURE__ */ Object.create(null), i = /([^\s,;=]+)\s*(?:=\s*([^,;]+))?/g;
  let o;
  for (; o = i.exec(t); )
    e[o[1]] = o[2];
  return e;
}
const Ts = (t) => /^[-_a-zA-Z0-9^`|~,!#$%&'*+.]+$/.test(t.trim());
function Se(t, e, i, o, s) {
  if (c.isFunction(o))
    return o.call(this, e, i);
  if (s && (e = i), !!c.isString(e)) {
    if (c.isString(o))
      return e.indexOf(o) !== -1;
    if (c.isRegExp(o))
      return o.test(e);
  }
}
function Es(t) {
  return t.trim().toLowerCase().replace(/([a-z\d])(\w*)/g, (e, i, o) => i.toUpperCase() + o);
}
function Ss(t, e) {
  const i = c.toCamelCase(" " + e);
  ["get", "set", "has"].forEach((o) => {
    Object.defineProperty(t, o + i, {
      value: function(s, r, n) {
        return this[o].call(this, e, s, r, n);
      },
      configurable: !0
    });
  });
}
let O = class {
  constructor(e) {
    e && this.set(e);
  }
  set(e, i, o) {
    const s = this;
    function r(a, l, d) {
      const h = Ot(l);
      if (!h)
        throw new Error("header name must be a non-empty string");
      const p = c.findKey(s, h);
      (!p || s[p] === void 0 || d === !0 || d === void 0 && s[p] !== !1) && (s[p || l] = re(a));
    }
    const n = (a, l) => c.forEach(a, (d, h) => r(d, h, l));
    if (c.isPlainObject(e) || e instanceof this.constructor)
      n(e, i);
    else if (c.isString(e) && (e = e.trim()) && !Ts(e))
      n($s(e), i);
    else if (c.isObject(e) && c.isIterable(e)) {
      let a = {}, l, d;
      for (const h of e) {
        if (!c.isArray(h))
          throw TypeError("Object iterator must return a key-value pair");
        a[d = h[0]] = (l = a[d]) ? c.isArray(l) ? [...l, h[1]] : [l, h[1]] : h[1];
      }
      n(a, i);
    } else
      e != null && r(i, e, o);
    return this;
  }
  get(e, i) {
    if (e = Ot(e), e) {
      const o = c.findKey(this, e);
      if (o) {
        const s = this[o];
        if (!i)
          return s;
        if (i === !0)
          return zs(s);
        if (c.isFunction(i))
          return i.call(this, s, o);
        if (c.isRegExp(i))
          return i.exec(s);
        throw new TypeError("parser must be boolean|regexp|function");
      }
    }
  }
  has(e, i) {
    if (e = Ot(e), e) {
      const o = c.findKey(this, e);
      return !!(o && this[o] !== void 0 && (!i || Se(this, this[o], o, i)));
    }
    return !1;
  }
  delete(e, i) {
    const o = this;
    let s = !1;
    function r(n) {
      if (n = Ot(n), n) {
        const a = c.findKey(o, n);
        a && (!i || Se(o, o[a], a, i)) && (delete o[a], s = !0);
      }
    }
    return c.isArray(e) ? e.forEach(r) : r(e), s;
  }
  clear(e) {
    const i = Object.keys(this);
    let o = i.length, s = !1;
    for (; o--; ) {
      const r = i[o];
      (!e || Se(this, this[r], r, e, !0)) && (delete this[r], s = !0);
    }
    return s;
  }
  normalize(e) {
    const i = this, o = {};
    return c.forEach(this, (s, r) => {
      const n = c.findKey(o, r);
      if (n) {
        i[n] = re(s), delete i[r];
        return;
      }
      const a = e ? Es(r) : String(r).trim();
      a !== r && delete i[r], i[a] = re(s), o[a] = !0;
    }), this;
  }
  concat(...e) {
    return this.constructor.concat(this, ...e);
  }
  toJSON(e) {
    const i = /* @__PURE__ */ Object.create(null);
    return c.forEach(this, (o, s) => {
      o != null && o !== !1 && (i[s] = e && c.isArray(o) ? o.join(", ") : o);
    }), i;
  }
  [Symbol.iterator]() {
    return Object.entries(this.toJSON())[Symbol.iterator]();
  }
  toString() {
    return Object.entries(this.toJSON()).map(([e, i]) => e + ": " + i).join(`
`);
  }
  getSetCookie() {
    return this.get("set-cookie") || [];
  }
  get [Symbol.toStringTag]() {
    return "AxiosHeaders";
  }
  static from(e) {
    return e instanceof this ? e : new this(e);
  }
  static concat(e, ...i) {
    const o = new this(e);
    return i.forEach((s) => o.set(s)), o;
  }
  static accessor(e) {
    const o = (this[hi] = this[hi] = {
      accessors: {}
    }).accessors, s = this.prototype;
    function r(n) {
      const a = Ot(n);
      o[a] || (Ss(s, n), o[a] = !0);
    }
    return c.isArray(e) ? e.forEach(r) : r(e), this;
  }
};
O.accessor(["Content-Type", "Content-Length", "Accept", "Accept-Encoding", "User-Agent", "Authorization"]);
c.reduceDescriptors(O.prototype, ({ value: t }, e) => {
  let i = e[0].toUpperCase() + e.slice(1);
  return {
    get: () => t,
    set(o) {
      this[i] = o;
    }
  };
});
c.freezeMethods(O);
function Ie(t, e) {
  const i = this || Yt, o = e || i, s = O.from(o.headers);
  let r = o.data;
  return c.forEach(t, function(a) {
    r = a.call(i, r, s.normalize(), e ? e.status : void 0);
  }), s.normalize(), r;
}
function Fi(t) {
  return !!(t && t.__CANCEL__);
}
function At(t, e, i) {
  y.call(this, t ?? "canceled", y.ERR_CANCELED, e, i), this.name = "CanceledError";
}
c.inherits(At, y, {
  __CANCEL__: !0
});
function Ni(t, e, i) {
  const o = i.config.validateStatus;
  !i.status || !o || o(i.status) ? t(i) : e(new y(
    "Request failed with status code " + i.status,
    [y.ERR_BAD_REQUEST, y.ERR_BAD_RESPONSE][Math.floor(i.status / 100) - 4],
    i.config,
    i.request,
    i
  ));
}
function Is(t) {
  const e = /^([-+\w]{1,25})(:?\/\/|:)/.exec(t);
  return e && e[1] || "";
}
function Cs(t, e) {
  t = t || 10;
  const i = new Array(t), o = new Array(t);
  let s = 0, r = 0, n;
  return e = e !== void 0 ? e : 1e3, function(l) {
    const d = Date.now(), h = o[r];
    n || (n = d), i[s] = l, o[s] = d;
    let p = r, w = 0;
    for (; p !== s; )
      w += i[p++], p = p % t;
    if (s = (s + 1) % t, s === r && (r = (r + 1) % t), d - n < e)
      return;
    const x = h && d - h;
    return x ? Math.round(w * 1e3 / x) : void 0;
  };
}
function Ds(t, e) {
  let i = 0, o = 1e3 / e, s, r;
  const n = (d, h = Date.now()) => {
    i = h, s = null, r && (clearTimeout(r), r = null), t.apply(null, d);
  };
  return [(...d) => {
    const h = Date.now(), p = h - i;
    p >= o ? n(d, h) : (s = d, r || (r = setTimeout(() => {
      r = null, n(s);
    }, o - p)));
  }, () => s && n(s)];
}
const de = (t, e, i = 3) => {
  let o = 0;
  const s = Cs(50, 250);
  return Ds((r) => {
    const n = r.loaded, a = r.lengthComputable ? r.total : void 0, l = n - o, d = s(l), h = n <= a;
    o = n;
    const p = {
      loaded: n,
      total: a,
      progress: a ? n / a : void 0,
      bytes: l,
      rate: d || void 0,
      estimated: d && a && h ? (a - n) / d : void 0,
      event: r,
      lengthComputable: a != null,
      [e ? "download" : "upload"]: !0
    };
    t(p);
  }, i);
}, pi = (t, e) => {
  const i = t != null;
  return [(o) => e[0]({
    lengthComputable: i,
    total: t,
    loaded: o
  }), e[1]];
}, ui = (t) => (...e) => c.asap(() => t(...e)), Rs = L.hasStandardBrowserEnv ? /* @__PURE__ */ ((t, e) => (i) => (i = new URL(i, L.origin), t.protocol === i.protocol && t.host === i.host && (e || t.port === i.port)))(
  new URL(L.origin),
  L.navigator && /(msie|trident)/i.test(L.navigator.userAgent)
) : () => !0, As = L.hasStandardBrowserEnv ? (
  // Standard browser envs support document.cookie
  {
    write(t, e, i, o, s, r) {
      const n = [t + "=" + encodeURIComponent(e)];
      c.isNumber(i) && n.push("expires=" + new Date(i).toGMTString()), c.isString(o) && n.push("path=" + o), c.isString(s) && n.push("domain=" + s), r === !0 && n.push("secure"), document.cookie = n.join("; ");
    },
    read(t) {
      const e = document.cookie.match(new RegExp("(^|;\\s*)(" + t + ")=([^;]*)"));
      return e ? decodeURIComponent(e[3]) : null;
    },
    remove(t) {
      this.write(t, "", Date.now() - 864e5);
    }
  }
) : (
  // Non-standard browser env (web workers, react-native) lack needed support.
  {
    write() {
    },
    read() {
      return null;
    },
    remove() {
    }
  }
);
function _s(t) {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(t);
}
function Ls(t, e) {
  return e ? t.replace(/\/?\/$/, "") + "/" + e.replace(/^\/+/, "") : t;
}
function Hi(t, e, i) {
  let o = !_s(e);
  return t && (o || i == !1) ? Ls(t, e) : e;
}
const gi = (t) => t instanceof O ? { ...t } : t;
function kt(t, e) {
  e = e || {};
  const i = {};
  function o(d, h, p, w) {
    return c.isPlainObject(d) && c.isPlainObject(h) ? c.merge.call({ caseless: w }, d, h) : c.isPlainObject(h) ? c.merge({}, h) : c.isArray(h) ? h.slice() : h;
  }
  function s(d, h, p, w) {
    if (c.isUndefined(h)) {
      if (!c.isUndefined(d))
        return o(void 0, d, p, w);
    } else return o(d, h, p, w);
  }
  function r(d, h) {
    if (!c.isUndefined(h))
      return o(void 0, h);
  }
  function n(d, h) {
    if (c.isUndefined(h)) {
      if (!c.isUndefined(d))
        return o(void 0, d);
    } else return o(void 0, h);
  }
  function a(d, h, p) {
    if (p in e)
      return o(d, h);
    if (p in t)
      return o(void 0, d);
  }
  const l = {
    url: r,
    method: r,
    data: r,
    baseURL: n,
    transformRequest: n,
    transformResponse: n,
    paramsSerializer: n,
    timeout: n,
    timeoutMessage: n,
    withCredentials: n,
    withXSRFToken: n,
    adapter: n,
    responseType: n,
    xsrfCookieName: n,
    xsrfHeaderName: n,
    onUploadProgress: n,
    onDownloadProgress: n,
    decompress: n,
    maxContentLength: n,
    maxBodyLength: n,
    beforeRedirect: n,
    transport: n,
    httpAgent: n,
    httpsAgent: n,
    cancelToken: n,
    socketPath: n,
    responseEncoding: n,
    validateStatus: a,
    headers: (d, h, p) => s(gi(d), gi(h), p, !0)
  };
  return c.forEach(Object.keys(Object.assign({}, t, e)), function(h) {
    const p = l[h] || s, w = p(t[h], e[h], h);
    c.isUndefined(w) && p !== a || (i[h] = w);
  }), i;
}
const qi = (t) => {
  const e = kt({}, t);
  let { data: i, withXSRFToken: o, xsrfHeaderName: s, xsrfCookieName: r, headers: n, auth: a } = e;
  e.headers = n = O.from(n), e.url = Bi(Hi(e.baseURL, e.url, e.allowAbsoluteUrls), t.params, t.paramsSerializer), a && n.set(
    "Authorization",
    "Basic " + btoa((a.username || "") + ":" + (a.password ? unescape(encodeURIComponent(a.password)) : ""))
  );
  let l;
  if (c.isFormData(i)) {
    if (L.hasStandardBrowserEnv || L.hasStandardBrowserWebWorkerEnv)
      n.setContentType(void 0);
    else if ((l = n.getContentType()) !== !1) {
      const [d, ...h] = l ? l.split(";").map((p) => p.trim()).filter(Boolean) : [];
      n.setContentType([d || "multipart/form-data", ...h].join("; "));
    }
  }
  if (L.hasStandardBrowserEnv && (o && c.isFunction(o) && (o = o(e)), o || o !== !1 && Rs(e.url))) {
    const d = s && r && As.read(r);
    d && n.set(s, d);
  }
  return e;
}, Ps = typeof XMLHttpRequest < "u", Ms = Ps && function(t) {
  return new Promise(function(i, o) {
    const s = qi(t);
    let r = s.data;
    const n = O.from(s.headers).normalize();
    let { responseType: a, onUploadProgress: l, onDownloadProgress: d } = s, h, p, w, x, f;
    function b() {
      x && x(), f && f(), s.cancelToken && s.cancelToken.unsubscribe(h), s.signal && s.signal.removeEventListener("abort", h);
    }
    let v = new XMLHttpRequest();
    v.open(s.method.toUpperCase(), s.url, !0), v.timeout = s.timeout;
    function S() {
      if (!v)
        return;
      const D = O.from(
        "getAllResponseHeaders" in v && v.getAllResponseHeaders()
      ), M = {
        data: !a || a === "text" || a === "json" ? v.responseText : v.response,
        status: v.status,
        statusText: v.statusText,
        headers: D,
        config: t,
        request: v
      };
      Ni(function(wt) {
        i(wt), b();
      }, function(wt) {
        o(wt), b();
      }, M), v = null;
    }
    "onloadend" in v ? v.onloadend = S : v.onreadystatechange = function() {
      !v || v.readyState !== 4 || v.status === 0 && !(v.responseURL && v.responseURL.indexOf("file:") === 0) || setTimeout(S);
    }, v.onabort = function() {
      v && (o(new y("Request aborted", y.ECONNABORTED, t, v)), v = null);
    }, v.onerror = function() {
      o(new y("Network Error", y.ERR_NETWORK, t, v)), v = null;
    }, v.ontimeout = function() {
      let ot = s.timeout ? "timeout of " + s.timeout + "ms exceeded" : "timeout exceeded";
      const M = s.transitional || Oi;
      s.timeoutErrorMessage && (ot = s.timeoutErrorMessage), o(new y(
        ot,
        M.clarifyTimeoutError ? y.ETIMEDOUT : y.ECONNABORTED,
        t,
        v
      )), v = null;
    }, r === void 0 && n.setContentType(null), "setRequestHeader" in v && c.forEach(n.toJSON(), function(ot, M) {
      v.setRequestHeader(M, ot);
    }), c.isUndefined(s.withCredentials) || (v.withCredentials = !!s.withCredentials), a && a !== "json" && (v.responseType = s.responseType), d && ([w, f] = de(d, !0), v.addEventListener("progress", w)), l && v.upload && ([p, x] = de(l), v.upload.addEventListener("progress", p), v.upload.addEventListener("loadend", x)), (s.cancelToken || s.signal) && (h = (D) => {
      v && (o(!D || D.type ? new At(null, t, v) : D), v.abort(), v = null);
    }, s.cancelToken && s.cancelToken.subscribe(h), s.signal && (s.signal.aborted ? h() : s.signal.addEventListener("abort", h)));
    const C = Is(s.url);
    if (C && L.protocols.indexOf(C) === -1) {
      o(new y("Unsupported protocol " + C + ":", y.ERR_BAD_REQUEST, t));
      return;
    }
    v.send(r || null);
  });
}, Bs = (t, e) => {
  const { length: i } = t = t ? t.filter(Boolean) : [];
  if (e || i) {
    let o = new AbortController(), s;
    const r = function(d) {
      if (!s) {
        s = !0, a();
        const h = d instanceof Error ? d : this.reason;
        o.abort(h instanceof y ? h : new At(h instanceof Error ? h.message : h));
      }
    };
    let n = e && setTimeout(() => {
      n = null, r(new y(`timeout ${e} of ms exceeded`, y.ETIMEDOUT));
    }, e);
    const a = () => {
      t && (n && clearTimeout(n), n = null, t.forEach((d) => {
        d.unsubscribe ? d.unsubscribe(r) : d.removeEventListener("abort", r);
      }), t = null);
    };
    t.forEach((d) => d.addEventListener("abort", r));
    const { signal: l } = o;
    return l.unsubscribe = () => c.asap(a), l;
  }
}, Os = function* (t, e) {
  let i = t.byteLength;
  if (i < e) {
    yield t;
    return;
  }
  let o = 0, s;
  for (; o < i; )
    s = o + e, yield t.slice(o, s), o = s;
}, Us = async function* (t, e) {
  for await (const i of Fs(t))
    yield* Os(i, e);
}, Fs = async function* (t) {
  if (t[Symbol.asyncIterator]) {
    yield* t;
    return;
  }
  const e = t.getReader();
  try {
    for (; ; ) {
      const { done: i, value: o } = await e.read();
      if (i)
        break;
      yield o;
    }
  } finally {
    await e.cancel();
  }
}, fi = (t, e, i, o) => {
  const s = Us(t, e);
  let r = 0, n, a = (l) => {
    n || (n = !0, o && o(l));
  };
  return new ReadableStream({
    async pull(l) {
      try {
        const { done: d, value: h } = await s.next();
        if (d) {
          a(), l.close();
          return;
        }
        let p = h.byteLength;
        if (i) {
          let w = r += p;
          i(w);
        }
        l.enqueue(new Uint8Array(h));
      } catch (d) {
        throw a(d), d;
      }
    },
    cancel(l) {
      return a(l), s.return();
    }
  }, {
    highWaterMark: 2
  });
}, ve = typeof fetch == "function" && typeof Request == "function" && typeof Response == "function", Vi = ve && typeof ReadableStream == "function", Ns = ve && (typeof TextEncoder == "function" ? /* @__PURE__ */ ((t) => (e) => t.encode(e))(new TextEncoder()) : async (t) => new Uint8Array(await new Response(t).arrayBuffer())), Ki = (t, ...e) => {
  try {
    return !!t(...e);
  } catch {
    return !1;
  }
}, Hs = Vi && Ki(() => {
  let t = !1;
  const e = new Request(L.origin, {
    body: new ReadableStream(),
    method: "POST",
    get duplex() {
      return t = !0, "half";
    }
  }).headers.has("Content-Type");
  return t && !e;
}), wi = 64 * 1024, _e = Vi && Ki(() => c.isReadableStream(new Response("").body)), ce = {
  stream: _e && ((t) => t.body)
};
ve && ((t) => {
  ["text", "arrayBuffer", "blob", "formData", "stream"].forEach((e) => {
    !ce[e] && (ce[e] = c.isFunction(t[e]) ? (i) => i[e]() : (i, o) => {
      throw new y(`Response type '${e}' is not supported`, y.ERR_NOT_SUPPORT, o);
    });
  });
})(new Response());
const qs = async (t) => {
  if (t == null)
    return 0;
  if (c.isBlob(t))
    return t.size;
  if (c.isSpecCompliantForm(t))
    return (await new Request(L.origin, {
      method: "POST",
      body: t
    }).arrayBuffer()).byteLength;
  if (c.isArrayBufferView(t) || c.isArrayBuffer(t))
    return t.byteLength;
  if (c.isURLSearchParams(t) && (t = t + ""), c.isString(t))
    return (await Ns(t)).byteLength;
}, Vs = async (t, e) => {
  const i = c.toFiniteNumber(t.getContentLength());
  return i ?? qs(e);
}, Ks = ve && (async (t) => {
  let {
    url: e,
    method: i,
    data: o,
    signal: s,
    cancelToken: r,
    timeout: n,
    onDownloadProgress: a,
    onUploadProgress: l,
    responseType: d,
    headers: h,
    withCredentials: p = "same-origin",
    fetchOptions: w
  } = qi(t);
  d = d ? (d + "").toLowerCase() : "text";
  let x = Bs([s, r && r.toAbortSignal()], n), f;
  const b = x && x.unsubscribe && (() => {
    x.unsubscribe();
  });
  let v;
  try {
    if (l && Hs && i !== "get" && i !== "head" && (v = await Vs(h, o)) !== 0) {
      let M = new Request(e, {
        method: "POST",
        body: o,
        duplex: "half"
      }), lt;
      if (c.isFormData(o) && (lt = M.headers.get("content-type")) && h.setContentType(lt), M.body) {
        const [wt, ee] = pi(
          v,
          de(ui(l))
        );
        o = fi(M.body, wi, wt, ee);
      }
    }
    c.isString(p) || (p = p ? "include" : "omit");
    const S = "credentials" in Request.prototype;
    f = new Request(e, {
      ...w,
      signal: x,
      method: i.toUpperCase(),
      headers: h.normalize().toJSON(),
      body: o,
      duplex: "half",
      credentials: S ? p : void 0
    });
    let C = await fetch(f, w);
    const D = _e && (d === "stream" || d === "response");
    if (_e && (a || D && b)) {
      const M = {};
      ["status", "statusText", "headers"].forEach((Ye) => {
        M[Ye] = C[Ye];
      });
      const lt = c.toFiniteNumber(C.headers.get("content-length")), [wt, ee] = a && pi(
        lt,
        de(ui(a), !0)
      ) || [];
      C = new Response(
        fi(C.body, wi, wt, () => {
          ee && ee(), b && b();
        }),
        M
      );
    }
    d = d || "text";
    let ot = await ce[c.findKey(ce, d) || "text"](C, t);
    return !D && b && b(), await new Promise((M, lt) => {
      Ni(M, lt, {
        data: ot,
        headers: O.from(C.headers),
        status: C.status,
        statusText: C.statusText,
        config: t,
        request: f
      });
    });
  } catch (S) {
    throw b && b(), S && S.name === "TypeError" && /Load failed|fetch/i.test(S.message) ? Object.assign(
      new y("Network Error", y.ERR_NETWORK, t, f),
      {
        cause: S.cause || S
      }
    ) : y.from(S, S && S.code, t, f);
  }
}), Le = {
  http: as,
  xhr: Ms,
  fetch: Ks
};
c.forEach(Le, (t, e) => {
  if (t) {
    try {
      Object.defineProperty(t, "name", { value: e });
    } catch {
    }
    Object.defineProperty(t, "adapterName", { value: e });
  }
});
const vi = (t) => `- ${t}`, Gs = (t) => c.isFunction(t) || t === null || t === !1, Gi = {
  getAdapter: (t) => {
    t = c.isArray(t) ? t : [t];
    const { length: e } = t;
    let i, o;
    const s = {};
    for (let r = 0; r < e; r++) {
      i = t[r];
      let n;
      if (o = i, !Gs(i) && (o = Le[(n = String(i)).toLowerCase()], o === void 0))
        throw new y(`Unknown adapter '${n}'`);
      if (o)
        break;
      s[n || "#" + r] = o;
    }
    if (!o) {
      const r = Object.entries(s).map(
        ([a, l]) => `adapter ${a} ` + (l === !1 ? "is not supported by the environment" : "is not available in the build")
      );
      let n = e ? r.length > 1 ? `since :
` + r.map(vi).join(`
`) : " " + vi(r[0]) : "as no adapter specified";
      throw new y(
        "There is no suitable adapter to dispatch the request " + n,
        "ERR_NOT_SUPPORT"
      );
    }
    return o;
  },
  adapters: Le
};
function Ce(t) {
  if (t.cancelToken && t.cancelToken.throwIfRequested(), t.signal && t.signal.aborted)
    throw new At(null, t);
}
function mi(t) {
  return Ce(t), t.headers = O.from(t.headers), t.data = Ie.call(
    t,
    t.transformRequest
  ), ["post", "put", "patch"].indexOf(t.method) !== -1 && t.headers.setContentType("application/x-www-form-urlencoded", !1), Gi.getAdapter(t.adapter || Yt.adapter)(t).then(function(o) {
    return Ce(t), o.data = Ie.call(
      t,
      t.transformResponse,
      o
    ), o.headers = O.from(o.headers), o;
  }, function(o) {
    return Fi(o) || (Ce(t), o && o.response && (o.response.data = Ie.call(
      t,
      t.transformResponse,
      o.response
    ), o.response.headers = O.from(o.response.headers))), Promise.reject(o);
  });
}
const Ji = "1.10.0", me = {};
["object", "boolean", "number", "function", "string", "symbol"].forEach((t, e) => {
  me[t] = function(o) {
    return typeof o === t || "a" + (e < 1 ? "n " : " ") + t;
  };
});
const bi = {};
me.transitional = function(e, i, o) {
  function s(r, n) {
    return "[Axios v" + Ji + "] Transitional option '" + r + "'" + n + (o ? ". " + o : "");
  }
  return (r, n, a) => {
    if (e === !1)
      throw new y(
        s(n, " has been removed" + (i ? " in " + i : "")),
        y.ERR_DEPRECATED
      );
    return i && !bi[n] && (bi[n] = !0, console.warn(
      s(
        n,
        " has been deprecated since v" + i + " and will be removed in the near future"
      )
    )), e ? e(r, n, a) : !0;
  };
};
me.spelling = function(e) {
  return (i, o) => (console.warn(`${o} is likely a misspelling of ${e}`), !0);
};
function Js(t, e, i) {
  if (typeof t != "object")
    throw new y("options must be an object", y.ERR_BAD_OPTION_VALUE);
  const o = Object.keys(t);
  let s = o.length;
  for (; s-- > 0; ) {
    const r = o[s], n = e[r];
    if (n) {
      const a = t[r], l = a === void 0 || n(a, r, t);
      if (l !== !0)
        throw new y("option " + r + " must be " + l, y.ERR_BAD_OPTION_VALUE);
      continue;
    }
    if (i !== !0)
      throw new y("Unknown option " + r, y.ERR_BAD_OPTION);
  }
}
const ne = {
  assertOptions: Js,
  validators: me
}, tt = ne.validators;
let yt = class {
  constructor(e) {
    this.defaults = e || {}, this.interceptors = {
      request: new ci(),
      response: new ci()
    };
  }
  /**
   * Dispatch a request
   *
   * @param {String|Object} configOrUrl The config specific for this request (merged with this.defaults)
   * @param {?Object} config
   *
   * @returns {Promise} The Promise to be fulfilled
   */
  async request(e, i) {
    try {
      return await this._request(e, i);
    } catch (o) {
      if (o instanceof Error) {
        let s = {};
        Error.captureStackTrace ? Error.captureStackTrace(s) : s = new Error();
        const r = s.stack ? s.stack.replace(/^.+\n/, "") : "";
        try {
          o.stack ? r && !String(o.stack).endsWith(r.replace(/^.+\n.+\n/, "")) && (o.stack += `
` + r) : o.stack = r;
        } catch {
        }
      }
      throw o;
    }
  }
  _request(e, i) {
    typeof e == "string" ? (i = i || {}, i.url = e) : i = e || {}, i = kt(this.defaults, i);
    const { transitional: o, paramsSerializer: s, headers: r } = i;
    o !== void 0 && ne.assertOptions(o, {
      silentJSONParsing: tt.transitional(tt.boolean),
      forcedJSONParsing: tt.transitional(tt.boolean),
      clarifyTimeoutError: tt.transitional(tt.boolean)
    }, !1), s != null && (c.isFunction(s) ? i.paramsSerializer = {
      serialize: s
    } : ne.assertOptions(s, {
      encode: tt.function,
      serialize: tt.function
    }, !0)), i.allowAbsoluteUrls !== void 0 || (this.defaults.allowAbsoluteUrls !== void 0 ? i.allowAbsoluteUrls = this.defaults.allowAbsoluteUrls : i.allowAbsoluteUrls = !0), ne.assertOptions(i, {
      baseUrl: tt.spelling("baseURL"),
      withXsrfToken: tt.spelling("withXSRFToken")
    }, !0), i.method = (i.method || this.defaults.method || "get").toLowerCase();
    let n = r && c.merge(
      r.common,
      r[i.method]
    );
    r && c.forEach(
      ["delete", "get", "head", "post", "put", "patch", "common"],
      (f) => {
        delete r[f];
      }
    ), i.headers = O.concat(n, r);
    const a = [];
    let l = !0;
    this.interceptors.request.forEach(function(b) {
      typeof b.runWhen == "function" && b.runWhen(i) === !1 || (l = l && b.synchronous, a.unshift(b.fulfilled, b.rejected));
    });
    const d = [];
    this.interceptors.response.forEach(function(b) {
      d.push(b.fulfilled, b.rejected);
    });
    let h, p = 0, w;
    if (!l) {
      const f = [mi.bind(this), void 0];
      for (f.unshift.apply(f, a), f.push.apply(f, d), w = f.length, h = Promise.resolve(i); p < w; )
        h = h.then(f[p++], f[p++]);
      return h;
    }
    w = a.length;
    let x = i;
    for (p = 0; p < w; ) {
      const f = a[p++], b = a[p++];
      try {
        x = f(x);
      } catch (v) {
        b.call(this, v);
        break;
      }
    }
    try {
      h = mi.call(this, x);
    } catch (f) {
      return Promise.reject(f);
    }
    for (p = 0, w = d.length; p < w; )
      h = h.then(d[p++], d[p++]);
    return h;
  }
  getUri(e) {
    e = kt(this.defaults, e);
    const i = Hi(e.baseURL, e.url, e.allowAbsoluteUrls);
    return Bi(i, e.params, e.paramsSerializer);
  }
};
c.forEach(["delete", "get", "head", "options"], function(e) {
  yt.prototype[e] = function(i, o) {
    return this.request(kt(o || {}, {
      method: e,
      url: i,
      data: (o || {}).data
    }));
  };
});
c.forEach(["post", "put", "patch"], function(e) {
  function i(o) {
    return function(r, n, a) {
      return this.request(kt(a || {}, {
        method: e,
        headers: o ? {
          "Content-Type": "multipart/form-data"
        } : {},
        url: r,
        data: n
      }));
    };
  }
  yt.prototype[e] = i(), yt.prototype[e + "Form"] = i(!0);
});
let Ws = class Wi {
  constructor(e) {
    if (typeof e != "function")
      throw new TypeError("executor must be a function.");
    let i;
    this.promise = new Promise(function(r) {
      i = r;
    });
    const o = this;
    this.promise.then((s) => {
      if (!o._listeners) return;
      let r = o._listeners.length;
      for (; r-- > 0; )
        o._listeners[r](s);
      o._listeners = null;
    }), this.promise.then = (s) => {
      let r;
      const n = new Promise((a) => {
        o.subscribe(a), r = a;
      }).then(s);
      return n.cancel = function() {
        o.unsubscribe(r);
      }, n;
    }, e(function(r, n, a) {
      o.reason || (o.reason = new At(r, n, a), i(o.reason));
    });
  }
  /**
   * Throws a `CanceledError` if cancellation has been requested.
   */
  throwIfRequested() {
    if (this.reason)
      throw this.reason;
  }
  /**
   * Subscribe to the cancel signal
   */
  subscribe(e) {
    if (this.reason) {
      e(this.reason);
      return;
    }
    this._listeners ? this._listeners.push(e) : this._listeners = [e];
  }
  /**
   * Unsubscribe from the cancel signal
   */
  unsubscribe(e) {
    if (!this._listeners)
      return;
    const i = this._listeners.indexOf(e);
    i !== -1 && this._listeners.splice(i, 1);
  }
  toAbortSignal() {
    const e = new AbortController(), i = (o) => {
      e.abort(o);
    };
    return this.subscribe(i), e.signal.unsubscribe = () => this.unsubscribe(i), e.signal;
  }
  /**
   * Returns an object that contains a new `CancelToken` and a function that, when called,
   * cancels the `CancelToken`.
   */
  static source() {
    let e;
    return {
      token: new Wi(function(s) {
        e = s;
      }),
      cancel: e
    };
  }
};
function Ys(t) {
  return function(i) {
    return t.apply(null, i);
  };
}
function Xs(t) {
  return c.isObject(t) && t.isAxiosError === !0;
}
const Pe = {
  Continue: 100,
  SwitchingProtocols: 101,
  Processing: 102,
  EarlyHints: 103,
  Ok: 200,
  Created: 201,
  Accepted: 202,
  NonAuthoritativeInformation: 203,
  NoContent: 204,
  ResetContent: 205,
  PartialContent: 206,
  MultiStatus: 207,
  AlreadyReported: 208,
  ImUsed: 226,
  MultipleChoices: 300,
  MovedPermanently: 301,
  Found: 302,
  SeeOther: 303,
  NotModified: 304,
  UseProxy: 305,
  Unused: 306,
  TemporaryRedirect: 307,
  PermanentRedirect: 308,
  BadRequest: 400,
  Unauthorized: 401,
  PaymentRequired: 402,
  Forbidden: 403,
  NotFound: 404,
  MethodNotAllowed: 405,
  NotAcceptable: 406,
  ProxyAuthenticationRequired: 407,
  RequestTimeout: 408,
  Conflict: 409,
  Gone: 410,
  LengthRequired: 411,
  PreconditionFailed: 412,
  PayloadTooLarge: 413,
  UriTooLong: 414,
  UnsupportedMediaType: 415,
  RangeNotSatisfiable: 416,
  ExpectationFailed: 417,
  ImATeapot: 418,
  MisdirectedRequest: 421,
  UnprocessableEntity: 422,
  Locked: 423,
  FailedDependency: 424,
  TooEarly: 425,
  UpgradeRequired: 426,
  PreconditionRequired: 428,
  TooManyRequests: 429,
  RequestHeaderFieldsTooLarge: 431,
  UnavailableForLegalReasons: 451,
  InternalServerError: 500,
  NotImplemented: 501,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
  HttpVersionNotSupported: 505,
  VariantAlsoNegotiates: 506,
  InsufficientStorage: 507,
  LoopDetected: 508,
  NotExtended: 510,
  NetworkAuthenticationRequired: 511
};
Object.entries(Pe).forEach(([t, e]) => {
  Pe[e] = t;
});
function Yi(t) {
  const e = new yt(t), i = Ti(yt.prototype.request, e);
  return c.extend(i, yt.prototype, e, { allOwnKeys: !0 }), c.extend(i, e, null, { allOwnKeys: !0 }), i.create = function(s) {
    return Yi(kt(t, s));
  }, i;
}
const j = Yi(Yt);
j.Axios = yt;
j.CanceledError = At;
j.CancelToken = Ws;
j.isCancel = Fi;
j.VERSION = Ji;
j.toFormData = we;
j.AxiosError = y;
j.Cancel = j.CanceledError;
j.all = function(e) {
  return Promise.all(e);
};
j.spread = Ys;
j.isAxiosError = Xs;
j.mergeConfig = kt;
j.AxiosHeaders = O;
j.formToJSON = (t) => Ui(c.isHTMLForm(t) ? new FormData(t) : t);
j.getAdapter = Gi.getAdapter;
j.HttpStatusCode = Pe;
j.default = j;
const {
  Axios: nn,
  AxiosError: an,
  CanceledError: ln,
  isCancel: dn,
  CancelToken: cn,
  VERSION: hn,
  all: pn,
  Cancel: un,
  isAxiosError: gn,
  spread: fn,
  toFormData: wn,
  AxiosHeaders: vn,
  HttpStatusCode: mn,
  formToJSON: bn,
  getAdapter: xn,
  mergeConfig: yn
} = j, Xt = "html,body,div,span,applet,object,iframe,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,abbr,acronym,address,big,cite,code,del,dfn,em,img,ins,kbd,q,s,samp,small,strike,strong,sub,sup,tt,var,b,u,i,center,dl,dt,dd,ol,ul,li,fieldset,form,label,legend,table,caption,tbody,tfoot,thead,tr,th,td,article,aside,canvas,details,embed,footer,header,hgroup,main,menu,nav,section,summary{margin:0;padding:0;border:0;font-family:var(--jds-font-family)}table{border-collapse:collapse;border-spacing:0}a{text-decoration:none;color:inherit}img{max-width:100%;height:auto}input,button,textarea,select{font-family:inherit;font-size:inherit;color:inherit}*,*:before,*:after{box-sizing:border-box}@media screen and (min-width: 1024px){html{font-size:clamp(12px,1.111111vw,16px)}}@media screen and (max-width: 1023.98px){html{font-size:clamp(16px,4.266666vw,32px)}}@media screen and (min-width: 1024px){.jds-animate jds-button.only-icon{transform:scale(0);transform-origin:center center;transition:all .5s cubic-bezier(.8,0,.2,1)}.jds-animate jds-button.only-icon::part(icon){transform:translate(-48px);transition:transform .5s cubic-bezier(.8,0,.2,1)}.jds-animate:hover jds-button.only-icon{transform:scale(1)}.jds-animate:hover jds-button.only-icon::part(icon){transform:translate(0)}.jds-animate[variant=primary]::part(button){transition:all .3s cubic-bezier(.8,0,.2,1);padding-right:0}.jds-animate[variant=primary]::part(label){overflow:visible}.jds-animate[variant=primary]:hover::part(button){--bg-color: var(--jds-color-primary);padding-right:var(--jds-size-8);padding-left:var(--jds-size-24)}.jds-animate[variant=primary] jds-button.only-icon{margin-left:var(--jds-size-8);transition:all .3s cubic-bezier(.8,0,.2,1)}.jds-animate[variant=primary] jds-button.only-icon::part(button){--height: var(--jds-size-32);--bg-color: var(--main-hover-color)}.jds-animate[variant=primary] jds-button.only-icon::part(icon){--icon-size: var(--jds-size-16);transition:transform .4s cubic-bezier(.8,0,.2,1)}}@media screen and (max-width: 1023.98px){.jds-animate jds-button.only-icon::part(button){--icon-size: var(--jds-size-24)}.jds-animate[variant=primary] jds-button.only-icon{display:none}}jds-icon:not(:defined),jds-badge:not(:defined),jds-spinner:not(:defined),jds-button:not(:defined),jds-dialog:not(:defined),jds-toast:not(:defined),jds-popover:not(:defined),jds-tooltip:not(:defined),jds-i18n:not(:defined){visibility:hidden}ul:not(.rte ul,.jds-rte ul),ol:not(.rte ol,.jds-rte ol),li:not(.rte li,.jds-rte li),menu:not(.rte menu,.jds-rte menu){list-style:none}::-webkit-scrollbar-thumb{background-color:#33333380;border-radius:4px}::-webkit-scrollbar-thumb:hover{background-color:#333333b3}::-webkit-scrollbar-track{background-color:transparent;border-radius:4px}::-webkit-scrollbar-track:hover{background-color:#ffffff80}@media screen and (min-width: 1024px){::-webkit-scrollbar{width:7px;height:7px}}@media screen and (max-width: 1023.98px){::-webkit-scrollbar{width:6px;height:6px}}.jds-h1{font:var(--jds-font-h1-medium);letter-spacing:var(--jds-h1-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h2{font:var(--jds-font-h2-medium);letter-spacing:var(--jds-h2-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h3{font:var(--jds-font-h3-medium);letter-spacing:var(--jds-h3-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h4{font:var(--jds-font-h4-medium);letter-spacing:var(--jds-h4-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h5{font:var(--jds-font-h5-medium);letter-spacing:var(--jds-h5-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h6{font:var(--jds-font-h6-semibold);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-b1{font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b1.font-regular{font:var(--jds-font-b1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b2{font:var(--jds-font-b2-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b2.font-regular{font:var(--jds-font-b2-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b3{font:var(--jds-font-b3-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b3.font-regular{font:var(--jds-font-b3-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-c1{font:var(--jds-font-c1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l3))}:root:not([lang=ja]){--jds-list-padding: var(--jds-size-20)}:root[lang=ja]{--jds-list-padding: var(--jds-size-24)}.jds-rte{word-wrap:break-word;overflow-wrap:break-word;word-break:keep-all;font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-rte ul li{list-style:disc}.jds-rte ol li{list-style:decimal}.jds-rte h1{font:var(--jds-font-h1-medium);letter-spacing:var(--jds-h1-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h2{font:var(--jds-font-h2-medium);letter-spacing:var(--jds-h2-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h3{font:var(--jds-font-h3-medium);letter-spacing:var(--jds-h3-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h4{font:var(--jds-font-h4-medium);letter-spacing:var(--jds-h4-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h5{font:var(--jds-font-h5-medium);letter-spacing:var(--jds-h5-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h6{font:var(--jds-font-h6-semibold);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte p{font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2));margin-bottom:var(--jds-size-16)}.jds-rte p:last-child{margin-bottom:0}.jds-rte strong,.jds-rte b{font-weight:600}.jds-rte a{text-decoration:underline}.jds-rte em,.jds-rte i{font-style:normal}.jds-rte.font-regular,.jds-rte.font-regular p{font:var(--jds-font-b1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}@media screen and (min-width: 1024px){.jds-rte ul,.jds-rte ol{padding-inline-start:var(--jds-list-padding)}}@media screen and (max-width: 1023.98px){.jds-rte ul,.jds-rte ol{padding-inline-start:var(--jds-size-36)}@supports (-webkit-touch-callout: none){.jds-rte ul{padding-inline-start:var(--jds-size-40)}.jds-rte ol{padding-inline-start:var(--jds-size-56)}}}.hidden,.pc-show,.mb-show{display:none}.text-overflow-1{display:-webkit-box;line-clamp:1;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}.text-overflow-2{display:-webkit-box;line-clamp:2;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}.text-overflow-3{display:-webkit-box;line-clamp:3;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis}@media screen and (min-width: 1024px){.pc-show{display:block}}@media screen and (max-width: 1023.98px){.mb-show{display:block}}", Qs = k`
  jds-dialog#rulesDialog{
    text-align: left;
  }
  jds-dialog#rulesDialog::part(dialog-container){
    min-height: 320px;
    max-height: 560px;
    height: auto;
  }
  jds-dialog#rulesDialog::part(dialog-content) {
    padding-left: 48px;
    padding-right: 10px;
    margin-right: 0;
  }
  jds-dialog#rulesDialog .dialog-content .detail {
    padding-right: 38px;
    max-height: 436px;
    overflow-y: auto;
  }
  jds-dialog#rulesDialog .dialog-content .detail img {
    width: auto;
    max-width: 100% !important;
    height: auto;
  }
  jds-dialog::part(dialog-content) {
    overflow: unset;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog#rulesDialog{
      text-align: left;
    }
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 22.2222222222222vw;
      max-height: 39.8888888888889vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 0.694444444444444vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.63888888888889vw;
      max-height: 30.2777777777778vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      overflow: unset;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 85.3333333333333vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 106.666666666667vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, Zs = k`
  jds-dialog:not(#rulesDialog) {
    text-align:center;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-content) {
    padding-left: 48px;
    padding-right: 18px;
    margin-right: 0;
    overflow: unset;
  }
  jds-dialog:not(#rulesDialog) .detail {
    padding-right: 30px;
    max-height: 380px;
    overflow-y: auto;
  }
  jds-dialog:not(#rulesDialog)#thsDialog .detail {
    max-height: 360px;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 1.25vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.08333333333333vw;
      max-height: 26.3888888888889vw;
      overflow-y: auto;
    }
    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 25vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.66666666666667vw;
      max-height: 88vw;
      overflow-y: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, tr = k`
  :host {
    --jds-rules-dialog-width: 600px;
    --jds-prize-dialog-width: 640px;
  }
  :host .jds-container{
    width: 1200px;
    height: 600px;
    margin: 0 auto;
    padding: 48px 46px;
    position: relative;
    z-index: 1;
    display:flex;
    justify-content: space-around;
    background-repeat: no-repeat;
    background-size: cover;
    box-sizing: border-box;
  }
  :host .wrapper{
    padding: 88px 0 66px;
    background-color: #FDF3E5;
  }
  .bg-img{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
  .rg-box{
    display:flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 100%;
  }
  jds-dialog .dialog-tips {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  jds-dialog .dialog-title{
    margin-right: 30px;
    height: 72px;
    margin-bottom: 18px;
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    color: #1A1A1A;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog#rulesDialog .dialog-title {
    height: auto;
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .ths-tips .dialog-title {
    height: 100px;
    font-size: 24px;
    line-height: 32px;
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .dialog-image{
    margin-top: 24px;
    margin-bottom: 16px;
    width: 248px;
    height: 166px;
    object-fit: contain;
  }
  jds-dialog .notes-full {
    font-size:14px;
    line-height: 22px;
    width: 100%;
    color: rgba(12,11,11,1);
  }
  jds-dialog .notes-box {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 24px;
    margin-top: 16px;
    margin-bottom: 10px;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules  {
    flex-shrink: 0;
    position: relative;
    overflow: visible;
    display: flex;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules {
    font-size: 16px;
    font-weight: 500;
    text-decoration-line: underline;
    text-underline-offset: 4px;
    color: #D98B24;
    cursor: pointer;
  }
  .rules-content {
    display: none;
    box-sizing: border-box;
    width: 420px;
    text-align: left;
    color: #444444;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    background-color: #fff;
    box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
    border-radius: 12px;
    border: 1px solid #EEEEEE;
    padding: 20px 10px 26px 32px;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 501;
  }
  .rules-content::after {
    display: block;
    content: '';
    position: absolute;
    left: 100%;
    bottom: 10px;
    z-index: 0;
    transform: translateY(-50%);
    border-color: transparent;
    border-left-color: rgba(255, 255, 255, 1);
    border-style: solid;
    background-color: transparent;
    filter: drop-shadow(5px 0px 3px rgba(0, 0, 0, 0.17));
    border-width: 13px;
    width: 0vw;
    height: 0vw
  }
  .rules-content h4.title {
    text-align: left;
    font-weight: 600;
    font-size: 24px;
    color: #666;
    padding-bottom: 10px;
    border-bottom: 1px solid #DDD;
    margin-bottom: 14px;
    margin-right: 6px;
  }
  .rules-content .detail {
    padding-right: 6px;
    max-height: 360px;
    overflow-y: auto;
  }
  .rules-content .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  .rules-content .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  .rules-content .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog .notes-box .notes{
    text-align: left;
    font-size:14px;
    line-height: 22px;
    width: fit-content;
    max-width: 364px;
    color: rgba(12,11,11,1);
  }
  jds-dialog jds-button::part(button) {
    height: 48px;
    line-height: 48px;
    min-width: 180px;
    font-size: 16px;
    font-weight: 600;
    margin-top: 38px;
  }


  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    :host {
      --jds-rules-dialog-width: 41.6666666666667vw;
      --jds-prize-dialog-width: 44.4444444444444vw;
    }
    :host .jds-container{
      width: 83.3333333333333vw;
      height: 41.6666666666667vw;
      margin: 0 auto;
      padding: 3.33333333333333vw 3.19444444444444vw;
      position: relative;
      z-index: 1;
      display:flex;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 6.11111111111111vw 0 4.58333333333333vw;
      background-color: #FDF3E5;
    }
    .bg-img{
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
    }
    .rg-box{
      display:flex;
      justify-content: center;
      align-items: center;
      width: auto;
      height: 100%;
    }
    jds-dialog .dialog-tips {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    jds-dialog .dialog-title{
      margin-right: 2.08333333333333vw;
      height: 5vw;
      margin-bottom: 1.25vw;
      font-size: 1.94444444444444vw;
      line-height: 2.5vw;
      font-weight: 600;
      color: #1A1A1A;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 6.94444444444444vw;
      font-size: 1.66666666666667vw;
      line-height: 2.22222222222222vw;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image{
      margin-top: 1.66666666666667vw;
      margin-bottom: 1.11111111111111vw;
      width: 17.2222222222222vw;
      height: 11.5277777777778vw;
      object-fit: contain;
    }
    jds-dialog .notes-full {
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.66666666666667vw;
      margin-top: 1.11111111111111vw;
      margin-bottom: 0.694444444444444vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules  {
      flex-shrink: 0;
      position: relative;
      overflow: visible;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 1.11111111111111vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 0.277777777777778vw;
      color: #D98B24;
      cursor: pointer;
    }
    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 29.1666666666667vw;
      text-align: left;
      color: #444444;
      font-size: 1.11111111111111vw;
      line-height: 1.66666666666667vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 0.138888888888889vw 0.555555555555556vw 0px rgba(0,0,0,0.27);
      border-radius: 0.833333333333333vw;
      border: 1px solid #EEEEEE;
      padding: 1.38888888888889vw 0.694444444444444vw 1.80555555555556vw 2.22222222222222vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: block;
      content: '';
      position: absolute;
      left: 100%;
      bottom: 0.972222222222222vw;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-left-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0.347222222222222vw 0px 0.208333333333333vw rgba(0, 0, 0, 0.17));
      border-width: 0.902777777777778vw;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 1.66666666666667vw;
      color: #666;
      padding-bottom: 0.694444444444444vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 0.972222222222222vw;
      margin-right: 0.416666666666667vw;
    }
    .rules-content .detail {
      padding-right: 0.416666666666667vw;
      max-height: 25vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
    jds-dialog .notes-box .notes{
      text-align: left;
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: fit-content;
      max-width: 25.2777777777778vw;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 3.33333333333333vw;
      line-height: 3.33333333333333vw;
      min-width: 12.5vw;
      font-size: 1.11111111111111vw;
      font-weight: 600;
      margin-top: 2.63888888888889vw;
    }
  }


  @media screen and (max-width: 1023.98px){
    :host {
      --jds-rules-dialog-width: 600px;
      --jds-prize-dialog-width: 640px;
    }
    :host .jds-container{
      width: 91.4666666666667vw;
      height: 170.666666666667vw;
      margin: 0 auto;
      padding: 4.26666666666667vw 4.8vw;
      position: relative;
      z-index: 1;
      display:flex;
      align-items: center;
      flex-direction: column;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 15.4666666666667vw 0 16.9333333333333vw;
      background-color: #FDF3E5;
    }
    jds-dialog .dialog-title {
      margin-right: 2.66666666666667vw;
      margin-bottom: 5.33333333333333vw;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      color: #1A1A1A;
      height: unset;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 25.6vw;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      display: -webkit-box;
      line-clamp: 4;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image {
      margin-bottom: 4.26666666666667vw;
      width: 53.3333333333333vw;
      height: 35.7333333333333vw;
      object-fit: contain;
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 6.4vw;
      margin-top: 4.26666666666667vw;
      margin-bottom: 6.4vw;
      padding-left: 1.33333333333333vw;
      padding-right: 4vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 3.73333333333333vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 4px;
      color: #D98B24;
      cursor: pointer;
    }
    jds-dialog .notes-box .notes {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      color: rgba(12,11,11,1);
    }

    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 77.3333333333333vw;
      text-align: left;
      color: #444444;
      font-size: 3.73333333333333vw;
      line-height: 5.33333333333333vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
      border-radius: 2.66666666666667vw;
      border: 1px solid #EEEEEE;
      padding: 4.26666666666667vw 1.6vw 5.33333333333333vw 4.26666666666667vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: none;
      content: '';
      position: absolute;
      top: calc(100% + 10px);
      left: 80%;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-top-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.17));
      border-width: 10px;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      color: #333;
      padding-bottom: 2.66666666666667vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 2.66666666666667vw;
      margin-right: 2.66666666666667vw;
    }
    .rules-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 80vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
    jds-dialog .notes-full {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 10.6666666666667vw;
      line-height: 10.6666666666667vw;
      min-width: 42.6666666666667vw;
      font-size: 3.73333333333333vw;
      font-weight: 600;
      margin-top: 6.4vw;
    }
  }
`;
function ct(t) {
  const e = new RegExp(`(^| )${t}=([^;]*)(;|$)`), i = document.cookie.match(e);
  return i ? decodeURI(i[2]) : null;
}
function be(t, e, i) {
  const o = {
    value: e,
    expirationTime: Date.now() + i
  };
  localStorage.setItem(t, JSON.stringify(o));
}
function V(t) {
  const e = localStorage.getItem(t);
  if (e) {
    const i = JSON.parse(e);
    if (i.expirationTime > Date.now())
      return i.value;
    localStorage.removeItem(t);
  }
  return null;
}
function $t(t, e = 100) {
  let i = null;
  return function(...o) {
    i && clearTimeout(i), i = setTimeout(() => {
      t.apply(this, o);
    }, e);
  };
}
function Ve(t) {
  return new Promise((e, i) => {
    t && (typeof t == "number" || typeof t == "string") ? navigator.clipboard.writeText(String(t)).then(() => e(String(t))).catch(i) : i("The parameter must be Number or String.");
  });
}
function et(t) {
  return new Promise((e) => setTimeout(e, t));
}
function Xi(t, e) {
  var i;
  if (window.dataLayer || Array.isArray(window.dataLayer)) {
    const o = ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || ct("_jk_cr") || localStorage.getItem("JACKERY_ID_CR") || "US";
    Object.assign(e, { CR: o, country: o }), window.dataLayer.push({ event_parameters: null }), window.dataLayer.push({
      event: "ga4Event",
      event_name: t,
      event_parameters: {
        current_page: window.location.href,
        ...e
      }
    });
  }
}
function P(t) {
  return Xi("section_pointslottery_user_clicks", t);
}
function Ke(t = window.location.href) {
  const e = new URL(t), i = new URLSearchParams(e.search), o = {};
  for (const [s, r] of i.entries())
    if (s in o) {
      const n = o[s];
      Array.isArray(n) ? n.push(r) : o[s] = [n, r];
    } else
      o[s] = r;
  return o;
}
function Qt(t) {
  return /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(t);
}
function Nt(t) {
  return t ? t.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim() : "";
}
function $(t, e = "info", i = 3) {
  if (window.pushMessage && typeof window.pushMessage == "function") {
    window.pushMessage(t, e);
    return;
  }
  const o = `<jds-toast variant="${e}" countdownTime="${i}" icon closed>${t}</jds-toast>`, n = new DOMParser().parseFromString(o, "text/html").body.firstChild;
  n ? (document.body.appendChild(n), Object.assign(n.style, {
    position: "fixed",
    top: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: "var(--jds-index-notification)"
  }), n.open(), setTimeout(() => {
    n.remove();
  }, i * 1e3)) : console.error("创建Toast失败！");
}
window.showToast = $;
var er = Object.defineProperty, ir = Object.getOwnPropertyDescriptor, Zt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? ir(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && er(e, i, s), s;
};
let zt = class extends A {
  constructor() {
    super(...arguments), this.pcBgImg = "", this.mbBgImg = "", this.alt = "", this.issub = !1;
  }
  render() {
    return u`
      <picture>
        <source media="(min-width: 1024px)" srcset="${this.pcBgImg}">
        <source media="(max-width: 1023.98px)" srcset="${this.mbBgImg}">
        <img class="lucky-bg-img" issub="${this.issub}" src="${this.mbBgImg}" alt="${this.alt}" loading="lazy">
      </picture>
    `;
  }
};
zt.styles = k`
    .lucky-bg-img {
      width: 1200px;
      min-height: 600px;
      height: 100%;
    }
    .lucky-bg-img[issub="true"] {
      min-height: 100%;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      .lucky-bg-img {
        width: 83.3333333333333vw;
        min-height: 41.6666666666667vw;
      }
    }
    @media screen and (max-width: 1023.98px){
      .lucky-bg-img {
        width: 91.4666666666667vw;
        min-height: 170.666666666667vw;
      }
      .lucky-bg-img[issub="true"] {
        min-height: 100%;
      }
    }
  `;
Zt([
  g({ type: String, reflect: !1 })
], zt.prototype, "pcBgImg", 2);
Zt([
  g({ type: String, reflect: !1 })
], zt.prototype, "mbBgImg", 2);
Zt([
  g({ type: String, reflect: !1 })
], zt.prototype, "alt", 2);
Zt([
  g({ type: Boolean, reflect: !1 })
], zt.prototype, "issub", 2);
zt = Zt([
  _("lucky-bg-img")
], zt);
const xe = k`
  * {
    margin: 0;
    padding:0;
    word-break: break-word;
    font-family: var(--jds-font-family);
  }
  li {
    list-style:none;
  }
  .text-overflow-1 {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }


  jds-dialog .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  jds-dialog .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  jds-dialog .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container){
    background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
    background-size: 70px 70px;
    position: relative;
    height: 560px;
    border-radius: 20px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
    display: block;
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    top: 8px;
    bottom: 8px;
    background: #fff;
    border-radius: 20px;
    z-index: -1;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 4.86111111111111vw 4.86111111111111vw;
      position: relative;
      height: 39.8888888888889vw;
      border-radius: 1.38888888888889vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 0.555555555555556vw;
      right: 0.555555555555556vw;
      top: 0.555555555555556vw;
      bottom: 0.555555555555556vw;
      background: #fff;
      border-radius: 1.38888888888889vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 70px 70px;
      position: relative;
      height: max-content;
      border-radius: 5.33333333333333vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 1.06666666666667vw;
      right: 1.06666666666667vw;
      top: 1.06666666666667vw;
      bottom: 1.06666666666667vw;
      background: #fff;
      border-radius: 5.33333333333333vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
  }
`;
var or = Object.defineProperty, sr = Object.getOwnPropertyDescriptor, Ge = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? sr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && or(e, i, s), s;
};
let Gt = class extends A {
  constructor() {
    super(...arguments), this.isLogined = !1, this.activityInfo = {
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      btnTitleNologin: "",
      btnTitleLogin: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return u`
      <div class="lf-info">
        <div class="title">
          ${this.activityInfo.mainTitleType === 0 ? u`<h2 class="title-content text-overflow-3" .innerHTML="${this.activityInfo.mainTitle}"></h2>` : u`<img src="${this.activityInfo.mainTitle}" loading="lazy"/>`}
        </div>
        <div class="subtitle text-overflow-2" .innerHTML="${this.activityInfo.subTitle}"></div>
        ${this.isLogined ? u`<div class="tips tips-one text-overflow-2" .innerHTML="${this.activityInfo.btnTitleLogin}"></div>` : u`<div class="tips tips-one text-overflow-2" .innerHTML="${this.activityInfo.btnTitleNologin}"></div>`}
        <div class="pc-show">
          <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
          ${this.activityInfo.ruleTitle ? u`
                <div class="rule-text" @click="${this.showRulesDialog}">
                  <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                  <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                    <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                  </svg>
                </div>
              ` : ""}
        </div>
      </div>
    `;
  }
};
Gt.styles = [xe, k`
    .pc-show {
      display: block;
    }
    .lf-info{
      width: 460px;
      height: 100% ;
      text-align: center;
    }
    .title{
      display: flex;
      justify-content: center;
      align-items: center;
      width: 460px;
      height: 238px;
      overflow: hidden;
    }
    .title img{
      width: 100%;
      height: auto;
    }
    .title-content{
      padding: 0 45px;
      font-weight: bold;
      font-style: normal;
      font-size: 56px;
      color: #FD5000;
      line-height: 64px;
      height: 200px;
      place-content: center;
    }
    .subtitle{
      margin-top: 6px;
      width: 460px;
      font-weight: bold;
      font-size: 28px;
      color: #FD5000;
      line-height: 36px;
      font-style: normal;
    }
    .tips{
      width: 460px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      font-style: normal;
    }
    .tips-one{
      margin-top: 8px;
      color: #fd5000;
    }
    .tips-two{
      margin-top: 40px;
      color: #1A1A1A;
      text-align: center;
    }
    .rule-text {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 10px;
      margin-top: 16px;
      width:100%;
      font-weight: 500;
      font-size: 20px;
      color: #1A1A1A;
      line-height: 20px;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 20px;
      height: 20px;
      color: #1A1A1A;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      .pc-show {
        display: block;
      }
      .lf-info{
        width: 31.9444444444444vw;
        height: 100% ;
        text-align: center;
      }
      .title{
        display: flex;
        justify-content: center;
        align-items: center;
        width: 31.9444444444444vw;
        height: 16.5277777777778vw;
        overflow: hidden;
      }
      .title img{
        width: 100%;
        height: auto;
      }
      .title-content{
        padding: 0 3.125vw;
        font-weight: bold;
        font-style: normal;
        font-size: 3.88888888888889vw;
        color: #FD5000;
        line-height: 4.44444444444444vw;
        height: 13.8888888888889vw;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.416666666666667vw;
        width: 31.9444444444444vw;
        font-weight: bold;
        font-size: 1.94444444444444vw;
        color: #FD5000;
        line-height: 2.5vw;
        font-style: normal;
      }
      .tips{
        width: 31.9444444444444vw;
        font-weight: 500;
        font-size: 1.11111111111111vw;
        line-height: 1.66666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin-top: 0.555555555555556vw;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 2.77777777777778vw;
        color: #1A1A1A;
        text-align: center;
      }
      .rule-text {
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 0.694444444444444vw;
        margin-top: 1.11111111111111vw;
        width:100%;
        font-weight: 500;
        font-size: 1.38888888888889vw;
        color: #1A1A1A;
        line-height: 1.38888888888889vw;
        font-style: normal;
        cursor: pointer;
      }
      .arrow {
        width: 1.38888888888889vw;
        height: 1.38888888888889vw;
        color: #1A1A1A;
      }
    }
    @media screen and (max-width: 1023.98px){
      .pc-show {
        display: none;
      }
      .lf-info{
        width: 80.8vw;
        display: flex;
        align-items: center;
        flex-direction: column;
      }
      .title{
        width: 61.3333333333333vw;
        height: 31.7333333333333vw;
        overflow: hidden;
      }
      .title-content{
        padding: 0 6vw;
        font-weight: bold;
        font-style: normal;
        font-size: 7.46666666666667vw;
        color: #FD5000;
        line-height: 8.53333333333333vw;
        height: 26.6666666666667vw;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.8vw;
        width: 80.8vw;
        font-weight: bold;
        font-size: 4.8vw;
        color: #FD5000;
        line-height: 5.86666666666667vw;
        font-style: normal;
      }
      .tips{
        width: 61.3333333333333vw;
        font-weight: 500;
        font-size: 3.46666666666667vw;
        line-height: 4.26666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin: 1.33333333333333vw 0;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 4.26666666666667vw;
        color: #1A1A1A;
        font-size: 3.46666666666667vw;
        line-height: 5.33333333333333vw;
      }
      .rule-text{
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 1.6vw;
        margin-top: 3.2vw;
        width:100%;
        font-weight: 500;
        font-size: 4vw;
        color: #1A1A1A;
        line-height: 4vw;
        font-style: normal;
        cursor: pointer;
      }
    }
  `];
Ge([
  g({ type: Boolean })
], Gt.prototype, "isLogined", 2);
Ge([
  g({ type: Object, reflect: !1 })
], Gt.prototype, "activityInfo", 2);
Gt = Ge([
  _("snippet-left-info")
], Gt);
const It = "data:image/png;base64,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", Je = k`
  .tips-two{
    margin-top: 40px;
    color: #1A1A1A;
    text-align: center;
  }
  .rule-text {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
    margin-top: 16px;
    width:100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 1;
    color: #1A1A1A;
    font-style: normal;
    cursor: pointer;
  }
  .grid {
    display: grid;
    gap: 12px;
    position: relative;
    margin: 0;
    text-align: center;
    box-sizing: border-box;
  }
  .prize {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 5px solid #fff;
    border-radius: 12px;
    padding: 8px 8px 6px;
    transition: transform 0.3s ease, border-color 0.3s ease;
    background-color: #fff;
    box-sizing: border-box;
  }
  .prize img {
    margin-bottom: 6px;
    width: 138px;
    height: 92px;
    object-fit: cover;
  }
  .prize .name, 
  .login-btn-tips {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 44px;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    color:#FE651F;
  }
  .prize .name p,
  .login-btn-tips p {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .selected {
    border:5px solid #FD891C;
    background-color:#FFEFB5;
  }
  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position:relative;
    padding: 0 20px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    color: #FFFFFF;
    background-color: #fd5000;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }
  .btn::before{
    content: " ";
    display: block;
    width: 67px;
    height: 43px;
    background-image: url(${H(It)});
    background-size: cover;
    position: absolute;
    top:6px;
    left:6px;
    z-index:1;
  }
  .btn:disabled {
    background-color: #ef6114;
    cursor: wait;
  }
  .btn:active {
    transform: scale(1.1);
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    .tips-two{
      margin-top: 2.77777777777778vw;
    }
    .rule-text {
      column-gap: 0.694444444444444vw;
      margin-top: 1.11111111111111vw;
      font-size: 1.38888888888889vw;
    }
    .grid {
      gap: 0.833333333333333vw;
    }
    .prize {
      border: 0.347222222222222vw solid #fff;
      border-radius: 0.833333333333333vw;
      padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
    }
    .prize img {
      margin-bottom: 0.416666666666667vw;
      width: 9.58333333333333vw;
      height: 6.38888888888889vw;
    }
    .prize .name, 
    .login-btn-tips {
      height: 3.05555555555556vw;
      font-size: 1.11111111111111vw;
      line-height: 1.38888888888889vw;
    }
    .selected {
      border: 0.347222222222222vw solid #FD891C;
    }
    .btn {
      padding: 0 1.38888888888889vw;
      border-radius: 0.555555555555556vw;
      box-shadow: 0 0.138888888888889vw 0.347222222222222vw rgba(0, 0, 0, 0.1);
    }
    .btn::before{
      content: " ";
      display: block;
      width: 4.65277777777778vw;
      height: 2.98611111111111vw;
      background-image: url(${H(It)});
      background-size: cover;
      position: absolute;
      top: 0.416666666666667vw;
      left: 0.416666666666667vw;
      z-index: 1;
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
  @media screen and (max-width: 1023.98px){
    .tips-two{
      margin-top: 4.26666666666667vw;
      color: #1A1A1A;
      font-size: 3.46666666666667vw;
      line-height: 5.33333333333333vw;
    }
    .rule-text{
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.6vw;
      margin-top: 3.2vw;
      width:100%;
      font-weight: 500;
      font-size: 4vw;
      color: #1A1A1A;
      line-height: 4vw;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 4vw;
      height: 4vw;
      color: #1A1A1A;
    }
    .grid {
      gap: 1.6vw;
    }
    .prize {
      border: 0.666666666666667vw solid #fff;
      border-radius: 1.6vw;
      padding: 1.06666666666667vw 1.06666666666667vw 0.8vw;
    }
    .prize img {
      margin-bottom: 0.266666666666667vw;
      width: 18.4vw;
      height: 12.2666666666667vw;
      object-fit: cover;
    }
    .prize .name,
    .login-btn-tips {
      width: 100%;
      height: 10.6666666666667vw;
      font-weight: 600;
      font-size: 3.2vw;
      line-height: 3.73333333333333vw;
      color:#FE651F;
    }
    .prize .name p,
    .login-btn-tips p {
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .selected {
      border:5px solid #FD891C;
      background-color:#FFEFB5;
    }
    .btn {
      position:relative;
      padding: 0 20px;
      border: none;
      border-radius: 8px;
      font-weight: bold;
      color: #FFFFFF;
      background-color: #fd5000;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
    .btn::before{
      content: " ";
      display: block;
      width: 67px;
      height: 43px;
      background-image: url(${H(It)});
      background-size: cover;
      position: absolute;
      top:6px;
      left:6px;
      z-index:1;
    }
    .btn:disabled {
      background-color: #ef6114;
      cursor: wait
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
`;
var rr = Object.defineProperty, nr = Object.getOwnPropertyDescriptor, _t = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? nr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && rr(e, i, s), s;
};
let pt = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return u`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
          ${this.isDrawing ? u`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : u`
                <div class="btn-txt text-overflow-1 ${this.isLogined || !this.activityInfo.guideText ? "btn-text-center" : "btn-text-end"}" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>
                ${this.isLogined || !this.activityInfo.guideText ? "" : u`<div class="login-btn-tips" .innerHTML="${this.activityInfo.guideText}"></div>`}
              `}
        </button>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 3; grid-row: 3;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 2; grid-row: 3;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 6 ? "selected" : ""}" style="grid-column: 1; grid-row: 3;">
          <img src="${this.prizes[6].image}" alt="${this.prizes[6].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[6].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 7 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[7].image}" alt="${this.prizes[7].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[7].name}</p>
          </div>
        </div>
      </div>
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
pt.styles = [xe, Je, k`
    :host{
      width: 606px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(3, 1fr);
      width: 606px;
      height: 505px;
    }
    .prize{
      padding: 4px 8px 4px;
    }
    .btn {
      --btn-size: 40px;
      grid-column: 2;
      grid-row: 2;
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .btn-txt{
      width: calc(100% + 40px);
      font-size: var(--btn-size);
      line-height: 56px;
      margin-top: 32px;
      margin-bottom: 6px;
    }
    .btn-text-center {
      align-items: center;
      margin-top: 0;
      margin-bottom: 0;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(3, 1fr);
        width: 42.0833333333333vw;
        height: 35.0694444444444vw;
      }
      .prize{
        padding: 0.277777777777778vw 0.555555555555556vw 0.277777777777778vw;
      }
      .btn {
        --btn-size: 2.77777777777778vw;
        grid-column: 2;
        grid-row: 2;
        border-radius: 1.11111111111111vw;
      }
      .btn-txt{
        width: calc(100% + 2.77777777777778vw);
        font-size: var(--btn-size);
        line-height: 3.88888888888889vw;
        margin-top: 2.22222222222222vw;
        margin-bottom: 0.416666666666667vw;
      }
      .btn-text-center {
        align-items: center;
        margin-top: 0;
        margin-bottom: 0;
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }
      .mb-show {
        display: block;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 80.8vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 4.8vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 25.3333333333333vw;
        border-radius: 2.13333333333333vw;
      }
      .btn::before{
        content: " ";
        display: block;
        width: 8.93333333333333vw;
        height: 5.2vw;
        background-image: url('https://f2e-cdn.jackery.com/@jackery/biz-components/latest/dist/assets/lucky-draw/icon-corner.png');
        background-size: cover;
        position: absolute;
        top: 0.666666666666667vw;
        left: 0.8vw;
        z-index:1;
      }
      .btn-txt{
        width: calc(100% + 5.33333333333333vw);
        font-size: 5.33333333333333vw;
        line-height: 7.46666666666667vw;
        margin-top: 0;
        margin-bottom: 0;
      }
      .btn-txt.btn-text-end {
        margin-top: 4.80000000000003vw;
      }
    }
  `];
_t([
  g({ type: Number, reflect: !0 })
], pt.prototype, "currentIndex", 2);
_t([
  g({ type: Array, reflect: !1 })
], pt.prototype, "prizes", 2);
_t([
  g({ type: Boolean, reflect: !0 })
], pt.prototype, "isDrawing", 2);
_t([
  g({ type: Boolean, reflect: !0 })
], pt.prototype, "isLogined", 2);
_t([
  g({ type: Object, reflect: !1 })
], pt.prototype, "activityInfo", 2);
pt = _t([
  _("snippet-right-eight-prizes")
], pt);
var ar = Object.defineProperty, lr = Object.getOwnPropertyDescriptor, Lt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? lr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && ar(e, i, s), s;
};
let ut = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return u`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
      </div>
      <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
        ${this.isDrawing ? u`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : u`<div class="btn-txt text-overflow-1" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>`}
      </button>
      ${this.isLogined ? "" : u`<div class="login-tips text-overflow-1" .innerHTML="${this.activityInfo.guideText}"></div>`}
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
ut.styles = [xe, Je, k`
    :host{
      width: 606px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 606px;
      height: 345px;
    }
    .prize {
      padding: 8px 8px 6px;
    }
    .btn {
      --btn-size:36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
      border-radius: 16px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 42.0833333333333vw;
        height: 23.9583333333333vw;
      }
      .prize {
        padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
        border-radius: 1.11111111111111vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }

      .mb-show {
        display: block;
      }
  
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 53.3333333333333vw;
      }

      .prize {
        height: 25.3333333333333vw;
      }
  
      .btn {
        --btn-size: 4.8vw;
        margin-top: 2.13333333333333vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 12.6666666666667vw;
        border-radius: 2.13333333333333vw;
      }

      .btn::before{
        content: " ";
        display: block;
        width: 8.93333333333333vw;
        height: 5.2vw;
        background-image: url('https://f2e-cdn.jackery.com/@jackery/biz-components/latest/dist/assets/lucky-draw/icon-corner.png');
        background-size: cover;
        position: absolute;
        top: 0.666666666666667vw;
        left: 0.8vw;
        z-index:1;
      }
  
      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
Lt([
  g({ type: Number, reflect: !0 })
], ut.prototype, "currentIndex", 2);
Lt([
  g({ type: Array, reflect: !1 })
], ut.prototype, "prizes", 2);
Lt([
  g({ type: Boolean, reflect: !0 })
], ut.prototype, "isDrawing", 2);
Lt([
  g({ type: Boolean, reflect: !0 })
], ut.prototype, "isLogined", 2);
Lt([
  g({ type: Object, reflect: !1 })
], ut.prototype, "activityInfo", 2);
ut = Lt([
  _("snippet-right-six-prizes")
], ut);
var dr = Object.defineProperty, cr = Object.getOwnPropertyDescriptor, Pt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? cr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && dr(e, i, s), s;
};
let gt = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return u`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;">
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
      </div>
      <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
        ${this.isDrawing ? u`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : u`<div class="btn-txt text-overflow-1" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>`}
      </button>
      ${this.isLogined ? "" : u`<div class="login-tips text-overflow-1" .innerHTML="${this.activityInfo.guideText}"></div>`}
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
gt.styles = [xe, Je, k`
    :host{
      width: 400px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 400px;
      height: 345px;
    }
    .btn {
      --btn-size: 36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 27.7777777777778vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 27.7777777777778vw;
        height: 23.9583333333333vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      .mb-show {
        display: block;
      }
      :host{
        width: 80.8vw;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 53.3333333333333vw;
        height: 52.2666666666667vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 5.33333333333333vw;
        margin-top: 2.66666666666667vw;
        padding: 0 2.66666666666667vw;
        width: 53.3333333333333vw;
        font-size: var(--btn-size);
        height: 12.8vw;
      }
      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
Pt([
  g({ type: Number, reflect: !0 })
], gt.prototype, "currentIndex", 2);
Pt([
  g({ type: Array, reflect: !1 })
], gt.prototype, "prizes", 2);
Pt([
  g({ type: Boolean, reflect: !0 })
], gt.prototype, "isDrawing", 2);
Pt([
  g({ type: Boolean, reflect: !0 })
], gt.prototype, "isLogined", 2);
Pt([
  g({ type: Object, reflect: !1 })
], gt.prototype, "activityInfo", 2);
gt = Pt([
  _("snippet-right-four-prizes")
], gt);
var hr = Object.defineProperty, pr = Object.getOwnPropertyDescriptor, I = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? pr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && hr(e, i, s), s;
};
let T = class extends A {
  constructor() {
    super(...arguments), this.platform = "membership", this.actId = "", this.shopifyShopId = "", this.customTag = "", this.showLuckyDraw = !1, this.prizes = [], this.currentIndex = -1, this.targetIndex = -1, this.isDrawing = !1, this.isLogined = !1, this.showRulesModal = !1, this.showTKSModal = !1, this.showXNKModal = !1, this.showYHQModal = !1, this.showJFModal = !1, this.timer = void 0, this.isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0, this.drawStartTime = 0, this.activityInfo = {
      shopId: "",
      busId: "",
      backgroundColor: "",
      pcBgImg: "",
      mbBgImg: "",
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      balance: 0,
      btnTitleNologin: "",
      btnTitleLogin: "",
      joinTimes: 0,
      joinDays: 0,
      joinMaxText: "",
      ruleTitle: "",
      ruleText: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      tksTitle: "",
      tksImg: "",
      tipsOther: ""
    }, this.drawResults = {
      itemId: 1,
      balance: 1,
      btnText: "按钮",
      image: "",
      btnLink: "https://www.jackery.com/",
      tipsLoseWindowTitle: "未中奖-弹窗标题",
      tipsLoseContent: "未中奖-备注文案",
      tipsWindowTitle: "中奖-优惠券和礼品卡标题",
      tipsCopyBtnText: "中奖-复制按钮code",
      code: "",
      tipsCopySuccessText: "中奖-复制成功提示语",
      tipsRuleBtnText: "中奖-规则按钮rules",
      tipsWindowTitleVc: "中奖-虚拟卡标题",
      tipsContentVc: "中奖-虚拟卡备注文案",
      tipsRuleBtnTextVc: "中奖-虚拟卡-规则按钮rules",
      tipsWindowTitlePoint: "中奖-积分标题",
      tipsContentPoint: "中奖-积分备注文案",
      welfareType: "default",
      couponRules: ""
    }, this.debounceOnClick = $t(() => this.loginCheck(), 300);
  }
  // mock data
  // async mockData() {
  //   await delay(300)
  //   this.drawResults = {
  //     "balance": 5,
  //     "welfareType": "virtual_card",
  //     "image": "https://cdn.shopify.com/s/files/1/0550/0524/9633/files/gift_card.png?v=1693815666",
  //     "tipsLoseWindowTitle": "奖品弹窗",
  //     "tipsLoseContent": "备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案",
  //     "tipsWindowTitle": "优惠券和礼品卡标题（含变量）title： {item_title}优惠券和礼品卡标题（含变量）title： {item_title}优惠券和礼品卡标题（含变量）title： {item_title}",
  //     "tipsCopyBtnText": "Code",
  //     "tipsCopySuccessText": "Copy Success",
  //     "tipsRuleBtnText": "Rules",
  //     "tipsWindowTitleVc": "虚拟卡标题（含变量）： gj虚拟奖品001（对外）虚拟卡标题（含变量）： gj虚拟奖品001（对外）虚拟卡标题（含变量）： gj虚拟奖品001（对外）",
  //     "tipsContentVc": "虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案",
  //     "tipsRuleBtnTextVc": "虚拟卡Rules",
  //     "tipsWindowTitlePoint": "积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}",
  //     "tipsContentPoint": "积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案",
  //     "code": "44DDGGTYX8HFG",
  //     "couponRules": "<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>2.虚拟电视剧的撒娇，虚拟电视剧的撒娇</p>\n<p>3.虚拟电视剧的撒娇</p>\n<p>4.哇哈哈</p>\n<p>5.3434223@##！#！</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>2.虚拟电视剧的撒娇，虚拟电视剧的撒娇</p>\n<p>3.虚拟电视剧的撒娇</p>\n<p>4.哇哈哈</p>\n<p>5.3434223@##！#！</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>",
  //     "btnText": "Spin Again",
  //     "btnLink": "",
  //     "itemId": 113
  //   }
  //   await delay(200)
  //   this.currentIndex = 1
  //   this.showDialog(this.drawResults.welfareType);
  // }
  async connectedCallback() {
    super.connectedCallback(), this.isLogined = ct("_jk_user") != null, await this.getActivityInfo();
    const { lucky_act_id: t } = Ke();
    t && setTimeout(() => {
      const e = document.querySelector(`jbc-lucky-draw[actId=${t}]`) || document.querySelector("jbc-lucky-draw");
      window.scrollTo({ top: (e == null ? void 0 : e.offsetTop) || 0 });
    }, 1e3);
  }
  // 登录态校验
  async loginCheck() {
    var t;
    if (!this.isDrawing) {
      if (this.isDrawing = !0, ct("_jk_user"))
        this.startDraw();
      else if (window != null && window.pageInfo) {
        if (window.pageInfo.platform === "AEM") {
          if (!window.pageInfo.enablePwdFreeLoginOrReg) {
            $("The password-free login function is not enabled. Please use normal login.", "warning");
            return;
          }
          const s = document.querySelector(".header .login-popup");
          s && (s.style.display = "block");
        }
        const e = window.location.search ? window.location.href + `#lucky_act_id=${this.actId || this.activityInfo.busId}` : window.location.href + `?lucky_act_id=${this.actId || this.activityInfo.busId}`, i = ["JFCJ-Register"], o = this.customTag ? i.concat(((t = this.customTag) == null ? void 0 : t.split(",")) || []).filter(Boolean) : i;
        window != null && window.jackeryLoginApp && (window == null || window.jackeryLoginApp.modalLogin({
          register_act: o.join(","),
          return_url: e
          // 返回当前页面
        })), await et(500), this.isDrawing = !1;
      } else
        $("Please log in first.", "warning"), await et(500), this.isDrawing = !1;
      P({
        module: "Lottery button",
        // 固定模块文案
        button_name: this.activityInfo.btnTitle,
        // 抽奖按钮文案
        status: this.isLogined ? "logged_in" : "tourist"
        // 登录状态
      });
    }
  }
  // 动画执行
  startDraw() {
    this.drawStartTime = Date.now(), this.currentIndex = 0, this.requestUpdate();
    const t = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate();
    }, 200), e = setInterval(() => {
      this.targetIndex != -1 && (clearInterval(t), clearInterval(e), this.finalAnimation());
    }, 200);
    this.joinActivity(t, e);
  }
  // 最后选中动画
  finalAnimation() {
    const t = this.targetIndex, e = this.prizes.length;
    let i = 0;
    t > this.currentIndex ? i = t - this.currentIndex : t === this.currentIndex ? i = 0 : i = e + t - this.currentIndex;
    const o = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate(), i--, i <= 0 && (clearInterval(o), this.currentIndex = t, this.getActivityInfo(), this.requestUpdate(), this.isDrawing = !1, this.showDialog(this.drawResults.welfareType), this.newCustomEvent());
    }, 200);
  }
  // 重置动画
  resetAnimation(t, e) {
    t && clearInterval(t), e && clearInterval(e), this.currentIndex = -1, this.targetIndex = -1, this.requestUpdate();
  }
  // 显示 规则弹窗
  showRulesDialog() {
    var i, o;
    this.showRulesModal = !0;
    const t = (i = this.shadowRoot) == null ? void 0 : i.querySelector("jds-dialog#rulesDialog"), e = (o = t == null ? void 0 : t.querySelector(".dialog-title")) == null ? void 0 : o.innerText;
    P({
      module: "Lottery button",
      // 固定模块文案
      button_name: e,
      // 规则按钮文案
      status: this.isLogined ? "logged_in" : "tourist"
      // 登录状态
    });
  }
  // 关闭所有弹窗，并重置动画 + 移除事件
  closeAllDialog() {
    ["showRulesModal", "showTKSModal", "showYHQModal", "showJFModal", "showXNKModal"].forEach((e) => this[e] = !1), this.resetAnimation(), this.removeRulesEvent();
  }
  // 处理对话框关闭事件
  handleDialogClose() {
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      // "button_name": this.drawResults.btnText, // 奖品按钮文案
      button_name: "close"
      // 关闭按钮文案
    }), this.closeAllDialog();
  }
  // 处理对话框跳转链接
  handleDialogLink() {
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.btnText,
      // 奖品按钮文案
      link: this.drawResults.btnLink
      // 跳转链接
    });
  }
  // 自定义抽奖结束事件用于外部监听
  newCustomEvent() {
    const t = new CustomEvent("luckyDrawDone", {
      detail: {
        status: !0
      }
    });
    document.dispatchEvent(t);
  }
  // 获取活动基本信息
  async getActivityInfo() {
    var s, r, n;
    const e = `${((s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/getCjActivityInfo`, i = {};
    !this.platform || this.platform === "membership" ? i.shopifyShopId = this.shopifyShopId : (i.platform = this.platform, i.actId = this.actId, i.shopifyShopId = this.shopifyShopId), i.showType = "grid", i.actType = "point";
    const o = ct("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const a = await j.get(e, {
        params: i,
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: o,
          Cr: ((r = window == null ? void 0 : window.pageInfo) == null ? void 0 : r.cr) || "US",
          Lang: ((n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.lang) || "en"
        }
      }), { data: l, success: d } = a.data;
      if (d && l !== null) {
        const { bgValue: h, bgValue1: p, items: w, ...x } = l;
        this.activityInfo = {
          ...x,
          pcBgImg: h,
          mbBgImg: p
        }, this.prizes = w, this.showLuckyDraw = !0;
      } else
        this.showLuckyDraw = !1;
    } catch (a) {
      this.showLuckyDraw = !1, a.code === "ECONNABORTED" ? console.error("请求超时:", a.message) : console.error("接口异常:", a);
    }
  }
  // 抽奖接口
  async joinActivity(t, e) {
    var n, a, l;
    const o = `${((n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/cjActivityJoin`, s = {};
    !this.platform || this.platform === "membership" ? (s.platform = "member", s.actId = this.activityInfo.busId) : (s.platform = this.platform, s.actId = this.actId), s.actType = "point", s.showType = "grid", s.shopId = this.activityInfo.shopId;
    const r = ct("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const d = await j.post(o, s, {
        withCredentials: !0,
        // 允许携带 Cookies
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: r,
          Cr: ((a = window == null ? void 0 : window.pageInfo) == null ? void 0 : a.cr) || "US",
          Lang: ((l = window == null ? void 0 : window.pageInfo) == null ? void 0 : l.lang) || "en"
        }
      }), { data: h, code: p, success: w, message: x } = d.data;
      if (w && h !== null)
        if (this.drawResults = h, this.drawResults.welfareType) {
          await et(Math.max(Date.now() - this.drawStartTime, 3e3));
          const f = h.itemId;
          this.targetIndex = this.prizes.findIndex((b) => b.itemId === f);
        } else
          await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
      else
        await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(x, "warning"), this.resetAnimation(t, e), p === 500209 && P({
          module: "Lottery button",
          // 固定模块文案
          button_name: "Lack of points",
          // 积分不足文案
          status: "logged_in"
          // 登录状态
        });
    } catch {
      await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
    }
  }
  // 中奖后，展示对应弹窗
  showDialog(t) {
    const i = {
      coupon: "showYHQModal",
      gift_card: "showYHQModal",
      point: "showJFModal",
      virtual_card: "showXNKModal",
      default: "showTKSModal"
    }[t] || "showTKSModal";
    this[i] = !0, this.registerRulesEvent(), P({
      module: "Awards",
      // 固定模块文案
      position: t !== "default" ? this.prizes[this.currentIndex].name : "Thanks！"
      // 奖品名称
    });
  }
  // 复制 code 码
  copyAndUseCode() {
    this.drawResults.code && (Ve(this.drawResults.code).then(() => $(this.drawResults.tipsCopySuccessText, "success")).catch(() => console.log("Copy Failed")), P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.code
      // 奖品券码
    }));
  }
  // 注册 rules 交互事件
  registerRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (this.mouseEnterEvent = this.mouseEnterEvent.bind(this), this.mouseLeaveEvent = this.mouseLeaveEvent.bind(this), this.clearTimeoutEvent = this.clearTimeoutEvent.bind(this), t.addEventListener("mouseover", this.clearTimeoutEvent), t.addEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.addEventListener("touchend", this.mouseEnterEvent) : o.addEventListener("mouseenter", this.mouseEnterEvent), o.addEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 移除 rules 交互事件
  removeRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (t.removeEventListener("mouseover", this.clearTimeoutEvent), t.removeEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.removeEventListener("touchend", this.mouseEnterEvent) : o.removeEventListener("mouseenter", this.mouseEnterEvent), o.removeEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 清除 timer 事件
  clearTimeoutEvent() {
    clearTimeout(this.timer);
  }
  // Rules 规则，鼠标移入 | touchend
  mouseEnterEvent(t) {
    var l, d, h, p;
    clearTimeout(this.timer);
    const e = t.target, i = (l = this.shadowRoot) == null ? void 0 : l.querySelector(".rules-content");
    i.style.display = "block";
    const { left: o, top: s, bottom: r } = e.getBoundingClientRect(), { width: n, height: a } = i.getBoundingClientRect();
    if (this.isTouchDevice) {
      const w = (h = (d = e.closest("jds-dialog")) == null ? void 0 : d.shadowRoot) == null ? void 0 : h.querySelector('[part="dialog-container"]'), x = Math.max(((p = w == null ? void 0 : w.getBoundingClientRect()) == null ? void 0 : p.left) || 0, 0), f = Math.max(s - a - 5, 0);
      i.style.left = x + "px", i.style.top = f + "px";
    } else
      i.style.left = Math.max(o - n - 20, 0) + "px", i.style.top = Math.max(r - a + 26, 0) + "px";
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.tipsRuleBtnText
      // 奖品规则按钮文案
    });
  }
  // Rules 规则，鼠标移出
  mouseLeaveEvent() {
    var e;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    this.timer = setTimeout(() => {
      t.style.display = "none";
    }, 150);
  }
  // 获取对应抽奖区DOM
  getPrizeContent() {
    switch (this.prizes.length) {
      case 8:
        return u`
        <snippet-right-eight-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
         ></snippet-right-eight-prizes>`;
      case 6:
        return u`
        <snippet-right-six-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
        ></snippet-right-six-prizes>`;
      case 4:
        return u`
        <snippet-right-four-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
        ></snippet-right-four-prizes>`;
      default:
        return u``;
    }
  }
  render() {
    return u`${this.showLuckyDraw ? u`
          <div class="wrapper" style="background-color:${this.activityInfo.backgroundColor};">
            <div class="jds-container">
              <!-- 背景图 -->
              <lucky-bg-img class="bg-img" .pcBgImg="${this.activityInfo.pcBgImg}" .mbBgImg="${this.activityInfo.mbBgImg}" alt="Lucky Draw Background Image"></lucky-bg-img>

              <!-- 左侧文案 -->
              <snippet-left-info @show-rules-dialog="${this.showRulesDialog}" .activityInfo="${this.activityInfo}" .isLogined="${this.isLogined}"></snippet-left-info>

              <!-- 右侧抽奖区 -->
              <div class="rg-box">
                ${this.getPrizeContent()}
              </div>
            </div>

            <!-- 规则弹窗 -->
            <jds-dialog id="rulesDialog" ?open=${this.showRulesModal} style="--jds-dialog-width:var(--jds-rules-dialog-width)" critical @closed="${this.closeAllDialog}">
              <h2 class="dialog-title" .innerHTML="${this.activityInfo.ruleTitle}"></h2>
              <section class="dialog-content">
                <div class="detail jds-rte font-regular" .innerHTML="${this.activityInfo.ruleText}"></div>
              </section>
            </jds-dialog>

            <!-- 中奖弹窗 - 谢谢参与 -->
            <jds-dialog id="thsDialog" ?open=${this.showTKSModal} style="--jds-dialog-width:var(--jds-prize-dialog-width)" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips ths-tips">
                <div class="dialog-title">${this.drawResults.tipsLoseWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsLoseContent}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button name="primary-action" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 虚拟卡 -->
            <jds-dialog id="xnkDialog" ?open=${this.showXNKModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);"  critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips xnk-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitleVc}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  <div class="notes">${this.drawResults.tipsContentVc}</div>
                  ${this.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnTextVc}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 优惠券礼品卡 -->
            <jds-dialog id="yhqDialog" ?open=${this.showYHQModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips yhq-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  ${this.drawResults.code ? u`<div class="code" @click="${this.copyAndUseCode}">${this.drawResults.tipsCopyBtnText}:${this.drawResults.code}</div>` : ""}
                  ${this.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnText}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button class="btn" href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button class="btn" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 积分 -->
            <jds-dialog id="jfDialog" ?open=${this.showJFModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips jf-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitlePoint}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsContentPoint}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 规则 - tooltip -->
            ${this.drawResults.couponRules ? u`
                  <div class="rules-content">
                    <h4 class="title">${this.drawResults.tipsRuleBtnText}</h4>
                    <div class="detail jds-rte font-regular" .innerHTML="${this.drawResults.couponRules}"></div>
                  </div>
                ` : ""}
          </div>
        ` : ""}`;
  }
};
T.styles = [H(Xt), Qs, Zs, tr];
I([
  g({ type: String, reflect: !0 })
], T.prototype, "platform", 2);
I([
  g({ type: String, reflect: !0 })
], T.prototype, "actId", 2);
I([
  g({ type: String, reflect: !0 })
], T.prototype, "shopifyShopId", 2);
I([
  g({ type: String, reflect: !0 })
], T.prototype, "customTag", 2);
I([
  g({ type: Boolean })
], T.prototype, "showLuckyDraw", 2);
I([
  g({ type: Array })
], T.prototype, "prizes", 2);
I([
  g({ type: Number })
], T.prototype, "currentIndex", 2);
I([
  g({ type: Number })
], T.prototype, "targetIndex", 2);
I([
  g({ type: Boolean })
], T.prototype, "isDrawing", 2);
I([
  g({ type: Boolean })
], T.prototype, "isLogined", 2);
I([
  m()
], T.prototype, "showRulesModal", 2);
I([
  m()
], T.prototype, "showTKSModal", 2);
I([
  m()
], T.prototype, "showXNKModal", 2);
I([
  m()
], T.prototype, "showYHQModal", 2);
I([
  m()
], T.prototype, "showJFModal", 2);
I([
  m()
], T.prototype, "timer", 2);
I([
  m()
], T.prototype, "isTouchDevice", 2);
I([
  m()
], T.prototype, "drawStartTime", 2);
I([
  g({ type: Object })
], T.prototype, "activityInfo", 2);
I([
  g({ type: Object })
], T.prototype, "drawResults", 2);
T = I([
  _("jbc-lucky-draw")
], T);
var ur = Object.defineProperty, ye = (t, e, i, o) => {
  for (var s = void 0, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = n(e, i, s) || s);
  return s && ur(e, i, s), s;
};
class te extends A {
  constructor() {
    super(...arguments), this.platform = "membership", this.actId = "", this.shopifyShopId = "", this.customTag = "";
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), window.i18n && window.i18n.setCurrLanguage(((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.lang) || "en");
  }
}
ye([
  g({ type: String, reflect: !0 })
], te.prototype, "platform");
ye([
  g({ type: String, reflect: !0 })
], te.prototype, "actId");
ye([
  g({ type: String, reflect: !0 })
], te.prototype, "shopifyShopId");
ye([
  g({ type: String, reflect: !0 })
], te.prototype, "customTag");
var gr = Object.defineProperty, fr = Object.getOwnPropertyDescriptor, je = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? fr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && gr(e, i, s), s;
};
let st = class extends te {
  constructor() {
    super(...arguments), this.showLuckyComponent = !1, this.activityInfo = {
      shopId: "",
      busId: "",
      backgroundColor: "",
      pcBgImg: "",
      mbBgImg: "",
      mainTitle: "",
      subTitle: "",
      guideText: "",
      highlightErrTips: "",
      showPhoneInput: 0,
      joinMaxText: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      joinTimes: 0,
      joinDays: 0,
      ruleTitle: "",
      ruleText: "",
      btnPlayText: "",
      drawGuideText: "",
      scratchBoxBgImg: "",
      noSubscribeMaskImg: "",
      subscribeMaskImg: "",
      showPrizeBtnText: "",
      tryAgainBtnText: "",
      tksTitle: "",
      tksImg: "",
      privacyAgreementText: ""
    }, this.drawResults = {
      itemId: -1,
      balance: -1,
      btnText: "",
      title: "",
      image: "",
      btnLink: "",
      tipsLoseWindowTitle: "",
      tipsLoseContent: "",
      tipsWindowTitle: "",
      tipsCopyBtnText: "",
      code: "",
      tipsCopySuccessText: "",
      tipsRuleBtnText: "",
      tipsWindowTitleVc: "",
      tipsContentVc: "",
      tipsRuleBtnTextVc: "",
      tipsWindowTitlePoint: "",
      tipsContentPoint: "",
      welfareType: "default",
      couponRules: "",
      token: "",
      userJoinTimes: 0
    };
  }
};
je([
  m()
], st.prototype, "showLuckyComponent", 2);
je([
  m()
], st.prototype, "activityInfo", 2);
je([
  m()
], st.prototype, "drawResults", 2);
st = je([
  _("common-lucky-component")
], st);
const wr = k`
  :host .jds-container {
    margin: 0 auto;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-repeat: no-repeat;
    background-size: cover;
    box-sizing: border-box;
  }

  :host .wrapper {
    background-color: #FDF3E5;
  }

  .bg-img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }

  .rg-box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 100%;
  }

  @media screen and (min-width: 1440px) {
    :host {
      --jds-rules-dialog-width: 600px;
      --jds-prize-dialog-width: 640px;
    }

    :host .jds-container {
      width: 1200px;
      min-height: 600px;
      padding: 100px 120px 100px 80px;
    }

    :host .wrapper {
      padding: 88px 0 66px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    :host {
      --jds-rules-dialog-width: 41.666666666667vw;
      --jds-prize-dialog-width: 44.444444444444vw;
    }

    :host .jds-container {
      width: 83.333333333333vw;
      min-height: 41.666666666667vw;
      margin: 0 auto;
      padding: 6.944444444444vw 8.333333333333vw 6.944444444444vw 5.555555555556vw;
    }

    :host .wrapper {
      padding: 6.111111111111vw 0 4.583333333333vw;
    }
  }

  @media screen and (max-width: 1023.98px) {
    :host {
      --jds-rules-dialog-width: 80vw;
      --jds-prize-dialog-width: 85.333333333333vw;
    }

    :host .wrapper {
      padding: 11.733333333333vw 0 8.8vw;
    }

    :host .jds-container {
      margin: 0 auto;
      flex-direction: column;
      width: 91.466666666667vw;
      min-height: 90.666666666667vw;
      padding: 10.666666666667vw 8.533333333333vw;
    }
  }
`, vr = function(t) {
  return u`
  ${t.showLuckyComponent ? u`
      <div class="wrapper" style="background-color:${t.activityInfo.backgroundColor};">
        <div class="jds-container">
          <!-- 背景图 -->
          <lucky-bg-img class="bg-img"
            .pcBgImg="${t.activityInfo.pcBgImg}"
            .mbBgImg="${t.activityInfo.mbBgImg}"
            alt="Lucky Draw Background Image">
          </lucky-bg-img>

          <!-- 左侧文案 -->
          <scratch-left-info
            @show-rules-dialog="${t.handleShowRulesDialog}"
            @subscribe-success="${t.handleSubscribeSuccess}"
            .customTag="${t.customTag}"
            .activityInfo="${t.activityInfo}"
            .hightLightUnlockTips = "${t.hightLightUnlockTips}">
          </scratch-left-info>

          <!-- 右侧抽奖区 -->
          <scratch-right-box
            .props=${{ platform: t.platform, actId: t.activityInfo.busId, shopifyShopId: t.activityInfo.shopId, customTag: t.customTag }}
            .activityInfo="${t.activityInfo}"
            .drawResults="${t.drawResults}"
            .email="${t.email}"
            .drawState="${t.drawState}"
            @update-prize-details="${t.handleUpdatePrizeDetails}"
            @show-prize-details="${t.handleShowPrizeDetails}"
            @unlock-click="${t.showUnlockTips}"
            @draw-state-update="${t.handleDrawStateUpdate}"
          ></scratch-right-box>
        </div>

        <!-- 所有弹窗 -->
        <lucky-all-dialog
          .activityInfo=${t.activityInfo}
          .drawResults="${t.drawResults}"
          .showRulesDialog="${t.showRulesDialog}"
          .showCouponDialog="${t.showCouponDialog}"
          .showPointsDialog="${t.showPointsDialog}"
          .showThanksDialog="${t.showThanksDialog}"
          .showVirtualCardDialog="${t.showVirtualCardDialog}"
          @modal-state-update="${t.handleModalStateUpdate}"
        ></lucky-all-dialog>

      </div>
    ` : ""}`;
}, mr = k`
  jds-dialog .dialog-tips {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  jds-dialog .dialog-title {
    font-weight: 600;
    color: #1A1A1A;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  jds-dialog#rulesDialog .dialog-title {
    height: auto;
    line-clamp: 1;
    -webkit-line-clamp: 1;
  }

  jds-dialog .dialog-image {
    object-fit: contain;
  }

  jds-dialog .notes-full {
    width: 100%;
    color: rgba(12, 11, 11, 1);
  }

  jds-dialog .notes-box {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }

  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules {
    flex-shrink: 0;
    position: relative;
    overflow: visible;
    display: flex;
    font-weight: 500;
    text-decoration-line: underline;
    color: #D98B24;
    cursor: pointer;
  }

  .rules-content {
    display: none;
    box-sizing: border-box;
    text-align: left;
    color: #444444;
    font-weight: 400;
    background-color: #fff;
    border: 1px solid #EEEEEE;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 501;
  }

  .rules-content::after {
    display: block;
    content: '';
    position: absolute;
    left: 100%;
    z-index: 0;
    transform: translateY(-50%);
    border-color: transparent;
    border-left-color: rgba(255, 255, 255, 1);
    border-style: solid;
    background-color: transparent;
    width: 0;
    height: 0;
  }

  .rules-content h4.title {
    text-align: left;
    font-weight: 600;
    color: #666;
    border-bottom: 1px solid #DDD;
  }

  .rules-content .detail {
    overflow-y: auto;
  }

  .rules-content .detail li{
    list-style: inherit;
  }

  jds-dialog .notes-box .notes {
    text-align: left;
    width: fit-content;
    color: rgba(12, 11, 11, 1);
  }

  jds-dialog jds-button::part(button) {
    font-weight: 600;
  }

  jds-dialog:not(#rulesDialog) {
    text-align: center;
  }

  jds-dialog:not(#rulesDialog)::part(dialog-content) {
    margin-right: 0;
    padding-top: var(--jds-size-32);
    padding-bottom: var(--jds-size-32);
    overflow: unset;
  }

  jds-dialog:not(#rulesDialog) .detail {
    overflow-y: auto;
  }

  jds-dialog:not(#rulesDialog)::part(dialog-container) {
    position: relative;
    background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
  }

  jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
    display: block;
    content: '';
    position: absolute;
    background: #fff;
    z-index: -1;
  }

  @media screen and (min-width: 1440px) {
    jds-dialog .dialog-tips {
      margin-bottom: 18px;
      height: 72px;
    }

    jds-dialog .dialog-title {
      margin-right: 30px;
      font-size: 28px;
      line-height: 36px;
    }

    jds-dialog .dialog-tips.ths-tips{
      height: 100px;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: 24px;
      line-height: 32px;
    }

    jds-dialog .dialog-image {
      margin-top: 24px;
      margin-bottom: 16px;
      width: 248px;
      height: 166px;
    }

    jds-dialog .notes-full {
      font-size: 14px;
      line-height: 22px;
    }

    jds-dialog .notes-box {
      column-gap: 24px;
      row-gap: 34px;
      margin-top: 16px;
      margin-bottom: 10px;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 16px;
      text-underline-offset: 4px;
    }

    .rules-content {
      width: 420px;
      font-size: 16px;
      line-height: 24px;
      box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.27);
      border-radius: 12px;
      padding: 20px 10px 26px 32px;
    }

    .rules-content::after {
      bottom: 10px;
      filter: drop-shadow(5px 0px 3px rgba(0, 0, 0, 0.17));
      border-width: 13px;
    }

    .rules-content h4.title {
      font-size: 24px;
      padding-bottom: 10px;
      margin-bottom: 14px;
      margin-right: 6px;
    }

    .rules-content .detail {
      padding-right: 6px;
      max-height: 360px;
    }

    jds-dialog .notes-box .notes {
      font-size: 14px;
      line-height: 22px;
      max-width: 364px;
    }

    jds-dialog jds-button::part(button) {
      height: 48px;
      line-height: 48px;
      min-width: 180px;
      font-size: 16px;
      margin-top: 38px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 48px;
      padding-right: 18px;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 30px;
      max-height: 380px;
    }

    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 360px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 70px 70px;
      height: 560px;
      border-radius: 20px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 8px;
      right: 8px;
      top: 8px;
      bottom: 8px;
      border-radius: 20px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    jds-dialog .dialog-tips {
      margin-bottom: 1.25vw;
      height: 5vw;
    }

    jds-dialog .dialog-title {
      margin-right: 2.08333333333333vw;
      font-size: max(12px, 1.94444444444444vw);
      line-height: 2.5vw;
    }

    jds-dialog .dialog-tips.ths-tips {
      height: 6.94444444444444vw;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: max(12px, 1.66666666666667vw);
      line-height: 2.22222222222222vw;
    }

    jds-dialog .dialog-image {
      margin-top: 1.66666666666667vw;
      margin-bottom: 1.11111111111111vw;
      width: 17.2222222222222vw;
      height: 11.5277777777778vw;
    }

    jds-dialog .notes-full {
      font-size: max(12px, 0.972222222222222vw);
      line-height: 1.52777777777778vw;
    }

    jds-dialog .notes-box {
      column-gap: 1.66666666666667vw;
      row-gap: 2.36111111111111vw;
      margin-top: 1.11111111111111vw;
      margin-bottom: 0.694444444444444vw;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: max(12px, 1.11111111111111vw);
      text-underline-offset: 0.277777777777778vw;
    }

    .rules-content {
      width: 29.1666666666667vw;
      font-size: max(12px, 1.11111111111111vw);
      line-height: 1.66666666666667vw;
      box-shadow: 0px 0.138888888888889vw 0.555555555555556vw 0px rgba(0, 0, 0, 0.27);
      border-radius: 0.833333333333333vw;
      padding: 1.38888888888889vw 0.694444444444444vw 1.80555555555556vw 2.22222222222222vw;
    }

    .rules-content::after {
      bottom: 0.972222222222222vw;
      filter: drop-shadow(0.347222222222222vw 0px 0.208333333333333vw rgba(0, 0, 0, 0.17));
      border-width: 0.902777777777778vw;
    }

    .rules-content h4.title {
      font-size: max(12px, 1.66666666666667vw);
      padding-bottom: 0.694444444444444vw;
      margin-bottom: 0.972222222222222vw;
      margin-right: 0.416666666666667vw;
    }

    .rules-content .detail {
      padding-right: 0.416666666666667vw;
      max-height: 25vw;
    }

    jds-dialog .notes-box .notes {
      font-size: max(12px, 0.972222222222222vw);
      line-height: 1.52777777777778vw;
      max-width: 25.2777777777778vw;
    }

    jds-dialog jds-button::part(button) {
      height: 3.33333333333333vw;
      line-height: 3.33333333333333vw;
      min-width: 12.5vw;
      font-size: max(12px, 1.11111111111111vw);
      margin-top: 2.63888888888889vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 1.25vw;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.08333333333333vw;
      max-height: 26.3888888888889vw;
    }

    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 25vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 4.86111111111111vw 4.86111111111111vw;
      height: 39.8888888888889vw;
      border-radius: 1.38888888888889vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 0.555555555555556vw;
      right: 0.555555555555556vw;
      top: 0.555555555555556vw;
      bottom: 0.555555555555556vw;
      border-radius: 1.38888888888889vw;
    }
  }

  @media screen and (min-width: 1024px) {
    jds-dialog .dialog-title {
      line-clamp: 2;
      -webkit-line-clamp: 2;
    }

    jds-dialog .ths-tips .dialog-title {
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }
  }

  @media screen and (max-width: 1023.98px) {
    jds-dialog .dialog-tips {
      margin-bottom: 5.33333333333333vw;
      height: unset;
    }

    jds-dialog .dialog-title {
      margin-right: 2.66666666666667vw;
      font-size: max(12px, 5.33333333333333vw);
      line-height: 6.4vw;
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }

    jds-dialog .dialog-tips.ths-tips {
      height: 25.6vw;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: max(12px, 5.33333333333333vw);
      line-height: 6.4vw;
      line-clamp: 4;
      -webkit-line-clamp: 4;
    }

    jds-dialog .dialog-image {
      margin-bottom: 4.26666666666667vw;
      width: 53.3333333333333vw;
      height: 35.7333333333333vw;
    }

    jds-dialog .notes-full {
      font-size: max(12px, 3.46666666666667vw);
      line-height: 4.26666666666667vw;
    }

    jds-dialog .notes-box {
      column-gap: 6.4vw;
      row-gap: 5.86666666666667vw;
      margin-top: 4.26666666666667vw;
      margin-bottom: 6.4vw;
      padding-left: 1.33333333333333vw;
      padding-right: 4vw;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: max(12px, 3.73333333333333vw);
      text-underline-offset: 0.53333333333333vw;
    }

    jds-dialog .notes-box .notes {
      font-size: max(12px, 3.46666666666667vw);
      line-height: 4.26666666666667vw;
      color: rgba(12, 11, 11, 1);
    }

    .rules-content {
      width: 74.3333333333333vw;
      font-size: max(12px, 3.73333333333333vw);
      line-height: 5.33333333333333vw;
      box-shadow: 0 0.26666666666667vw 1.06666666666667vw 0 rgba(0, 0, 0, 0.27);
      border-radius: 2.66666666666667vw;
      padding: 4.26666666666667vw 1.6vw 5.33333333333333vw 4.26666666666667vw;
    }

    .rules-content::after {
      display: none;
      top: calc(100% + 1.33333333333333vw);
      left: 80%;
      filter: drop-shadow(0 0.66666666666667vw 0.4vw rgba(0, 0, 0, 0.17));
      border-width: 1.33333333333333vw;
    }

    .rules-content h4.title {
      font-size: max(12px, 5.33333333333333vw);
      color: #333;
      padding-bottom: 2.66666666666667vw;
      margin-bottom: 2.66666666666667vw;
      margin-right: 2.66666666666667vw;
    }

    .rules-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 80vw;
    }

    jds-dialog jds-button{
      margin-top: 6.4vw;
    }
    jds-dialog jds-button::part(button) {
      height: 10.6666666666667vw;
      line-height: 10.6666666666667vw;
      min-width: 42.6666666666667vw;
      font-size: max(12px, 3.73333333333333vw);
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-top: var(--jds-size-56);
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.66666666666667vw;
      max-height: 88vw;
    }

    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 9.33333333333333vw 9.33333333333333vw;
      height: max-content;
      border-radius: 5.33333333333333vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 1.06666666666667vw;
      right: 1.06666666666667vw;
      top: 1.06666666666667vw;
      bottom: 1.06666666666667vw;
      border-radius: 5.33333333333333vw;
    }
  }
`, br = k`
  jds-dialog#rulesDialog {
    text-align: left;
  }

  jds-dialog#rulesDialog::part(dialog-content) {
    margin-right: 0;
  }

  jds-dialog#rulesDialog .dialog-content .detail {
    overflow-y: auto;
  }

  jds-dialog#rulesDialog .dialog-content .detail img {
    width: auto;
    max-width: 100% !important;
    height: auto;
  }

  jds-dialog::part(dialog-content) {
    overflow: unset;
  }

  jds-dialog#rulesDialog::part(dialog-container) {
    height: auto;
  }

  @media screen and (min-width: 1440px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 320px;
      max-height: 560px;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 48px;
      padding-right: 10px;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 38px;
      max-height: 436px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 22.2222222222222vw;
      max-height: 39.8888888888889vw;
    }

    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 0.694444444444444vw;
      margin-right: 0;
    }

    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.63888888888889vw;
      max-height: 30.2777777777778vw;
    }
  }

  @media screen and (max-width: 1023.98px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 85.3333333333333vw;
    }

    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
    }

    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 106.666666666667vw;
    }

    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
    }
  }
`, xr = function(t) {
  return u`
    <!-- 规则弹窗 -->
    <jds-dialog id="rulesDialog" ?open=${t.showRulesDialog} style="--jds-dialog-width:var(--jds-rules-dialog-width)" critical @closed="${t.closeAllDialog}">
      <h2 class="dialog-title" .innerHTML="${t.activityInfo.ruleTitle}"></h2>
      <section class="dialog-content">
        <div class="detail jds-rte font-regular" .innerHTML="${t.activityInfo.ruleText}"></div>
      </section>
    </jds-dialog>

    <!-- 中奖弹窗 - 谢谢参与 -->
    <jds-dialog id="thsDialog" ?open=${t.showThanksDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width)" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips ths-tips">
        <div class="dialog-title">${t.drawResults.tipsLoseWindowTitle}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-full">${t.drawResults.tipsLoseContent}</div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? u`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : u`<jds-button name="primary-action" @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 虚拟卡 -->
    <jds-dialog id="xnkDialog" ?open=${t.showVirtualCardDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);"  critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips xnk-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitleVc}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-box">
          <div class="notes">${t.drawResults.tipsContentVc}</div>
          ${t.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${t.drawResults.tipsRuleBtnTextVc}</div>` : ""}
        </div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? u`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : u`<jds-button @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 优惠券礼品卡 -->
    <jds-dialog id="yhqDialog" ?open=${t.showCouponDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips yhq-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitle}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-box">
          ${t.drawResults.code ? u`<div class="code" @click="${t.copyAndUseCode}">${t.drawResults.tipsCopyBtnText}:${t.drawResults.code}</div>` : ""}
          ${t.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${t.drawResults.tipsRuleBtnText}</div>` : ""}
        </div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? u`<jds-button class="btn" href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : u`<jds-button class="btn" @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 积分 -->
    <jds-dialog id="jfDialog" ?open=${t.showPointsDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips jf-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitlePoint}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-full">${t.drawResults.tipsContentPoint}</div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? u`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : u`<jds-button @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 规则 - tooltip -->
    ${t.drawResults.couponRules ? u`
        <div class="rules-content">
          <h4 class="title"></h4>
          <div class="detail jds-rte font-regular" .innerHTML="${t.drawResults.couponRules}"></div>
        </div>
      ` : ""}
  `;
};
var yr = Object.defineProperty, jr = Object.getOwnPropertyDescriptor, nt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? jr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && yr(e, i, s), s;
};
let Y = class extends st {
  constructor() {
    super(...arguments), this.showRulesDialog = !1, this.showThanksDialog = !1, this.showVirtualCardDialog = !1, this.showCouponDialog = !1, this.showPointsDialog = !1, this.isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0, this.timer = void 0, this.touchEndFired = !1;
  }
  // 标记touchend事件是否已触发
  // 注册 rules 交互事件
  registerRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (this.mouseEnterEvent = this.mouseEnterEvent.bind(this), this.mouseLeaveEvent = this.mouseLeaveEvent.bind(this), this.clearTimeoutEvent = this.clearTimeoutEvent.bind(this), this.handleDocumentClick = this.handleDocumentClick.bind(this), t.addEventListener("mouseover", this.clearTimeoutEvent), t.addEventListener("mouseleave", this.mouseLeaveEvent), document.addEventListener("click", this.handleDocumentClick), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.addEventListener("touchend", (s) => {
        s.stopPropagation(), s.preventDefault(), this.touchEndFired || (this.touchEndFired = !0, this.mouseEnterEvent(s), setTimeout(() => {
          this.touchEndFired = !1;
        }, 300));
      }) : o.addEventListener("mouseenter", this.mouseEnterEvent), o.addEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 展示对应弹窗
  showDialog(t) {
    const i = {
      coupon: "showCouponDialog",
      gift_card: "showCouponDialog",
      point: "showPointsDialog",
      virtual_card: "showVirtualCardDialog",
      default: "showThanksDialog"
    }[t] || "showThanksDialog";
    this[i] = !0, this.registerRulesEvent();
  }
  // 复制 code 码
  copyAndUseCode() {
    this.drawResults.code && Ve(this.drawResults.code).then(() => $(this.drawResults.tipsCopySuccessText, "success")).catch(() => console.log("Copy Failed"));
  }
  // 关闭所有弹窗， 移除事件
  closeAllDialog() {
    ["showRulesDialog", "showCouponDialog", "showPointsDialog", "showVirtualCardDialog", "showThanksDialog"].forEach((e) => this[e] = !1), this.dispatchEvent(new CustomEvent("modal-state-update", {
      detail: {
        showRulesDialog: !1,
        showCouponDialog: !1,
        showPointsDialog: !1,
        showVirtualCardDialog: !1,
        showThanksDialog: !1
      }
    })), this.removeRulesEvent();
  }
  // 处理对话框关闭事件
  handleDialogClose() {
    this.closeAllDialog();
  }
  // 处理document点击事件
  handleDocumentClick(t) {
    var s;
    const e = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".rules-content"), i = t.target, o = i.closest("[data-rules-wrap]") || i.closest(".rules-content");
    e && !o && (e.style.display = "none");
  }
  // 移除 rules 交互事件
  removeRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (t.removeEventListener("mouseover", this.clearTimeoutEvent), t.removeEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.removeEventListener("touchend", this.mouseEnterEvent) : o.removeEventListener("mouseenter", this.mouseEnterEvent), o.removeEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 清除 timer 事件
  clearTimeoutEvent() {
    clearTimeout(this.timer);
  }
  // Rules 规则，鼠标移入 | touchend
  mouseEnterEvent(t) {
    var h, p, w, x;
    clearTimeout(this.timer);
    const e = t.target, i = (h = this.shadowRoot) == null ? void 0 : h.querySelector(".rules-content"), o = e.textContent || "", s = i.querySelector(".title");
    s && (s.textContent = o), i.style.display = "block";
    const { left: r, top: n, bottom: a } = e.getBoundingClientRect(), { width: l, height: d } = i.getBoundingClientRect();
    if (this.isTouchDevice) {
      const f = (w = (p = e.closest("jds-dialog")) == null ? void 0 : p.shadowRoot) == null ? void 0 : w.querySelector('[part="dialog-container"]'), b = Math.max(((x = f == null ? void 0 : f.getBoundingClientRect()) == null ? void 0 : x.left) || 0, 0), v = Math.max(n - d - 5, 0);
      i.style.left = b + "px", i.style.top = v + "px";
    } else
      i.style.left = Math.max(r - l - 20, 0) + "px", i.style.top = Math.max(a - d + 26, 0) + "px";
  }
  // Rules 规则，鼠标移出
  mouseLeaveEvent() {
    var e;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    this.timer = setTimeout(() => {
      t.style.display = "none";
    }, 150);
  }
  render() {
    return xr(this);
  }
};
Y.styles = [H(Xt), mr, br];
nt([
  g({ type: Boolean })
], Y.prototype, "showRulesDialog", 2);
nt([
  g({ type: Boolean })
], Y.prototype, "showThanksDialog", 2);
nt([
  g({ type: Boolean })
], Y.prototype, "showVirtualCardDialog", 2);
nt([
  g({ type: Boolean })
], Y.prototype, "showCouponDialog", 2);
nt([
  g({ type: Boolean })
], Y.prototype, "showPointsDialog", 2);
nt([
  m()
], Y.prototype, "isTouchDevice", 2);
nt([
  m()
], Y.prototype, "timer", 2);
nt([
  m()
], Y.prototype, "touchEndFired", 2);
Y = nt([
  _("lucky-all-dialog")
], Y);
const Qi = k`
  .info-box .title {
    display: flex;
    text-align: center;
    position: relative;
    justify-content: center;
    align-items: center;
    font-weight: 600;
    font-style: normal;
    color: #fbf5db;
  }

  .info-box .subtitle,
  .info-box .tips {
    font-weight: 500;
    font-style: normal;
    color: #fff;
  }

  .info-box .tips {
    text-align: center;
  }

  .info-box .tips.active *{
    color: inherit !important;
  }

  @media screen and (min-width: 1440px) {
    .info-box .title {
      width: 480px;
      font-size: 32px;
      line-height: 42px;
    }

    .info-box .subtitle {
      margin-top: 12px;
      font-size: 16px;
      line-height: 24px;
    }

    .info-box .subtitle,
    .info-box .tips {
      width: 400px;
    }

    .info-box .tips {
      margin-top: 28px;
      height: 16px;
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .info-box .title {
      width: 33.33vw;
      font-size: max(12px, 2.22vw);
      line-height: 2.92vw;
    }

    .info-box .subtitle {
      margin-top: 0.83vw;
      font-size: max(12px, 1.11vw);
      line-height: 1.67vw;
    }

    .info-box .subtitle,
    .info-box .tips {
      width: 27.78vw;
    }

    .info-box .tips {
      margin-top: 1.94vw;
      height: 1.11vw;
      font-size: max(12px, 0.83vw);
      line-height: 1.11vw;
    }
  }

  @media screen and (max-width: 1023.98px) {

    .info-box .title {
      font-size: max(12px, 8vw);
      line-height: 10.13333333333333vw;
    }

    .info-box .subtitle {
      margin-top: 1.06666666666667vw;
      font-size: max(12px, 4.26666666666667vw);
      line-height: 6.4vw;
      font-weight: 600;
    }

    .info-box .title,
    .info-box .subtitle,
    .info-box .tips {
      width: 74.4vw;
    }

    .info-box .tips {
      display: -webkit-box;
      font-size: max(12px, 3.73333333333333vw);
      line-height: 4.8vw;
      height: 9.6vw;
      font-weight: 600;
    }
  }
`, kr = k`
  .lf-info {
    flex-direction: column;
    height: 100%;
  }

  .lf-info {
    display: flex;
    text-align: center;
    align-items: center;
  }
  .info-box{
    display: none;
    flex-direction: column;
    align-items: center;
  }
  .rule-text {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-weight: 500;
    color: rgba(255, 255, 255, .6);
    font-style: normal;
    cursor: pointer;
  }

  .item-box {
    position: relative;
    display: flex;
    box-sizing: border-box;
  }

  input,
  input:focus,
  input:focus-visible {
    width: 100%;
    background-color: transparent;
    border: none;
    outline: none;
  }

  .email-box,
  .phone-box {
    background-color: #fff;
  }

  .item-box.sub-success {
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
  }

  .status-check {
    display: none;
    color: #fd5000;
  }

  .email-box label {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  .item-box.sub-success .status-required {
    display: none;
  }

  .item-box.sub-success .status-check {
    display: block;
  }

  .email-box .box-inp {
    width: 100%;
  }

  .item-box.sub-success .box-inp {
    color: #fff;
  }

  .item-box .error-tips {
    position: absolute;
    top: 106%;
    font-weight: 400;
    color: #ff3b30;
    text-align: left;
  }

  .email-box label>span {
    position: absolute;
    line-height: 1;
    color: rgba(0, 0, 0, .6);
  }

  .checkbox {
    position: relative;
    display: flex;
    align-items: flex-start;
    align-self: flex-start;
    cursor: pointer;
  }

  .item-box.checkbox-box {
    height: auto;
  }

  .checkbox .checkbox-original {
    position: absolute;
    left: 0;
    margin: 0;
    color: #fff;
    background: transparent;
    transition: border .3s ease;
    opacity: 0;
    outline: none;
    transform: none;
  }

  .checkbox .checkbox-original:checked:after {
    background-color: #ff5000;
    border: none;
    color: #fff;
  }

  .checkbox .checkbox-original:checked~.checkbox-inner:before {
    background: #fd5000;
    border-color: #fd5000;
    color: #fff;
  }

  .checkbox .jackery-icon-checkbox:before {
    color: transparent;
  }

  .checkbox .checkbox-inner .icon-check {
    display: none;
    color: #fff;
    align-items: center;
    justify-content: center;
  }

  .checkbox .checkbox-original:checked~.checkbox-inner .icon-check {
    display: flex;
  }

  .checkbox .checkbox-inner {
    position: relative;
    display: flex;
  }

  .checkbox .checkbox-inner:after,
  .checkbox .checkbox-inner:before {
    content: "";
    letter-spacing: -.02em;
    line-height: 1.33;
    position: absolute;
  }

  .checkbox .checkbox-inner:before {
    left: 0;
    box-sizing: border-box;
    border-color: hsla(0, 0%, 100%, .6);
    border-style: solid;
  }

  .checkbox .checkbox-input:hover .checkbox-inner:before {
    border-color: #fd5000;
  }

  .checkbox .checkbox-inner:after {
    color: #fff;
    cursor: pointer;
    font-weight: 900;
    pointer-events: none;
    z-index: 1;
  }

  .checkbox .checkbox-inner .jackery-icon-checkbox {
    opacity: 0;
    position: relative;
    transition: all .3s ease;
    z-index: 9;
  }

  .checkbox .checkbox-label {
    letter-spacing: 0;
    font-weight: 400;
    color: hsla(0, 0%, 100%, .6);
    text-align: left;
    font-style: normal;
  }

  .checkbox a {
    text-decoration: underline;
    color: hsla(0, 0%, 100%, .6);
  }

  .checkbox a:hover {
    color: #fff;
  }

  jds-button[class~=btn-subscribe] {
    width: 100%;
  }

  jds-button[class~=disabled] {
    width: 100%;
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  jds-button[class~=btn-subscribe]::part(button) {
    width: 100%;
    font-weight: 600;
    transition: all .3s ease;
    color: var(--btn-text-color);
    background-color: var(--btn-bg-color);
    border-color: var(--btn-bg-color);
  }

  jds-button[class=btn-subscribe]::part(button):hover {
    background-color: color-mix(in srgb, var(--btn-bg-color) 85%, #fff);
    border-color: color-mix(in srgb, var(--btn-bg-color) 85%, #fff);
  }

  jds-button[class=btn-subscribe]::part(button):active {
    background-color: color-mix(in srgb, var(--btn-bg-color) 90%, #000);
    border-color: color-mix(in srgb, var(--btn-bg-color) 90%, #000);
  }

  @media screen and (min-width: 1440px) {
    .lf-info {
      width: 480px;
    }

    .btn-subscribe {
      margin-bottom: 20px;
    }

    .rule-text {
      column-gap: 6px;
      font-size: 16px;
      line-height: 20px;
    }

    .form {
      margin-top: 12px;
      width: 400px;
    }

    .item-box {
      height: 48px;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: 14px;
    }

    .email-box,
    .phone-box {
      margin-bottom: 20px;
      padding: 12px;
      border-radius: 8px;
    }

    .status-check {
      font-size: 20px;
    }

    .email-box .box-inp {
      margin-left: 24px;
    }

    .item-box .error-tips {
      font-size: 12px;
      line-height: 14px;
    }

    .email-box label>span {
      font-size: 28px;
      top: 4px;
      height: 16px;
    }

    .checkbox-box {
      min-height: 24px;
      margin-bottom: 26px;
    }

    :host-context(html[lang='de']) .checkbox-box {
      margin-bottom: 34px;
    }

    .checkbox .checkbox-original {
      border: 1px solid hsla(0, 0%, 100%, .6);
      border-radius: 2px;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: 12px;
    }

    .checkbox .checkbox-inner .icon-check {
      width: 16px;
      font-size: 14px;
    }

    .checkbox .checkbox-input {
      height: 16px;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 16px;
      height: 16px;
    }

    .checkbox .checkbox-inner {
      font-size: 12px;
      width: 16px;
      height: 16px;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: 12px;
    }

    .checkbox .checkbox-inner:before {
      border-width: 1px;
      border-radius: 2px;
    }

    .checkbox .checkbox-label {
      margin-left: 12px;
      min-height: 14px;
      font-size: 12px;
      line-height: 14px;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 48px;
      font-size: 18px;
      line-height: 24px;
      border-radius: 8px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .lf-info {
      width: 33.33vw;
    }

    .btn-subscribe {
      margin-bottom: 1.39vw;
    }

    .rule-text {
      column-gap: 0.42vw;
      font-size: max(12px, 1.11vw);
      line-height: 1.39vw;
    }

    .form {
      margin-top: 0.83vw;
      width: 27.78vw;
    }

    .item-box {
      height: 3.33vw;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: max(12px, 0.97vw);
    }

    .email-box,
    .phone-box {
      margin-bottom: 1.39vw;
      padding: 0.83vw;
      border-radius: 0.56vw;
    }

    .status-check {
      font-size: max(12px, 1.39vw);
    }

    .email-box .box-inp {
      margin-left: 1.67vw;
    }

    .item-box .error-tips {
      font-size: max(12px, 0.83vw);
      line-height: 0.97vw;
    }

    .email-box label>span {
      font-size: max(12px, 1.94vw);
      top: 0.28vw;
      height: 1.11vw;
    }

    .checkbox-box {
      min-height: 1.67vw;
      margin-bottom: 1.81vw;
    }

    :host-context(html[lang='de']) .checkbox-box {
      margin-bottom: 2.36vw;
    }

    .checkbox .checkbox-original {
      border: 0.14vw solid hsla(0, 0%, 100%, .6);
      border-radius: 0.14vw;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: max(12px, 0.83vw);
    }

    .checkbox .checkbox-inner .icon-check {
      width: 1.11vw;
      font-size: max(12px, 0.97vw);
    }

    .checkbox .checkbox-input {
      height: 1.11vw;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 1.11vw;
      height: 1.11vw;
    }

    .checkbox .checkbox-inner {
      font-size: max(12px, 0.83vw);
      width: 1.11vw;
      height: 1.11vw;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: max(12px, 0.83vw);
    }

    .checkbox .checkbox-inner:before {
      border-width: 0.14vw;
      border-radius: 0.14vw;
    }

    .checkbox .checkbox-label {
      margin-left: 0.83vw;
      min-height: 0.97vw;
      font-size: max(12px, 0.83vw);
      line-height: 0.97vw;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 3.33vw;
      font-size: max(12px, 1.25vw);
      line-height: 1.67vw;
      border-radius: 0.56vw;
    }
  }

  @media screen and (min-width:1024px) {
    .info-box {
      display: flex;
    }
  }

  @media screen and (max-width:1023.98px) {
    :host{
      order: 2;
    }

    .lf-info {
      width: 74.4vw;
    }

    .form {
      margin-top: 4.27vw;
      width: 74.4vw;
    }

    .item-box {
      height: 12.8vw;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: max(12px, 3.2vw);
    }

    .email-box,
    .phone-box {
      margin-bottom: 4.27vw;
      padding: 2.67vw 3.2vw;
      border-radius: 1.6vw;
    }

    .status-check {
      font-size: max(12px, 5.33vw);
    }

    .email-box .box-inp {
      margin-left: 4.67vw;
    }

    .item-box .error-tips {
      top: 102%;
      font-size: max(12px, 2.67vw);
      line-height: 1.4;
    }

    .email-box label>span {
      font-size: max(12px, 5.33vw);
      top: 2vw;
      height: 3.2vw;
      color:#FD5000;
    }

    .checkbox{
      align-items: flex-start;
    }

    .checkbox-box {
      min-height: 4.27vw;
      margin-bottom: 9.33vw;
    }

    .checkbox-box .error-tips {
      margin-left: 3.2vw;
    }

    .checkbox .checkbox-original {
      border: 0.27vw solid hsla(0, 0%, 100%, .6);
      border-radius: 0.27vw;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: max(12px, 4vw);
    }

    .checkbox .checkbox-inner .icon-check {
      width: 4.27vw;
      font-size: max(12px, 3.47vw);
    }

    .checkbox .checkbox-input {
      height: 4.27vw;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 4.27vw;
      height: 4.27vw;
    }

    .checkbox .checkbox-inner {
      font-size: max(12px, 4vw);
      width: 4.27vw;
      height: 4.27vw;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: max(12px, 4vw);
    }

    .checkbox .checkbox-inner:before {
      border-width: 0.27vw;
      border-radius: 0.53vw;
    }

    .checkbox .checkbox-label {
      margin-left: 3.2vw;
      font-size: max(12px, 2.67vw);
      line-height: 1.4;
    }

    .btn-subscribe {
      margin-bottom: 5.33vw;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 12.8vw;
      font-size: max(12px, 4vw);
      line-height: 1.25;
      border-radius: 1.6vw;
    }

    .rule-text {
      column-gap: 1.6vw;
      font-size: max(12px, 4.8vw);
      line-height: 1.25;
    }
  }
`, $r = k`
  .phone-box .country-area-num {
    color: rgba(0, 0, 0, 0.7);
    word-break: normal;
    min-width: fit-content;
  }

  .phone-box .country-area-select {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  .phone-box .country-area-select .change-area {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
  }

  .country-area-select .change-area.active .select-arrow {
    transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    transition: transform .5s;
  }

  .phone-box .selected-flag {
    max-width:none;
    height: auto;
    background-color: #fff;
    box-shadow: 0 0 2px #ddd;
  }

  .phone-box .selected-item {
    display: block;
    position: relative;
    color: rgba(0, 0, 0, 0.7);
    word-break: normal;
  }

  /* 移除数字输入框的上下箭头 */
  .box-inp::-webkit-outer-spin-button,
  .box-inp::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .box-inp[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .phone-box .select-arrow {
    color: rgba(0, 0, 0, 0.7);
  }

  .item-box.sub-success .country-area-num,
  .item-box.sub-success .selected-item,
  .item-box.sub-success .select-arrow {
    color: #fff;
  }

  .phone-box .country-area-list {
    position: absolute;
    background-color: #fff;
    max-height: 30vh;
    overflow-y: auto;
    transition: all .2s ease;
    z-index: 1;
  }

  .country-area-list .search-swrapper {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  .country-area-list .search-swrapper input {
    width: 100%;
    color: #333;
    background: transparent;
    border: 1px solid #b3b3b3;
    box-sizing: border-box;
  }

  .country-area-list .no-data {
    text-align: center;
  }

  .country-area-list .country-item {
    position: relative;
    color: #202223;
    transition: all .3s;
    cursor: pointer;
    font-weight: 400;
    text-align: left;
  }

  .country-area-list .country-item:hover {
    background: #f6f6f7;
  }

  .country-area-list .country-item.active {
    background: #f2f7fe;
  }

  .country-area-list .country-item.active:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: #fd5000;
  }

  .phone-box .box-inp {
    width: 100%;
    border: none;
  }

  @media screen and (min-width: 1440px) {
    .phone-box .selected-flag {
      width: 32px;
    }

    .phone-box .selected-item {
      min-width: 20px;
    }

    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: 16px;
    }

    .phone-box .select-arrow {
      margin-right: 10px;
    }

    .phone-box .country-area-list {
      border-radius: 8px;
      left: -12px;
      bottom: calc(100% + 20px);
      box-shadow: 2px 2px 8px rgba(23, 24, 24, .051), 2px 2px 4px rgba(0, 0, 0, .149);
      width: 300px;
      min-height: 260px;
    }

    .country-area-list .search-swrapper {
      padding: 8px;
    }

    .country-area-list .search-swrapper input {
      padding: 8px 4px;
      border-radius: 4px;
    }

    .country-area-list .no-data {
      line-height: 60px;
    }

    .country-area-list .country-item {
      padding: 8px;
      font-size: 14px;
    }

    .country-area-list .country-item.active:before {
      width: 4px;
    }

    .phone-box .box-inp {
      margin-left: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .phone-box .selected-flag {
      width: 2.22vw;
    }

    .phone-box .selected-item {
      min-width: 1.39vw;
    }
    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: max(12px, 1.11vw);
    }

    .phone-box .select-arrow {
      margin-right: 0.69vw;
    }

    .phone-box .country-area-list {
      border-radius: 0.56vw;
      left: -0.83vw;
      bottom: calc(100% + 1.39vw);
      box-shadow: 0.14vw 0.14vw 0.56vw rgba(23, 24, 24, .051), 0.14vw 0.14vw 0.28vw rgba(0, 0, 0, .149);
      width: 20.83vw;
      min-height: 18.06vw;
    }

    .country-area-list .search-swrapper {
      padding: 0.56vw;
    }

    .country-area-list .search-swrapper input {
      padding: 0.56vw 0.28vw;
      border-radius: 0.28vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .no-data {
      line-height: 4.17vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .country-item {
      padding: 0.56vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .country-item.active:before {
      width: 0.28vw;
    }

    .phone-box .box-inp {
      margin-left: 1.11vw;
    }
  }

  @media screen and (max-width:1023.98px) {
    .phone-box .selected-flag {
      width: 5.33vw;
    }

    .phone-box .selected-item {
      min-width: 5.33vw;
    }

    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: max(12px, 3.73vw);
    }

    .phone-box .select-arrow {
      margin-right: 1.33vw;
      font-size: max(12px, 3.73vw);
    }

    .country-area-overlay{
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 10000;
      background: rgba(0, 0, 0, .4);
    }

    .phone-box .country-area-list {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10001;
      width: 100%;
      height: 40vh;
      box-shadow: 0.27vw 0.27vw 1.07vw rgba(23, 24, 24, .051), 0.27vw 0.27vw 0.53vw rgba(0, 0, 0, .149);
      min-height: 34.67vw;
    }

    .country-area-list .search-swrapper {
      padding: 1.6vw;
    }

    .country-area-list .search-swrapper input {
      padding: 1.07vw 0.53vw;
      font-size: max(12px, 2.67vw);
      border-radius: 0.53vw;
    }

    .country-area-list .no-data {
      font-size: max(12px, 2.67vw);
      line-height: 13.33vw;
    }

    .country-area-list .country-item {
      padding: 1.07vw 1.6vw;
      font-size: max(12px, 2.67vw);
    }

    .country-area-list .country-item.active:before {
      width: 0.8vw;
    }

    .phone-box .box-inp {
      margin-left: 2.13vw;
    }
  }
`, zr = function(t) {
  return u`
      <div class="lf-info">
        <div class="info-box pc-show">
          <div class="title" .innerHTML="${t.activityInfo.mainTitle}"></div>
          <div class="subtitle text-overflow-1" .innerHTML="${t.activityInfo.subTitle}"></div>
          <div class="tips text-overflow-1" .innerHTML="${t.activityInfo.guideText}"></div>
        </div>
        <div class="form">
          <div class="item-box email-box">
            <label>
              <span class="status-required">*</span>
              <input class="box-inp" data-flag="email" type="text" placeholder="${window.i18n.t("form.placeholder.email")}">
              <jds-icon class="status-check" name="check-fill"></jds-icon>
            </label>
            <div class="error-tips email-error-tips hidden">${window.i18n.t("form.validation.email")}</div>
          </div>
          ${t.activityInfo.showPhoneInput ? u`
              <div class="item-box phone-box">
                <div class="country-area-select">
                  <div class="change-area">
                    <!-- 国家或区域旗帜 -->
                    <img class="selected-flag" src="" alt="" loading="lazy">

                    <!-- 国家或区域编码 -->
                    <span class="selected-item"></span>
                    <jds-icon class="select-arrow" name="arrow-down"></jds-icon>
                  </div>
                  <!-- 国家或区域列表 -->
                  <div class="country-area-overlay hidden"></div>
                  <div class="country-area-list hidden">
                    <div class="search-swrapper">
                      <input
                          type="text"
                          class="country-area-search"
                          placeholder="${window.i18n.t("form.placeholder.enterKeyword")}"
                      >
                    </div>
                    <div class="no-data">${window.i18n.t("form.validation.enterKeyword")}</div>
                    <div class="list-box"></div>
                  </div>
                  <span class="country-area-num"></span>
                  <input class="box-inp" data-flag="phone" type="number" placeholder="${window.i18n.t("form.placeholder.phone")}">
                  <jds-icon class="status-check" name="check-fill"></jds-icon>
                </div>
                <div class="error-tips phone-error-tips hidden">${window.i18n.t("form.validation.phone")}</div>
              </div>
            ` : ""}

          <div class="item-box checkbox-box">
            <label class="checkbox">
              <span class="checkbox-input">
                <input
                    type="checkbox"
                    class="checkbox-original"
                    data-section-class="subscribe-content"
                >
                <span class="checkbox-inner">
                  <jds-icon class="icon-check" name="check"></jds-icon>
                </span>
              </span>
              <div class="checkbox-label" .innerHTML="${t.activityInfo.privacyAgreementText}"></div>
            </label>
            <div class="error-tips checkbox-error-tips hidden">${window.i18n.t("form.validation.termsOfService")}</div>
          </div>
          <jds-button
            class="btn-subscribe"
            size="medium"
            .innerHTML="${t.activityInfo.btnTitle}"
            style="--btn-text-color: ${t.activityInfo.btnColor}; --btn-bg-color: ${t.activityInfo.btnBgValue};"
          ></jds-button>
        </div>
        ${t.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${t.showRulesDialog}">
                <span .innerHTML="${t.activityInfo.ruleTitle}"></span>
                <jds-icon name="arrow-right"></jds-icon>
              </div>
          ` : ""}
      </div>
    `;
}, ie = [
  {
    country: "Afghanistan",
    code: "93",
    iso: "AF"
  },
  {
    country: "Albania",
    code: "355",
    iso: "AL"
  },
  {
    country: "Algeria",
    code: "213",
    iso: "DZ"
  },
  {
    country: "American Samoa",
    code: "1-684",
    iso: "AS"
  },
  {
    country: "Andorra",
    code: "376",
    iso: "AD"
  },
  {
    country: "Angola",
    code: "244",
    iso: "AO"
  },
  {
    country: "Anguilla",
    code: "1-264",
    iso: "AI"
  },
  {
    country: "Antarctica",
    code: "672",
    iso: "AQ"
  },
  {
    country: "Antigua and Barbuda",
    code: "1-268",
    iso: "AG"
  },
  {
    country: "Argentina",
    code: "54",
    iso: "AR"
  },
  {
    country: "Armenia",
    code: "374",
    iso: "AM"
  },
  {
    country: "Aruba",
    code: "297",
    iso: "AW"
  },
  {
    country: "Australia",
    code: "61",
    iso: "AU"
  },
  {
    country: "Austria",
    code: "43",
    iso: "AT"
  },
  {
    country: "Azerbaijan",
    code: "994",
    iso: "AZ"
  },
  {
    country: "Bahamas",
    code: "1-242",
    iso: "BS"
  },
  {
    country: "Bahrain",
    code: "973",
    iso: "BH"
  },
  {
    country: "Bangladesh",
    code: "880",
    iso: "BD"
  },
  {
    country: "Barbados",
    code: "1-246",
    iso: "BB"
  },
  {
    country: "Belarus",
    code: "375",
    iso: "BY"
  },
  {
    country: "Belgium",
    code: "32",
    iso: "BE"
  },
  {
    country: "Belize",
    code: "501",
    iso: "BZ"
  },
  {
    country: "Benin",
    code: "229",
    iso: "BJ"
  },
  {
    country: "Bermuda",
    code: "1-441",
    iso: "BM"
  },
  {
    country: "Bhutan",
    code: "975",
    iso: "BT"
  },
  {
    country: "Bolivia",
    code: "591",
    iso: "BO"
  },
  {
    country: "Bosnia and Herzegovina",
    code: "387",
    iso: "BA"
  },
  {
    country: "Botswana",
    code: "267",
    iso: "BW"
  },
  {
    country: "Brazil",
    code: "55",
    iso: "BR"
  },
  {
    country: "British Indian Ocean Territory",
    code: "246",
    iso: "IO"
  },
  {
    country: "British Virgin Islands",
    code: "1-284",
    iso: "VG"
  },
  {
    country: "Brunei",
    code: "673",
    iso: "BN"
  },
  {
    country: "Bulgaria",
    code: "359",
    iso: "BG"
  },
  {
    country: "Burkina Faso",
    code: "226",
    iso: "BF"
  },
  {
    country: "Burundi",
    code: "257",
    iso: "BI"
  },
  {
    country: "Cambodia",
    code: "855",
    iso: "KH"
  },
  {
    country: "Cameroon",
    code: "237",
    iso: "CM"
  },
  {
    country: "Canada",
    code: "1",
    iso: "CA"
  },
  {
    country: "Cape Verde",
    code: "238",
    iso: "CV"
  },
  {
    country: "Cayman Islands",
    code: "1-345",
    iso: "KY"
  },
  {
    country: "Central African Republic",
    code: "236",
    iso: "CF"
  },
  {
    country: "Chad",
    code: "235",
    iso: "TD"
  },
  {
    country: "Chile",
    code: "56",
    iso: "CL"
  },
  {
    country: "China",
    code: "86",
    iso: "CN"
  },
  {
    country: "Christmas Island",
    code: "61",
    iso: "CX"
  },
  {
    country: "Cocos Islands",
    code: "61",
    iso: "CC"
  },
  {
    country: "Colombia",
    code: "57",
    iso: "CO"
  },
  {
    country: "Comoros",
    code: "269",
    iso: "KM"
  },
  {
    country: "Cook Islands",
    code: "682",
    iso: "CK"
  },
  {
    country: "Costa Rica",
    code: "506",
    iso: "CR"
  },
  {
    country: "Croatia",
    code: "385",
    iso: "HR"
  },
  {
    country: "Cuba",
    code: "53",
    iso: "CU"
  },
  {
    country: "Curacao",
    code: "599",
    iso: "CW"
  },
  {
    country: "Cyprus",
    code: "357",
    iso: "CY"
  },
  {
    country: "Czech Republic",
    code: "420",
    iso: "CZ"
  },
  {
    country: "Democratic Republic of the Congo",
    code: "243",
    iso: "CD"
  },
  {
    country: "Denmark",
    code: "45",
    iso: "DK"
  },
  {
    country: "Djibouti",
    code: "253",
    iso: "DJ"
  },
  {
    country: "Dominica",
    code: "1-767",
    iso: "DM"
  },
  {
    country: "Dominican Republic",
    code: "1-809, 1-829, 1-849",
    iso: "DO"
  },
  {
    country: "East Timor",
    code: "670",
    iso: "TL"
  },
  {
    country: "Ecuador",
    code: "593",
    iso: "EC"
  },
  {
    country: "Egypt",
    code: "20",
    iso: "EG"
  },
  {
    country: "El Salvador",
    code: "503",
    iso: "SV"
  },
  {
    country: "Equatorial Guinea",
    code: "240",
    iso: "GQ"
  },
  {
    country: "Eritrea",
    code: "291",
    iso: "ER"
  },
  {
    country: "Estonia",
    code: "372",
    iso: "EE"
  },
  {
    country: "Ethiopia",
    code: "251",
    iso: "ET"
  },
  {
    country: "Falkland Islands",
    code: "500",
    iso: "FK"
  },
  {
    country: "Faroe Islands",
    code: "298",
    iso: "FO"
  },
  {
    country: "Fiji",
    code: "679",
    iso: "FJ"
  },
  {
    country: "Finland",
    code: "358",
    iso: "FI"
  },
  {
    country: "France",
    code: "33",
    iso: "FR"
  },
  {
    country: "French Polynesia",
    code: "689",
    iso: "PF"
  },
  {
    country: "Gabon",
    code: "241",
    iso: "GA"
  },
  {
    country: "Gambia",
    code: "220",
    iso: "GM"
  },
  {
    country: "Georgia",
    code: "995",
    iso: "GE"
  },
  {
    country: "Germany",
    code: "49",
    iso: "DE"
  },
  {
    country: "Ghana",
    code: "233",
    iso: "GH"
  },
  {
    country: "Gibraltar",
    code: "350",
    iso: "GI"
  },
  {
    country: "Greece",
    code: "30",
    iso: "GR"
  },
  {
    country: "Greenland",
    code: "299",
    iso: "GL"
  },
  {
    country: "Grenada",
    code: "1-473",
    iso: "GD"
  },
  {
    country: "Guam",
    code: "1-671",
    iso: "GU"
  },
  {
    country: "Guatemala",
    code: "502",
    iso: "GT"
  },
  {
    country: "Guernsey",
    code: "44-1481",
    iso: "GG"
  },
  {
    country: "Guinea",
    code: "224",
    iso: "GN"
  },
  {
    country: "Guinea-Bissau",
    code: "245",
    iso: "GW"
  },
  {
    country: "Guyana",
    code: "592",
    iso: "GY"
  },
  {
    country: "Haiti",
    code: "509",
    iso: "HT"
  },
  {
    country: "Honduras",
    code: "504",
    iso: "HN"
  },
  {
    country: "Hong Kong",
    code: "852",
    iso: "HK"
  },
  {
    country: "Hungary",
    code: "36",
    iso: "HU"
  },
  {
    country: "Iceland",
    code: "354",
    iso: "IS"
  },
  {
    country: "India",
    code: "91",
    iso: "IN"
  },
  {
    country: "Indonesia",
    code: "62",
    iso: "ID"
  },
  {
    country: "Iran",
    code: "98",
    iso: "IR"
  },
  {
    country: "Iraq",
    code: "964",
    iso: "IQ"
  },
  {
    country: "Ireland",
    code: "353",
    iso: "IE"
  },
  {
    country: "Isle of Man",
    code: "44-1624",
    iso: "IM"
  },
  {
    country: "Israel",
    code: "972",
    iso: "IL"
  },
  {
    country: "Italy",
    code: "39",
    iso: "IT"
  },
  {
    country: "Ivory Coast",
    code: "225",
    iso: "CI"
  },
  {
    country: "Jamaica",
    code: "1-876",
    iso: "JM"
  },
  {
    country: "Japan",
    code: "81",
    iso: "JP"
  },
  {
    country: "Jersey",
    code: "44-1534",
    iso: "JE"
  },
  {
    country: "Jordan",
    code: "962",
    iso: "JO"
  },
  {
    country: "Kazakhstan",
    code: "7",
    iso: "KZ"
  },
  {
    country: "Kenya",
    code: "254",
    iso: "KE"
  },
  {
    country: "Kiribati",
    code: "686",
    iso: "KI"
  },
  {
    country: "Kosovo",
    code: "383",
    iso: "XK"
  },
  {
    country: "Kuwait",
    code: "965",
    iso: "KW"
  },
  {
    country: "Kyrgyzstan",
    code: "996",
    iso: "KG"
  },
  {
    country: "Laos",
    code: "856",
    iso: "LA"
  },
  {
    country: "Latvia",
    code: "371",
    iso: "LV"
  },
  {
    country: "Lebanon",
    code: "961",
    iso: "LB"
  },
  {
    country: "Lesotho",
    code: "266",
    iso: "LS"
  },
  {
    country: "Liberia",
    code: "231",
    iso: "LR"
  },
  {
    country: "Libya",
    code: "218",
    iso: "LY"
  },
  {
    country: "Liechtenstein",
    code: "423",
    iso: "LI"
  },
  {
    country: "Lithuania",
    code: "370",
    iso: "LT"
  },
  {
    country: "Luxembourg",
    code: "352",
    iso: "LU"
  },
  {
    country: "Macao",
    code: "853",
    iso: "MO"
  },
  {
    country: "Macedonia",
    code: "389",
    iso: "MK"
  },
  {
    country: "Madagascar",
    code: "261",
    iso: "MG"
  },
  {
    country: "Malawi",
    code: "265",
    iso: "MW"
  },
  {
    country: "Malaysia",
    code: "60",
    iso: "MY"
  },
  {
    country: "Maldives",
    code: "960",
    iso: "MV"
  },
  {
    country: "Mali",
    code: "223",
    iso: "ML"
  },
  {
    country: "Malta",
    code: "356",
    iso: "MT"
  },
  {
    country: "Marshall Islands",
    code: "692",
    iso: "MH"
  },
  {
    country: "Mauritania",
    code: "222",
    iso: "MR"
  },
  {
    country: "Mauritius",
    code: "230",
    iso: "MU"
  },
  {
    country: "Mayotte",
    code: "262",
    iso: "YT"
  },
  {
    country: "Mexico",
    code: "52",
    iso: "MX"
  },
  {
    country: "Micronesia",
    code: "691",
    iso: "FM"
  },
  {
    country: "Moldova",
    code: "373",
    iso: "MD"
  },
  {
    country: "Monaco",
    code: "377",
    iso: "MC"
  },
  {
    country: "Mongolia",
    code: "976",
    iso: "MN"
  },
  {
    country: "Montenegro",
    code: "382",
    iso: "ME"
  },
  {
    country: "Montserrat",
    code: "1-664",
    iso: "MS"
  },
  {
    country: "Morocco",
    code: "212",
    iso: "MA"
  },
  {
    country: "Mozambique",
    code: "258",
    iso: "MZ"
  },
  {
    country: "Myanmar",
    code: "95",
    iso: "MM"
  },
  {
    country: "Namibia",
    code: "264",
    iso: "NA"
  },
  {
    country: "Nauru",
    code: "674",
    iso: "NR"
  },
  {
    country: "Nepal",
    code: "977",
    iso: "NP"
  },
  {
    country: "Netherlands",
    code: "31",
    iso: "NL"
  },
  {
    country: "Netherlands Antilles",
    code: "599",
    iso: "AN"
  },
  {
    country: "New Caledonia",
    code: "687",
    iso: "NC"
  },
  {
    country: "New Zealand",
    code: "64",
    iso: "NZ"
  },
  {
    country: "Nicaragua",
    code: "505",
    iso: "NI"
  },
  {
    country: "Niger",
    code: "227",
    iso: "NE"
  },
  {
    country: "Nigeria",
    code: "234",
    iso: "NG"
  },
  {
    country: "Niue",
    code: "683",
    iso: "NU"
  },
  {
    country: "North Korea",
    code: "850",
    iso: "KP"
  },
  {
    country: "Northern Mariana Islands",
    code: "1-670",
    iso: "MP"
  },
  {
    country: "Norway",
    code: "47",
    iso: "NO"
  },
  {
    country: "Oman",
    code: "968",
    iso: "OM"
  },
  {
    country: "Pakistan",
    code: "92",
    iso: "PK"
  },
  {
    country: "Palau",
    code: "680",
    iso: "PW"
  },
  {
    country: "Palestine",
    code: "970",
    iso: "PS"
  },
  {
    country: "Panama",
    code: "507",
    iso: "PA"
  },
  {
    country: "Papua New Guinea",
    code: "675",
    iso: "PG"
  },
  {
    country: "Paraguay",
    code: "595",
    iso: "PY"
  },
  {
    country: "Peru",
    code: "51",
    iso: "PE"
  },
  {
    country: "Philippines",
    code: "63",
    iso: "PH"
  },
  {
    country: "Pitcairn",
    code: "64",
    iso: "PN"
  },
  {
    country: "Poland",
    code: "48",
    iso: "PL"
  },
  {
    country: "Portugal",
    code: "351",
    iso: "PT"
  },
  {
    country: "Puerto Rico",
    code: "1-787, 1-939",
    iso: "PR"
  },
  {
    country: "Qatar",
    code: "974",
    iso: "QA"
  },
  {
    country: "Republic of the Congo",
    code: "242",
    iso: "CG"
  },
  {
    country: "Reunion",
    code: "262",
    iso: "RE"
  },
  {
    country: "Romania",
    code: "40",
    iso: "RO"
  },
  {
    country: "Russia",
    code: "7",
    iso: "RU"
  },
  {
    country: "Rwanda",
    code: "250",
    iso: "RW"
  },
  {
    country: "Saint Barthelemy",
    code: "590",
    iso: "BL"
  },
  {
    country: "Saint Helena",
    code: "290",
    iso: "SH"
  },
  {
    country: "Saint Kitts and Nevis",
    code: "1-869",
    iso: "KN"
  },
  {
    country: "Saint Lucia",
    code: "1-758",
    iso: "LC"
  },
  {
    country: "Saint Martin",
    code: "590",
    iso: "MF"
  },
  {
    country: "Saint Pierre and Miquelon",
    code: "508",
    iso: "PM"
  },
  {
    country: "Saint Vincent and the Grenadines",
    code: "1-784",
    iso: "VC"
  },
  {
    country: "Samoa",
    code: "685",
    iso: "WS"
  },
  {
    country: "San Marino",
    code: "378",
    iso: "SM"
  },
  {
    country: "Sao Tome and Principe",
    code: "239",
    iso: "ST"
  },
  {
    country: "Saudi Arabia",
    code: "966",
    iso: "SA"
  },
  {
    country: "Senegal",
    code: "221",
    iso: "SN"
  },
  {
    country: "Serbia",
    code: "381",
    iso: "RS"
  },
  {
    country: "Seychelles",
    code: "248",
    iso: "SC"
  },
  {
    country: "Sierra Leone",
    code: "232",
    iso: "SL"
  },
  {
    country: "Singapore",
    code: "65",
    iso: "SG"
  },
  {
    country: "Sint Maarten",
    code: "1-721",
    iso: "SX"
  },
  {
    country: "Slovakia",
    code: "421",
    iso: "SK"
  },
  {
    country: "Slovenia",
    code: "386",
    iso: "SI"
  },
  {
    country: "Solomon Islands",
    code: "677",
    iso: "SB"
  },
  {
    country: "Somalia",
    code: "252",
    iso: "SO"
  },
  {
    country: "South Africa",
    code: "27",
    iso: "ZA"
  },
  {
    country: "South Korea",
    code: "82",
    iso: "KR"
  },
  {
    country: "South Sudan",
    code: "211",
    iso: "SS"
  },
  {
    country: "Spain",
    code: "34",
    iso: "ES"
  },
  {
    country: "Sri Lanka",
    code: "94",
    iso: "LK"
  },
  {
    country: "Sudan",
    code: "249",
    iso: "SD"
  },
  {
    country: "Suriname",
    code: "597",
    iso: "SR"
  },
  {
    country: "Svalbard and Jan Mayen",
    code: "47",
    iso: "SJ"
  },
  {
    country: "Swaziland",
    code: "268",
    iso: "SZ"
  },
  {
    country: "Sweden",
    code: "46",
    iso: "SE"
  },
  {
    country: "Switzerland",
    code: "41",
    iso: "CH"
  },
  {
    country: "Syria",
    code: "963",
    iso: "SY"
  },
  {
    country: "Taiwan",
    code: "886",
    iso: "TW"
  },
  {
    country: "Tajikistan",
    code: "992",
    iso: "TJ"
  },
  {
    country: "Tanzania",
    code: "255",
    iso: "TZ"
  },
  {
    country: "Thailand",
    code: "66",
    iso: "TH"
  },
  {
    country: "Togo",
    code: "228",
    iso: "TG"
  },
  {
    country: "Tokelau",
    code: "690",
    iso: "TK"
  },
  {
    country: "Tonga",
    code: "676",
    iso: "TO"
  },
  {
    country: "Trinidad and Tobago",
    code: "1-868",
    iso: "TT"
  },
  {
    country: "Tunisia",
    code: "216",
    iso: "TN"
  },
  {
    country: "Turkey",
    code: "90",
    iso: "TR"
  },
  {
    country: "Turkmenistan",
    code: "993",
    iso: "TM"
  },
  {
    country: "Turks and Caicos Islands",
    code: "1-649",
    iso: "TC"
  },
  {
    country: "Tuvalu",
    code: "688",
    iso: "TV"
  },
  {
    country: "U.S. Virgin Islands",
    code: "1-340",
    iso: "VI"
  },
  {
    country: "Uganda",
    code: "256",
    iso: "UG"
  },
  {
    country: "Ukraine",
    code: "380",
    iso: "UA"
  },
  {
    country: "United Arab Emirates",
    code: "971",
    iso: "AE"
  },
  {
    country: "United Kingdom",
    code: "44",
    iso: "GB"
  },
  {
    country: "United States",
    code: "1",
    iso: "US"
  },
  {
    country: "Uruguay",
    code: "598",
    iso: "UY"
  },
  {
    country: "Uzbekistan",
    code: "998",
    iso: "UZ"
  },
  {
    country: "Vanuatu",
    code: "678",
    iso: "VU"
  },
  {
    country: "Vatican",
    code: "379",
    iso: "VA"
  },
  {
    country: "Venezuela",
    code: "58",
    iso: "VE"
  },
  {
    country: "Vietnam",
    code: "84",
    iso: "VN"
  },
  {
    country: "Wallis and Futuna",
    code: "681",
    iso: "WF"
  },
  {
    country: "Western Sahara",
    code: "212",
    iso: "EH"
  },
  {
    country: "Yemen",
    code: "967",
    iso: "YE"
  },
  {
    country: "Zambia",
    code: "260",
    iso: "ZM"
  },
  {
    country: "Zimbabwe",
    code: "263",
    iso: "ZW"
  }
];
function St(t) {
  return Xi("Section_Scratch_Card_user_clicks", t);
}
var Tr = Object.defineProperty, Er = Object.getOwnPropertyDescriptor, Tt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Er(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Tr(e, i, s), s;
};
let rt = class extends st {
  constructor() {
    super(...arguments), this.hightLightUnlockTips = !1, this.currEmail = "", this.currPhone = "", this.selectedPhoneCountry = "US", this.selectedAreaCode = "1", this.customTag = "";
  }
  /**
   * 组件首次更新完成后的回调
   */
  firstUpdated() {
    this.initDefaultCountry(), this.renderCountryList(ie), this.setupEventListeners(), this.setupSubscribeButton(), this.setupCheckboxListener(), this.setupInputFocusEvents();
  }
  /**
   * @method updated
   * @param {Map<string, any>} changedProperties - 变更的属性集合
   * @description 组件更新后的生命周期钩子，监听state变化以初始化Canvas
   */
  updated(t) {
    var e;
    if (t.has("hightLightUnlockTips") && this.hightLightUnlockTips === !0) {
      const i = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".tips");
      i && (i.classList.add("active"), i.style.color = this.activityInfo.highlightErrTips);
    }
  }
  /**
   * 初始化默认国家/地区设置
   */
  initDefaultCountry() {
    try {
      const t = ie.find((i) => {
        var o;
        return i.iso === (((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.cr) || "US");
      });
      if (!t) return;
      const e = {
        iso: t.iso || "US",
        flag: this.getCountryFlagUrl(t.iso),
        code: t.code || "1"
      };
      this.selectedPhoneCountry = e.iso, this.selectedAreaCode = e.code, this.updateSelectedCountry(e);
    } catch (t) {
      console.error("初始化默认国家/地区失败:", t);
    }
  }
  /**
   * 渲染国家/地区列表
   * @param listData 国家/地区数据列表
   */
  renderCountryList(t) {
    var e, i;
    try {
      const o = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".list-box"), s = (i = this.shadowRoot) == null ? void 0 : i.querySelector(".no-data");
      if (!(t != null && t.length)) {
        s == null || s.classList.remove("hidden"), o == null || o.classList.add("hidden");
        return;
      }
      s == null || s.classList.add("hidden"), o == null || o.classList.remove("hidden");
      const r = t.map((n) => this.renderCountryItem(n)).join("");
      o && (o.innerHTML = r);
    } catch (o) {
      console.error("渲染国家/地区列表失败:", o);
    }
  }
  /**
   * 渲染单个国家/地区项
   * @param country 国家/地区数据
   */
  renderCountryItem(t) {
    return `
      <div class="country-item ${this.selectedPhoneCountry === t.iso ? "active" : ""}"
           data-code="${t.code}"
           data-iso="${t.iso}">
        ${t.country}
      </div>
    `;
  }
  /**
   * 更新选中的国家/地区显示
   * @param options 国家/地区选项
   */
  updateSelectedCountry(t) {
    var e, i, o;
    try {
      const s = {
        flagImg: (e = this.shadowRoot) == null ? void 0 : e.querySelector(".selected-flag"),
        isoSpan: (i = this.shadowRoot) == null ? void 0 : i.querySelector(".selected-item"),
        codeSpan: (o = this.shadowRoot) == null ? void 0 : o.querySelector(".country-area-num")
      };
      s.flagImg && (s.flagImg.src = t.flag), s.isoSpan && (s.isoSpan.textContent = t.iso), s.codeSpan && (s.codeSpan.textContent = `+${t.code}`);
    } catch (s) {
      console.error("更新选中的国家/地区显示失败:", s);
    }
  }
  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    try {
      this.setupCountryListToggle(), this.setupCountrySelection();
    } catch (t) {
      console.error("设置事件监听器失败:", t);
    }
  }
  /**
   * 设置国家/地区列表切换事件
   */
  setupCountryListToggle() {
    var t, e;
    try {
      (e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".change-area")) == null || e.addEventListener("click", (i) => {
        var r, n;
        const o = i.currentTarget, s = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".country-area-list");
        if (o == null || o.classList.toggle("active"), s == null || s.classList.toggle("hidden"), window.innerWidth < 1024) {
          const a = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".country-area-overlay");
          a == null || a.classList.toggle("hidden");
        }
        this.renderCountryList(ie);
      });
    } catch (i) {
      console.error("设置国家/地区列表切换事件失败:", i);
    }
  }
  /**
   * 设置国家/地区选择事件
   */
  setupCountrySelection() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".list-box");
      e == null || e.addEventListener("click", (i) => {
        var n, a, l, d, h, p;
        const o = i.target;
        if (!o.classList.contains("country-item")) return;
        e.querySelectorAll(".country-item").forEach((w) => {
          w.classList.remove("active");
        }), this.selectedPhoneCountry = o.dataset.iso || "US", this.selectedAreaCode = o.dataset.code || "1", o.classList.add("active");
        const s = {
          iso: this.selectedPhoneCountry,
          flag: this.getCountryFlagUrl(this.selectedPhoneCountry),
          code: this.selectedAreaCode
        };
        (a = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".country-area-list")) == null || a.classList.add("hidden");
        const r = (l = this.shadowRoot) == null ? void 0 : l.querySelector(".country-area-search");
        if (r && (r.value = ""), (h = (d = this.shadowRoot) == null ? void 0 : d.querySelector(".change-area")) == null || h.classList.remove("active"), window.innerWidth < 1024) {
          const w = (p = this.shadowRoot) == null ? void 0 : p.querySelector(".country-area-overlay");
          w == null || w.classList.add("hidden");
        }
        this.updateSelectedCountry(s);
      }), this.setupCountrySearch(), this.setupClickOutside();
    } catch (e) {
      console.error("设置国家/地区选择事件失败:", e);
    }
  }
  /**
   * 设置国家/地区搜索事件
   */
  setupCountrySearch() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".country-area-search");
      if (!e) return;
      e.addEventListener("input", () => {
        const i = e.value.trim().toLowerCase(), o = this.searchCountries(i);
        this.renderCountryList(o);
      });
    } catch (e) {
      console.error("设置国家/地区搜索事件失败:", e);
    }
  }
  /**
   * 设置点击外部区域事件
   */
  setupClickOutside() {
    try {
      document.addEventListener("click", (t) => {
        var n, a;
        const e = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".change-area"), i = (a = this.shadowRoot) == null ? void 0 : a.querySelector(".country-area-list"), o = t.composedPath().find(
          (l) => {
            var d;
            return l instanceof HTMLElement && ((d = this.shadowRoot) == null ? void 0 : d.contains(l));
          }
        ), s = !(e != null && e.contains(o)) && e !== o, r = !(i != null && i.contains(o)) && i !== o;
        s && r && this.hideCountryList();
      });
    } catch (t) {
      console.error("设置点击外部区域事件失败:", t);
    }
  }
  /**
   * 获取国家/地区旗帜URL
   * @param iso 国家/地区代码
   */
  getCountryFlagUrl(t) {
    return t ? `https://cdn.shopify.com/static/images/flags/${t.toLowerCase()}.svg` : "";
  }
  /**
   * 搜索国家/地区
   * @param keyword 搜索关键词
   */
  searchCountries(t) {
    return ie.filter(
      (e) => e.country.toLowerCase().includes(t) || e.iso.toLowerCase().includes(t)
    );
  }
  /**
   * 隐藏国家/地区列表
   */
  hideCountryList() {
    var o, s, r, n;
    const t = (o = this.shadowRoot) == null ? void 0 : o.querySelector(".country-area-list"), e = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".country-area-search"), i = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".change-area");
    if (t == null || t.classList.add("hidden"), e && (e.value = ""), i == null || i.classList.remove("active"), window.innerWidth < 1024) {
      const a = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".country-area-overlay");
      a == null || a.classList.add("hidden");
    }
  }
  /**
   * 显示规则对话框
   */
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog", {
      detail: {
        showRulesDialog: !0
      }
    })), St({
      module: "Subscribe",
      // 点击模块固定名称Subscribe
      button_name: Nt(this.activityInfo.ruleTitle)
      // 点击规则按钮，识别当地文本text
    });
  }
  /**
   * 错误提示控制
   * @param errorType 错误类型：email/phone/checkbox
   * @param toggleType 显示/隐藏类型：show/hide
   */
  errorTipsControl(t, e) {
    var i;
    try {
      const o = (i = this.shadowRoot) == null ? void 0 : i.querySelector(`.${t}-error-tips`);
      o && (e === "show" ? o.classList.remove("hidden") : o.classList.add("hidden"));
    } catch (o) {
      console.error("错误提示控制失败:", o);
    }
  }
  /**
   * 校验表单
   * @returns 表单校验状态 true/false
   */
  checkForm() {
    var t, e, i, o, s, r;
    try {
      const n = {
        emailInput: (t = this.shadowRoot) == null ? void 0 : t.querySelector(".email-box .box-inp"),
        phoneInput: (e = this.shadowRoot) == null ? void 0 : e.querySelector(".phone-box .box-inp"),
        checkbox: (i = this.shadowRoot) == null ? void 0 : i.querySelector(".checkbox-box .checkbox-original")
      };
      return (o = n.checkbox) != null && o.checked ? (this.errorTipsControl("checkbox", "hide"), this.currEmail = ((s = n.emailInput) == null ? void 0 : s.value) || "", this.currEmail ? Qt(this.currEmail) ? (this.errorTipsControl("email", "hide"), this.currPhone = ((r = n.phoneInput) == null ? void 0 : r.value) || "", !0) : (this.errorTipsControl("email", "show"), !1) : (this.errorTipsControl("email", "show"), !1)) : (this.errorTipsControl("checkbox", "show"), !1);
    } catch {
      return !1;
    }
  }
  /**
   * 订阅功能
   */
  async subscribe() {
    var t, e, i, o;
    try {
      if (!this.checkForm())
        return;
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.currEmail,
        tags: this.customTag,
        componentName: "刮刮乐订阅",
        pageUrl: window.location.href
      };
      this.currPhone && (s.phone_cr = this.selectedPhoneCountry, s.phone = this.selectedAreaCode + this.currPhone);
      const n = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await j.post(n, s, {
        withCredentials: !0,
        // 允许携带 Cookies
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          Cr: ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || "US",
          Lang: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang) || "en"
        }
      }), { code: l, success: d, message: h } = a.data;
      d ? l === 200 && this.handleSubscribeSuccess(s) : $(h, "danger");
    } catch {
      $(window.i18n.t("pages.error.default"), "danger");
    }
  }
  /**
   * 处理订阅成功
   */
  handleSubscribeSuccess(t) {
    var e, i, o;
    try {
      if (this.errorTipsControl("email", "hide"), t.email) {
        const r = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".email-box");
        r && r.classList.add("sub-success");
      }
      if (t.phone) {
        const r = (i = this.shadowRoot) == null ? void 0 : i.querySelector(".phone-box");
        r && r.classList.add("sub-success");
      }
      const s = (o = this.shadowRoot) == null ? void 0 : o.querySelector(".btn-subscribe");
      s && s.classList.add("disabled"), sessionStorage.setItem("plus-event-email-subscribe", this.currEmail), this.currPhone && sessionStorage.setItem("plus-event-phone-subscribe", this.selectedAreaCode + this.currPhone), this.dispatchEvent(new CustomEvent("subscribe-success", {
        detail: {
          email: this.currEmail,
          drawState: "initial"
        }
      })), St({
        module: "Subscribe",
        //点击模块固定名称Subscribe   
        position: t.phone ? "phone" : "",
        // 记录是否填写电话，若填写则为phone，若无则为Null
        button_name: Nt(this.activityInfo.btnTitle)
        // 点击按钮，识别按钮文本text；
      });
    } catch (s) {
      console.error("处理订阅成功失败:", s);
    }
  }
  /**
   * 监听协议勾选状态
   */
  setupCheckboxListener() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".checkbox-original");
      if (!e) return;
      e.addEventListener("change", () => {
        e.checked ? this.errorTipsControl("checkbox", "hide") : this.errorTipsControl("checkbox", "show");
      });
    } catch (e) {
      console.error("设置协议勾选监听失败:", e);
    }
  }
  /**
   * 设置订阅按钮点击事件
   */
  setupSubscribeButton() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".btn-subscribe");
      e == null || e.addEventListener("click", $t(() => this.subscribe(), 300));
    } catch (e) {
      console.error("设置订阅按钮点击事件失败:", e);
    }
  }
  /**
   * 设置输入框聚焦事件
   */
  setupInputFocusEvents() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelectorAll(".box-inp");
      e == null || e.forEach((i) => {
        i.addEventListener("focus", () => {
          var s;
          const o = i.closest(".item-box");
          if (o != null && o.classList.contains("sub-success") && o.classList.remove("sub-success"), i.dataset.flag === "email") {
            const r = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".btn-subscribe");
            r == null || r.classList.remove("disabled");
          }
        });
      });
    } catch (e) {
      console.error("设置输入框聚焦事件失败:", e);
    }
  }
  render() {
    return zr(this);
  }
};
rt.styles = [H(Xt), Qi, kr, $r];
Tt([
  m()
], rt.prototype, "hightLightUnlockTips", 2);
Tt([
  m()
], rt.prototype, "currEmail", 2);
Tt([
  m()
], rt.prototype, "currPhone", 2);
Tt([
  m()
], rt.prototype, "selectedPhoneCountry", 2);
Tt([
  m()
], rt.prototype, "selectedAreaCode", 2);
Tt([
  g({ type: String, reflect: !1 })
], rt.prototype, "customTag", 2);
rt = Tt([
  _("scratch-left-info")
], rt);
const Sr = k`
  .inline-block {
    display: inline-block;
  }
  .right-box {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    background-size: cover;
    background-position: center;
  }

  .btn {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    font-weight: 600;
    color: #872017;
    text-align: center;
    font-style: normal;
    background: linear-gradient(180deg, #FFE68A 0%, #FFF5B7 43%, #FFE18B 100%);
    border-style: solid;
    border-color: transparent;
    cursor: pointer;
  }

  .btn::before,
  .btn::after {
    content: "";
    position: absolute;
    left: 8%;
    right: 8%;
    display:block;
    background: linear-gradient(90deg, transparent 0%, #fff 50%, transparent 100%);
  }

  .btn::before{
    top: 0;
  }

  .btn::after{
    bottom: 0;
  }

  .btn * {
    word-break: break-all;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-play {
    position: absolute;
    z-index: 1;
  }

  .btn-group {
    position: absolute;
    z-index: 1;
    display: flex;
  }

  .btn-group .btn {
    overflow: hidden;
  }

  .initial-state {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .scratch-area {
    position: relative;
    background-color: #fff;
  }

  .initial-state img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  .scratch-canvas {
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    cursor: pointer;
  }

  .prize-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  .prize-image {
    object-fit: contain;
  }

  .prize-name {
    width: 100%;
    font-weight: 500;
    color: #5C5C5C;
    line-height: 1.4;
    text-align: center;
  }

  .draw-tip {
    position: absolute;
    color: #FFEDA6;
    font-weight: 600;
  }

  @media screen and (min-width: 1440px) {
    :host {
      width: 446px;
      height: 400px;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 24px;
    }

    .btn {
      padding: 0 28px;
      height: 40px;
      line-height: 40px;
      font-size: 16px;
    }

    .btn::before,
    .btn::after{
      height: 4px;
    }

    .btn-play {
      bottom: -28px;
    }

    .btn-group {
      bottom: -28px;
      gap: 12px;
    }

    .btn-group .btn{
      width: 180px;
      padding-left: 20px;
      padding-right: 20px;
    }

    .btn-play-again {
      gap: 2px;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 22px;
      width: 392px;
      height: 216px;
    }

    .prize-image {
      margin-bottom: 8px;
      width: 248px;
      height: 166px;
    }

    .prize-name {
      font-size: 16px;
    }

    .draw-tip {
      bottom: -38px;
      font-size: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    :host {
      width: 30.97vw;
      height: 27.78vw;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 1.67vw;
    }

    .btn {
      padding: 0 1.94vw;
      height: 2.78vw;
      line-height: 2.78vw;
      font-size: max(12px, 1.11vw);
    }

    .btn::before,
    .btn::after{
      height: 0.28vw;
    }

    .btn-play {
      bottom: -1.94vw;
    }

    .btn-group {
      bottom: -1.94vw;
      gap: 0.83vw;
    }

    .btn-group .btn{
      width: 12.5vw;
      padding-left: 1.39vw;
      padding-right: 1.39vw;
    }

    .btn-play-again {
      gap: 0.14vw;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 1.53vw;
      width: 27.22vw;
      height: 15vw;
    }

    .prize-image {
      margin-bottom: 0.56vw;
      width: 17.22vw;
      height: 11.53vw;
    }

    .prize-name {
      font-size: max(12px, 1.11vw);
    }

    .draw-tip {
      bottom: -2.64vw;
      font-size: max(12px, 1.11vw);
    }
  }

  @media screen and (min-width: 1024px) {
    .tips{
      display: none;
    }
  }

  @media screen and (max-width: 1023.98px) {
    :host {
      order: 1;
      width: 74.4vw;
      min-height: 53.33vw;
    }

    .opacity-hide{
      opacity: 0;
    }

    .right-info{
      text-align: center;
    }

    .tips{
      display: -webkit-box;
    }

    .right-box{
      margin-top: 8.53vw;
      margin-bottom: 6.4vw;
      height: 66.67vw;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 3.2vw;
    }

    .btn {
      height: 10.67vw;
      line-height: 10.67vw;
      padding: 0 5.33vw;
      font-size: max(12px, 3.73vw);
      font-weight: 500;
      border-radius: 5.87vw;
    }

    .btn::before,
    .btn::after {
        height: 0.93vw;
    }

    .btn-play {
      bottom: -15vw;
    }

    .btn-group {
      bottom: -15vw;
      gap: 1.6vw;
    }

    .btn-group .btn{
      width: 35.6vw;
      padding-left: 2.67vw;
      padding-right: 2.67vw;
    }

    .btn-play-again {
      gap: 0.53vw;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 3.2vw;
      width: 68vw;
      height: 36vw;
    }

    .prize-image {
      margin-bottom: 0.8vw;
      width: 43.07vw;
      height: 28.8vw;
    }

    .prize-name {
      font-size: max(12px, 3.2vw);
    }

    .draw-tip {
      bottom: -11.73vw;
      font-size: max(12px, 3.73vw);
      height: 4.26vw;
    }
  }
`, Ir = (t) => u`
    <div class="initial-state"
      @touchstart="${$t(() => t.handleUnlockClick(), 300)}"
      @mousedown="${$t(() => t.handleUnlockClick(), 300)}"
    >
      <img src="${t.scratchOverlayImg}" alt="Locked" />
    </div>
  `, Cr = (t) => u`
    <div class="initial-state">
      <img src="${t.scratchOverlayImg}" alt="Initial" />
    </div>
    <button class="btn btn-play"
     @click="${$t(() => t.startScratch(), 300)}"
     .innerHTML="${t.activityInfo.btnPlayText}"
    ></button>
  `, Dr = (t) => u`
    <div class="scratch-area">
      ${Zi(t)}
      <canvas class="scratch-canvas"></canvas>
    </div>
    <div class="draw-tip text-overflow-1" .innerHTML="${t.activityInfo.drawGuideText}"></div>
  `, Zi = (t) => {
  var e, i, o;
  return u`
    <div class="prize-content">
      <img class="prize-image" src="${(e = t.currentPrize) == null ? void 0 : e.image}" alt="${(i = t.currentPrize) == null ? void 0 : i.name}" />
      <div class="prize-name text-overflow-1">${(o = t.currentPrize) == null ? void 0 : o.name}</div>
    </div>
  `;
}, Rr = (t) => u`
    ${t.showBtnGroup ? u`
      <div class="btn-group">
        <button class="btn"
          @click="${$t(() => t.showPrizeDetails(), 300)}"
          .innerHTML="${t.activityInfo.showPrizeBtnText}"
        ></button>

        ${t.remainingTimes === -1 || t.remainingTimes > 0 ? u`
          <button class="btn btn-play-again" @click="${$t(() => t.playAgain(), 300)}">
            <div class="inline-block" .innerHTML="${t.activityInfo.tryAgainBtnText}"></div>
            ${t.remainingTimes > 0 ? u`(${t.remainingTimes})` : ""}
          </button>
        ` : ""}

      </div>` : ""}
  `, Ar = (t) => u`
    <div class="scratch-area">
      ${Zi(t)}
    </div>
    ${Rr(t)}
  `, _r = (t) => u`
    <div class="right-info info-box mb-show">
      <div class="title" .innerHTML="${t.activityInfo.mainTitle}"></div>
      <div class="subtitle text-overflow-1" .innerHTML="${t.activityInfo.subTitle}"></div>
    </div>
    <div class="right-box" style="background-image: url(${t.scratchRgBoxBg});">
      ${(() => {
  switch (t.drawState) {
    case "locked":
      return Ir(t);
    case "initial":
      return Cr(t);
    case "scratching":
      return Dr(t);
    case "revealed":
      return Ar(t);
    default:
      return "";
  }
})()}
    </div>
    <div class="info-box">
      <div
        class="tips text-overflow-2 ${t.hideMBUnlockTips ? "opacity-hide" : ""}"
        .innerHTML="${t.activityInfo.guideText}">
      </div>
    </div>
  `;
var Lr = Object.defineProperty, Pr = Object.getOwnPropertyDescriptor, Z = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Pr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Lr(e, i, s), s;
};
let q = class extends st {
  constructor() {
    super(...arguments), this.props = {
      shopifyShopId: "",
      platform: "membership",
      actId: "",
      customTag: ""
    }, this.email = "", this.showBtnGroup = !1, this.hideMBUnlockTips = !1, this.scratchRgBoxBg = "", this.scratchOverlayImg = "", this.currentPrize = {
      itemId: 0,
      name: "",
      image: ""
    }, this.remainingTimes = 0, this.drawState = "locked", this.token = "", this.canvas = null, this.ctx = null, this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0;
  }
  // 组件渲染模板
  connectedCallback() {
    super.connectedCallback(), this.scratchRgBoxBg = this.activityInfo.scratchBoxBgImg, this.scratchOverlayImg = this.activityInfo.noSubscribeMaskImg;
  }
  /**
   * @method firstUpdated
   * @description 组件首次更新完成后的生命周期钩子，初始化Canvas
   */
  firstUpdated() {
    this.drawState === "scratching" && this.initCanvas();
  }
  /**
   * @method updated
   * @param {Map<string, any>} changedProperties - 变更的属性集合
   * @description 组件更新后的生命周期钩子，监听state变化以初始化Canvas
   */
  updated(t) {
    super.updated(t), t.has("drawState") && (this.drawState === "initial" ? (this.unlock(), this.clearCanvas()) : this.drawState === "scratching" ? this.initCanvas() : this.drawState === "locked" && (this.hideMBUnlockTips = !1, this.scratchOverlayImg = this.activityInfo.noSubscribeMaskImg));
  }
  /**
   * @method clearCanvas
   * @private
   * @description 清除画布内容和事件监听
   */
  clearCanvas() {
    this.canvas && (this.canvas.removeEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.removeEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.removeEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.removeEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.removeEventListener("touchend", this.handleTouchEnd.bind(this))), this.canvas && this.ctx && (this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.width = 0, this.canvas.height = 0, this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0);
  }
  /**
   * @method initCanvas
   * @private
   * @description 初始化Canvas画布，设置尺寸和事件监听
   */
  initCanvas() {
    var t;
    if (this.canvas = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".scratch-canvas"), !this.canvas) {
      console.warn("Canvas element not found");
      return;
    }
    if (this.ctx = this.canvas.getContext("2d"), !this.ctx) {
      console.warn("Failed to get canvas context");
      return;
    }
    if (this.canvas.offsetWidth <= 0 || this.canvas.offsetHeight <= 0) {
      console.warn("Invalid canvas dimensions");
      return;
    }
    this.canvas.width = this.canvas.offsetWidth, this.canvas.height = this.canvas.offsetHeight, this.totalPixels = this.canvas.width * this.canvas.height, this.ctx.fillStyle = "#7B7B7B", this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.addEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.addEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.addEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.addEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.addEventListener("touchend", this.handleTouchEnd.bind(this));
  }
  /**
   * @method handleMouseDown
   * @private
   * @param {MouseEvent} e - 鼠标事件对象
   * @description 处理鼠标按下事件，开始刮奖
   */
  handleMouseDown(t) {
    this.isDrawing = !0, [this.lastX, this.lastY] = [t.offsetX, t.offsetY];
  }
  /**
   * @method handleMouseMove
   * @private
   * @param {MouseEvent} e - 鼠标事件对象
   * @description 处理鼠标移动事件，执行刮奖动作
   */
  handleMouseMove(t) {
    !this.isDrawing || !this.ctx || !this.canvas || this.scratch(t.offsetX, t.offsetY);
  }
  /**
   * @method handleMouseUp
   * @private
   * @description 处理鼠标松开事件，结束刮奖动作
   */
  handleMouseUp() {
    this.isDrawing = !1;
  }
  /**
   * @method handleTouchStart
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸开始事件，支持移动端刮奖
   */
  handleTouchStart(t) {
    if (t.preventDefault(), !this.canvas) return;
    const e = t.touches[0], i = this.canvas.getBoundingClientRect();
    this.isDrawing = !0, this.lastX = e.clientX - i.left, this.lastY = e.clientY - i.top;
  }
  /**
   * @method handleTouchMove
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸移动事件，执行移动端刮奖动作
   */
  handleTouchMove(t) {
    if (t.preventDefault(), !this.isDrawing || !this.ctx || !this.canvas) return;
    const e = t.touches[0], i = this.canvas.getBoundingClientRect();
    this.scratch(e.clientX - i.left, e.clientY - i.top);
  }
  /**
   * @method handleTouchEnd
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸结束事件，结束移动端刮奖动作
   */
  handleTouchEnd(t) {
    t.preventDefault(), this.isDrawing = !1;
  }
  /**
   * @method scratch
   * @private
   * @param {number} x - 当前刮奖位置的X坐标
   * @param {number} y - 当前刮奖位置的Y坐标
   * @description 执行刮奖动作，计算已刮开区域并触发相应事件
   */
  scratch(t, e) {
    if (!this.ctx || !this.canvas) return;
    this.ctx.globalCompositeOperation = "destination-out", this.ctx.lineWidth = 35, this.ctx.lineCap = "round", this.ctx.lineJoin = "round", this.ctx.beginPath(), this.ctx.moveTo(this.lastX, this.lastY), this.ctx.lineTo(t, e), this.ctx.stroke(), [this.lastX, this.lastY] = [t, e];
    const i = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    let o = 0;
    for (let r = 0; r < i.data.length; r += 4)
      i.data[r + 3] === 0 && o++;
    o / this.totalPixels * 100 > 40 && this.drawState !== "revealed" && (this.drawState = "revealed", this.dispatchEvent(new CustomEvent("prize-revealed")), this.drawPrize("check"));
  }
  /**
   * @method drawPrize
   * @private
   * @description 调用抽奖接口获取奖品
   */
  async drawPrize(t) {
    var e, i, o, s;
    try {
      const r = {
        shopId: this.props.shopifyShopId || ((e = window.pageInfo) == null ? void 0 : e.shopId) || "55005249633",
        platform: this.props.platform === "membership" ? "member" : this.props.platform,
        actId: this.props.actId,
        tags: this.props.customTag,
        email: this.email,
        showType: "scratch",
        actType: "subscribe"
        // 活动类型：订阅抽奖
      };
      this.token && (r.token = this.token);
      const a = `${((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/cjActivityJoin`, l = await j.post(a, r, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.cr) || "US",
          Lang: ((s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.lang) || "en"
        }
      }), { code: d, success: h, message: p, data: w } = l.data;
      h && d === 200 ? (this.token ? (this.token = "", this.drawResults = w, this.dispatchEvent(new CustomEvent("update-prize-details", {
        detail: {
          drawResults: this.drawResults
        }
      })), this.showBtnGroup = !0) : (this.token = w.token, this.remainingTimes = w.userJoinTimes === null ? -1 : w.userJoinTimes, this.drawState = "scratching", this.dispatchEvent(new CustomEvent("draw-state-update", {
        detail: {
          drawState: this.drawState
        }
      }))), this.currentPrize = {
        itemId: w.itemId,
        name: w.title,
        image: w.image
      }, t === "check" && St({
        module: "Lottery",
        //点击模块固定名称Lottery   
        position: w.title
        // 奖品文本text
      })) : (d === 0 && (this.remainingTimes = 0), this.drawState === "initial" && (this.drawState = "locked", this.dispatchEvent(new CustomEvent("draw-state-update", {
        detail: {
          drawState: this.drawState
        }
      }))), this.token = "", $(p, "danger"));
    } catch {
      $(window.i18n.t("pages.error.default"), "danger");
    }
  }
  /**
   * @method unlock
   * @public
   * @description 解锁刮奖区域，切换到初始状态
   */
  unlock() {
    this.token = "", this.hideMBUnlockTips = !0, this.scratchOverlayImg = this.activityInfo.subscribeMaskImg, this.dispatchEvent(new CustomEvent("unlock-scratch"));
  }
  /**
   * @method handleUnlockClick
   * @public
   * @description 处理解锁按钮点击事件，触发相应事件
   */
  handleUnlockClick() {
    var t;
    if (window.innerWidth < 1024) {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".tips");
      e && (e.classList.add("active"), e.style.color = this.activityInfo.highlightErrTips);
    }
    this.dispatchEvent(new CustomEvent("unlock-click"));
  }
  /**
   * @method startScratch
   * @public
   * @description 开始刮奖，切换到刮奖状态
   */
  startScratch() {
    this.drawPrize("play"), St({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: Nt(this.activityInfo.btnPlayText)
      // 点击按钮，识别按钮文本text；
    });
  }
  /**
   * @method showPrizeDetails
   * @public
   * @description 显示奖品详情
   */
  showPrizeDetails() {
    this.dispatchEvent(new CustomEvent("show-prize-details", {
      detail: {
        welfareType: this.drawResults.welfareType
      }
    })), St({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: Nt(this.activityInfo.showPrizeBtnText)
      //点击文本 查看奖品/再玩一次（记录次数），识别文本text。
    });
  }
  /**
   * @method playAgain
   * @public
   * @description 再次进行刮奖，需要剩余次数大于0
   */
  playAgain() {
    (this.remainingTimes > 0 || this.remainingTimes === -1) && (this.canvas && (this.canvas.removeEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.removeEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.removeEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.removeEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.removeEventListener("touchend", this.handleTouchEnd.bind(this))), this.canvas && this.ctx && (this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.width = 0, this.canvas.height = 0), this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0, this.showBtnGroup = !1, this.drawPrize("playAgain"), St({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: `${Nt(this.activityInfo.tryAgainBtnText)}(${this.remainingTimes})`
      //点击文本 再玩一次（记录次数），识别文本text。
    }));
  }
  /**
   * @method render
   * @returns {TemplateResult} 组件的渲染模板
   * @description 渲染组件界面
   */
  render() {
    return _r(this);
  }
};
q.styles = [H(Xt), Qi, Sr];
Z([
  g({ type: Object, reflect: !1 })
], q.prototype, "props", 2);
Z([
  g({ type: String })
], q.prototype, "email", 2);
Z([
  m()
], q.prototype, "showBtnGroup", 2);
Z([
  m()
], q.prototype, "hideMBUnlockTips", 2);
Z([
  m()
], q.prototype, "scratchRgBoxBg", 2);
Z([
  m()
], q.prototype, "scratchOverlayImg", 2);
Z([
  m()
], q.prototype, "currentPrize", 2);
Z([
  g({ type: Number })
], q.prototype, "remainingTimes", 2);
Z([
  g({ type: String })
], q.prototype, "drawState", 2);
Z([
  m()
], q.prototype, "token", 2);
q = Z([
  _("scratch-right-box")
], q);
var Mr = Object.defineProperty, Br = Object.getOwnPropertyDescriptor, at = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Br(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Mr(e, i, s), s;
};
let X = class extends st {
  constructor() {
    super(...arguments), this.email = "", this.drawState = "locked", this.hightLightUnlockTips = !1, this.showRulesDialog = !1, this.showThanksDialog = !1, this.showVirtualCardDialog = !1, this.showCouponDialog = !1, this.showPointsDialog = !1;
  }
  // 是否显示积分弹窗
  // 生命周期 - 组件挂载后
  async connectedCallback() {
    super.connectedCallback(), await this.getActivityInfo();
    const { lucky_act_id: t } = Ke();
    t && setTimeout(() => {
      const e = document.querySelector(`jbc-lucky-scratch[actId=${t}]`) || document.querySelector("jbc-lucky-scratch");
      window.scrollTo({ top: (e == null ? void 0 : e.offsetTop) || 0 });
    }, 1e3);
  }
  // 高亮显示 未解锁 提示内容
  showUnlockTips() {
    this.hightLightUnlockTips = !0;
  }
  // 订阅成功
  handleSubscribeSuccess(t) {
    this.email = t.detail.email, this.drawState = t.detail.drawState, this.requestUpdate();
  }
  // 监听刮奖状态更新
  handleDrawStateUpdate(t) {
    this.drawState = t.detail.drawState, this.requestUpdate();
  }
  // 显示规则弹窗
  handleShowRulesDialog(t) {
    this.showRulesDialog = t.detail.showRulesDialog, this.requestUpdate();
  }
  // 监听奖品详情更新事件
  handleUpdatePrizeDetails(t) {
    this.drawResults = t.detail.drawResults, this.requestUpdate();
  }
  // 监听显示奖品详情事件
  handleShowPrizeDetails(t) {
    var i;
    const e = (i = this.shadowRoot) == null ? void 0 : i.querySelector("lucky-all-dialog");
    e && e.showDialog(t.detail.welfareType);
  }
  // 监听弹窗状态更新事件
  handleModalStateUpdate(t) {
    this.showRulesDialog = t.detail.showRulesDialog, this.showCouponDialog = t.detail.showCouponDialog, this.showPointsDialog = t.detail.showPointsDialog, this.showVirtualCardDialog = t.detail.showVirtualCardDialog, this.showThanksDialog = t.detail.showThanksDialog, this.requestUpdate();
  }
  // 自定义抽奖结束事件用于外部监听
  newCustomEvent() {
    const t = new CustomEvent("LuckyScratchDone", {
      detail: {
        status: !0
      }
    });
    document.dispatchEvent(t);
  }
  // 获取活动基本信息
  async getActivityInfo() {
    var o, s, r;
    const e = `${((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/getCjActivityInfo`, i = {};
    !this.platform || this.platform === "membership" ? i.shopifyShopId = this.shopifyShopId : (i.platform = this.platform, i.actId = this.actId, i.shopifyShopId = this.shopifyShopId), i.actType = "subscribe", i.showType = "scratch";
    try {
      const n = await j.get(e, {
        params: i,
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          Cr: ((s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.cr) || "US",
          Lang: ((r = window == null ? void 0 : window.pageInfo) == null ? void 0 : r.lang) || "en"
        }
      }), { data: a, success: l } = n.data;
      if (l && a !== null) {
        const {
          bgValue: d,
          bgValue1: h,
          btnBgType: p,
          btnTitleNologin: w,
          bgValue2: x,
          btnTitleLogin: f,
          ...b
        } = a;
        this.activityInfo = {
          pcBgImg: d,
          mbBgImg: h,
          showPhoneInput: p,
          btnPlayText: w,
          scratchBoxBgImg: x,
          tryAgainBtnText: f,
          ...b
        }, this.showLuckyComponent = !0;
      } else
        this.showLuckyComponent = !1;
    } catch (n) {
      this.showLuckyComponent = !1, n.code === "ECONNABORTED" ? console.error("请求超时:", n.message) : console.error("接口异常:", n);
    }
  }
  render() {
    return vr(this);
  }
};
X.styles = [H(Xt), wr];
at([
  m()
], X.prototype, "email", 2);
at([
  m()
], X.prototype, "drawState", 2);
at([
  m()
], X.prototype, "hightLightUnlockTips", 2);
at([
  m()
], X.prototype, "showRulesDialog", 2);
at([
  m()
], X.prototype, "showThanksDialog", 2);
at([
  m()
], X.prototype, "showVirtualCardDialog", 2);
at([
  m()
], X.prototype, "showCouponDialog", 2);
at([
  m()
], X.prototype, "showPointsDialog", 2);
X = at([
  _("jbc-lucky-scratch")
], X);
const Or = "html,body,div,span,applet,object,iframe,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,abbr,acronym,address,big,cite,code,del,dfn,em,img,ins,kbd,q,s,samp,small,strike,strong,sub,sup,tt,var,b,u,i,center,dl,dt,dd,ol,ul,li,fieldset,form,label,legend,table,caption,tbody,tfoot,thead,tr,th,td,article,aside,canvas,details,embed,footer,header,hgroup,main,menu,nav,section,summary{margin:0;padding:0;border:0;font-family:var(--jds-font-family)}table{border-collapse:collapse;border-spacing:0}a{text-decoration:none;color:inherit}img{max-width:100%;height:auto}input,button,textarea,select{font-family:inherit;font-size:inherit;color:inherit}*,*:before,*:after{box-sizing:border-box}@media screen and (min-width: 1024px){html{font-size:clamp(12px,1.111111vw,16px)}}@media screen and (max-width: 1023.98px){html{font-size:clamp(16px,4.266666vw,32px)}}@media screen and (min-width: 1024px){.jds-animate jds-button.only-icon{transform:scale(0);transform-origin:center center;transition:all .5s cubic-bezier(.8,0,.2,1)}.jds-animate jds-button.only-icon::part(icon){transform:translate(-48px);transition:transform .5s cubic-bezier(.8,0,.2,1)}.jds-animate:hover jds-button.only-icon{transform:scale(1)}.jds-animate:hover jds-button.only-icon::part(icon){transform:translate(0)}.jds-animate[variant=primary]::part(button){transition:all .3s cubic-bezier(.8,0,.2,1);padding-right:0}.jds-animate[variant=primary]::part(label){overflow:visible}.jds-animate[variant=primary]:hover::part(button){--bg-color: var(--jds-color-primary);padding-right:var(--jds-size-8);padding-left:var(--jds-size-24)}.jds-animate[variant=primary] jds-button.only-icon{margin-left:var(--jds-size-8);transition:all .3s cubic-bezier(.8,0,.2,1)}.jds-animate[variant=primary] jds-button.only-icon::part(button){--height: var(--jds-size-32);--bg-color: var(--main-hover-color)}.jds-animate[variant=primary] jds-button.only-icon::part(icon){--icon-size: var(--jds-size-16);transition:transform .4s cubic-bezier(.8,0,.2,1)}}@media screen and (max-width: 1023.98px){.jds-animate jds-button.only-icon::part(button){--icon-size: var(--jds-size-24)}.jds-animate[variant=primary] jds-button.only-icon{display:none}}jds-icon:not(:defined),jds-badge:not(:defined),jds-spinner:not(:defined),jds-button:not(:defined),jds-dialog:not(:defined),jds-toast:not(:defined),jds-popover:not(:defined),jds-tooltip:not(:defined),jds-i18n:not(:defined){visibility:hidden}ul:not(.rte ul,.jds-rte ul),ol:not(.rte ol,.jds-rte ol),li:not(.rte li,.jds-rte li),menu:not(.rte menu,.jds-rte menu){list-style:none}::-webkit-scrollbar-thumb{background-color:#33333380;border-radius:4px}::-webkit-scrollbar-thumb:hover{background-color:#333333b3}::-webkit-scrollbar-track{background-color:transparent;border-radius:4px}::-webkit-scrollbar-track:hover{background-color:#ffffff80}@media screen and (min-width: 1024px){::-webkit-scrollbar{width:7px;height:7px}}@media screen and (max-width: 1023.98px){::-webkit-scrollbar{width:6px;height:6px}}.jds-h1{font:var(--jds-font-h1-medium);letter-spacing:var(--jds-h1-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h2{font:var(--jds-font-h2-medium);letter-spacing:var(--jds-h2-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h3{font:var(--jds-font-h3-medium);letter-spacing:var(--jds-h3-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h4{font:var(--jds-font-h4-medium);letter-spacing:var(--jds-h4-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h5{font:var(--jds-font-h5-medium);letter-spacing:var(--jds-h5-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-h6{font:var(--jds-font-h6-semibold);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-b1{font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b1.font-regular{font:var(--jds-font-b1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b2{font:var(--jds-font-b2-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b2.font-regular{font:var(--jds-font-b2-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b3{font:var(--jds-font-b3-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-b3.font-regular{font:var(--jds-font-b3-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-c1{font:var(--jds-font-c1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l3))}:root:not([lang=ja]){--jds-list-padding: var(--jds-size-20)}:root[lang=ja]{--jds-list-padding: var(--jds-size-24)}.jds-rte{word-wrap:break-word;overflow-wrap:break-word;word-break:keep-all;font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}.jds-rte ul li{list-style:disc}.jds-rte ol li{list-style:decimal}.jds-rte h1{font:var(--jds-font-h1-medium);letter-spacing:var(--jds-h1-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h2{font:var(--jds-font-h2-medium);letter-spacing:var(--jds-h2-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h3{font:var(--jds-font-h3-medium);letter-spacing:var(--jds-h3-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h4{font:var(--jds-font-h4-medium);letter-spacing:var(--jds-h4-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h5{font:var(--jds-font-h5-medium);letter-spacing:var(--jds-h5-letter-spacing);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte h6{font:var(--jds-font-h6-semibold);color:var(--jds-rte-text-color, var(--jds-color-text-l1))}.jds-rte p{font:var(--jds-font-b1-medium);color:var(--jds-rte-text-color, var(--jds-color-text-l2));margin-bottom:var(--jds-size-16)}.jds-rte p:last-child{margin-bottom:0}.jds-rte strong,.jds-rte b{font-weight:600}.jds-rte a{text-decoration:underline}.jds-rte em,.jds-rte i{font-style:normal}.jds-rte.font-regular,.jds-rte.font-regular p{font:var(--jds-font-b1-regular);color:var(--jds-rte-text-color, var(--jds-color-text-l2))}@media screen and (min-width: 1024px){.jds-rte ul,.jds-rte ol{padding-inline-start:var(--jds-list-padding)}}@media screen and (max-width: 1023.98px){.jds-rte ul,.jds-rte ol{padding-inline-start:var(--jds-size-36)}@supports (-webkit-touch-callout: none){.jds-rte ul{padding-inline-start:var(--jds-size-40)}.jds-rte ol{padding-inline-start:var(--jds-size-56)}}}", Mt = k`
  * {
    margin: 0;
    padding:0;
    word-break: break-word;
    font-family: var(--jds-font-family);
  }
  li {
    list-style:none;
  }
  .text-overflow-1 {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }


  jds-dialog .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  jds-dialog .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  jds-dialog .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container){
    background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
    background-size: 70px 70px;
    position: relative;
    height: 560px;
    border-radius: 20px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
    display: block;
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    top: 8px;
    bottom: 8px;
    background: #fff;
    border-radius: 20px;
    z-index: -1;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 4.86111111111111vw 4.86111111111111vw;
      position: relative;
      height: 39.8888888888889vw;
      border-radius: 1.38888888888889vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 0.555555555555556vw;
      right: 0.555555555555556vw;
      top: 0.555555555555556vw;
      bottom: 0.555555555555556vw;
      background: #fff;
      border-radius: 1.38888888888889vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 70px 70px;
      position: relative;
      height: max-content;
      border-radius: 5.33333333333333vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 1.06666666666667vw;
      right: 1.06666666666667vw;
      top: 1.06666666666667vw;
      bottom: 1.06666666666667vw;
      background: #fff;
      border-radius: 5.33333333333333vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
  }
`, Ur = k`
  jds-dialog#rulesDialog{
    text-align: left;
  }
  jds-dialog#rulesDialog::part(dialog-container){
    min-height: 320px;
    max-height: 560px;
    height: auto;
  }
  jds-dialog#rulesDialog::part(dialog-content) {
    padding-left: 48px;
    padding-right: 10px;
    margin-right: 0;
  }
  jds-dialog#rulesDialog .dialog-content .detail {
    padding-right: 38px;
    padding-top: 6px;
    padding-bottom: 6px;
    max-height: 436px;
    overflow-y: auto;
  }
  jds-dialog#rulesDialog .dialog-content .detail img {
    width: auto;
    max-width: 100% !important;
    height: auto;
  }
  jds-dialog::part(dialog-content) {
    overflow: unset;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog#rulesDialog{
      text-align: left;
    }
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 22.2222222222222vw;
      max-height: 39.8888888888889vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 0.694444444444444vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.63888888888889vw;
      max-height: 30.2777777777778vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      overflow: unset;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 85.3333333333333vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 106.666666666667vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, Fr = k`
  jds-dialog:not(#rulesDialog) {
    text-align:center;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-content) {
    padding-left: 48px;
    padding-right: 18px;
    margin-right: 0;
    overflow: unset;
  }
  jds-dialog:not(#rulesDialog) .detail {
    padding-right: 30px;
    max-height: 380px;
    overflow-y: auto;
  }
  jds-dialog:not(#rulesDialog)#thsDialog .detail {
    max-height: 360px;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 1.25vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.08333333333333vw;
      max-height: 26.3888888888889vw;
      overflow-y: auto;
    }
    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 25vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.66666666666667vw;
      max-height: 88vw;
      overflow-y: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, Nr = k`
  :host {
    --jds-rules-dialog-width: 600px;
    --jds-prize-dialog-width: 640px;
  }
  :host .jds-container{
    width: 1200px;
    align-items: center;
    height: auto;
    margin: 0 auto;
    padding: 32px 46px;
    position: relative;
    z-index: 1;
    display:flex;
    justify-content: space-around;
    background-repeat: no-repeat;
    background-size: cover;
    box-sizing: border-box;
  }
  :host .wrapper{
    padding: 88px 0 66px;
    background-color: #FDF3E5;
  }
  .bg-img{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
  .rg-box{
    display:flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 100%;
  }
  jds-dialog .dialog-tips {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  jds-dialog .dialog-title{
    margin-right: 30px;
    height: 72px;
    margin-bottom: 18px;
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    color: #1A1A1A;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog#rulesDialog .dialog-title {
    height: auto;
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .ths-tips .dialog-title {
    height: 100px;
    font-size: 24px;
    line-height: 32px;
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .dialog-image{
    margin-top: 24px;
    margin-bottom: 16px;
    width: 248px;
    height: 166px;
    object-fit: contain;
  }
  jds-dialog .notes-full {
    font-size:14px;
    line-height: 22px;
    width: 100%;
    color: rgba(12,11,11,1);
  }
  jds-dialog .notes-box {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 24px;
    margin-top: 16px;
    margin-bottom: 10px;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules  {
    flex-shrink: 0;
    position: relative;
    overflow: visible;
    display: flex;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules {
    font-size: 16px;
    font-weight: 500;
    text-decoration-line: underline;
    text-underline-offset: 4px;
    color: #D98B24;
    cursor: pointer;
  }
  .rules-content {
    display: none;
    box-sizing: border-box;
    width: 420px;
    text-align: left;
    color: #444444;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    background-color: #fff;
    box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
    border-radius: 12px;
    border: 1px solid #EEEEEE;
    padding: 20px 10px 26px 32px;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 501;
  }
  .rules-content::after {
    display: block;
    content: '';
    position: absolute;
    left: 100%;
    bottom: 10px;
    z-index: 0;
    transform: translateY(-50%);
    border-color: transparent;
    border-left-color: rgba(255, 255, 255, 1);
    border-style: solid;
    background-color: transparent;
    filter: drop-shadow(5px 0px 3px rgba(0, 0, 0, 0.17));
    border-width: 13px;
    width: 0vw;
    height: 0vw
  }
  .rules-content h4.title {
    text-align: left;
    font-weight: 600;
    font-size: 24px;
    color: #666;
    padding-bottom: 10px;
    border-bottom: 1px solid #DDD;
    margin-bottom: 14px;
    margin-right: 6px;
  }
  .rules-content .detail {
    padding-right: 6px;
    max-height: 360px;
    overflow-y: auto;
  }
  .rules-content .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  .rules-content .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  .rules-content .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog .notes-box .notes{
    text-align: left;
    font-size:14px;
    line-height: 22px;
    width: fit-content;
    max-width: 364px;
    color: rgba(12,11,11,1);
  }
  jds-dialog jds-button::part(button) {
    height: 48px;
    line-height: 48px;
    min-width: 180px;
    font-size: 16px;
    font-weight: 600;
    margin-top: 38px;
  }


  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    :host {
      --jds-rules-dialog-width: 41.6666666666667vw;
      --jds-prize-dialog-width: 44.4444444444444vw;
    }
    :host .jds-container{
      width: 83.3333333333333vw;
      margin: 0 auto;
      padding: 3.33333333333333vw 3.19444444444444vw;
      position: relative;
      z-index: 1;
      display:flex;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 6.11111111111111vw 0 4.58333333333333vw;
      background-color: #FDF3E5;
    }
    .bg-img{
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
    }
    .rg-box{
      display:flex;
      justify-content: center;
      align-items: center;
      width: auto;
      height: 100%;
    }
    jds-dialog .dialog-tips {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    jds-dialog .dialog-title{
      margin-right: 2.08333333333333vw;
      height: 5vw;
      margin-bottom: 1.25vw;
      font-size: 1.94444444444444vw;
      line-height: 2.5vw;
      font-weight: 600;
      color: #1A1A1A;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 6.94444444444444vw;
      font-size: 1.66666666666667vw;
      line-height: 2.22222222222222vw;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image{
      margin-top: 1.66666666666667vw;
      margin-bottom: 1.11111111111111vw;
      width: 17.2222222222222vw;
      height: 11.5277777777778vw;
      object-fit: contain;
    }
    jds-dialog .notes-full {
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.66666666666667vw;
      margin-top: 1.11111111111111vw;
      margin-bottom: 0.694444444444444vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules  {
      flex-shrink: 0;
      position: relative;
      overflow: visible;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 1.11111111111111vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 0.277777777777778vw;
      color: #D98B24;
      cursor: pointer;
    }
    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 29.1666666666667vw;
      text-align: left;
      color: #444444;
      font-size: 1.11111111111111vw;
      line-height: 1.66666666666667vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 0.138888888888889vw 0.555555555555556vw 0px rgba(0,0,0,0.27);
      border-radius: 0.833333333333333vw;
      border: 1px solid #EEEEEE;
      padding: 1.38888888888889vw 0.694444444444444vw 1.80555555555556vw 2.22222222222222vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: block;
      content: '';
      position: absolute;
      left: 100%;
      bottom: 0.972222222222222vw;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-left-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0.347222222222222vw 0px 0.208333333333333vw rgba(0, 0, 0, 0.17));
      border-width: 0.902777777777778vw;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 1.66666666666667vw;
      color: #666;
      padding-bottom: 0.694444444444444vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 0.972222222222222vw;
      margin-right: 0.416666666666667vw;
    }
    .rules-content .detail {
      padding-right: 0.416666666666667vw;
      max-height: 25vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
    jds-dialog .notes-box .notes{
      text-align: left;
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: fit-content;
      max-width: 25.2777777777778vw;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 3.33333333333333vw;
      line-height: 3.33333333333333vw;
      min-width: 12.5vw;
      font-size: 1.11111111111111vw;
      font-weight: 600;
      margin-top: 2.63888888888889vw;
    }
  }


  @media screen and (max-width: 1023.98px){
    :host {
      --jds-rules-dialog-width: 600px;
      --jds-prize-dialog-width: 640px;
    }
    :host .jds-container{
      width: 91.4666666666667vw;
      margin: 0 auto;
      padding: 4.26666666666667vw 4.8vw;
      position: relative;
      z-index: 1;
      display:flex;
      align-items: center;
      flex-direction: column;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 15.4666666666667vw 0 16.9333333333333vw;
      background-color: #FDF3E5;
    }
    jds-dialog .dialog-title {
      margin-right: 2.66666666666667vw;
      margin-bottom: 5.33333333333333vw;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      color: #1A1A1A;
      height: unset;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 25.6vw;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      display: -webkit-box;
      line-clamp: 4;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image {
      margin-bottom: 4.26666666666667vw;
      width: 53.3333333333333vw;
      height: 35.7333333333333vw;
      object-fit: contain;
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 6.4vw;
      margin-top: 4.26666666666667vw;
      margin-bottom: 6.4vw;
      padding-left: 1.33333333333333vw;
      padding-right: 4vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 3.73333333333333vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 4px;
      color: #D98B24;
      cursor: pointer;
    }
    jds-dialog .notes-box .notes {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      color: rgba(12,11,11,1);
    }

    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 77.3333333333333vw;
      text-align: left;
      color: #444444;
      font-size: 3.73333333333333vw;
      line-height: 5.33333333333333vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
      border-radius: 2.66666666666667vw;
      border: 1px solid #EEEEEE;
      padding: 4.26666666666667vw 1.6vw 5.33333333333333vw 4.26666666666667vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: none;
      content: '';
      position: absolute;
      top: calc(100% + 10px);
      left: 80%;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-top-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.17));
      border-width: 10px;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      color: #333;
      padding-bottom: 2.66666666666667vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 2.66666666666667vw;
      margin-right: 2.66666666666667vw;
    }
    .rules-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 80vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
    jds-dialog .notes-full {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 10.6666666666667vw;
      line-height: 10.6666666666667vw;
      min-width: 42.6666666666667vw;
      font-size: 3.73333333333333vw;
      font-weight: 600;
      margin-top: 6.4vw;
    }
  }
`, ke = k`
  .form-subs {
      padding-top: 16px;
  }

  .form-items {
      position: relative;
      margin-bottom: 12px;
  }

  .form-items input::placeholder {
    color: rgba(153, 153, 153, 1);
  }
  .form-items input {
      box-sizing: border-box;
      padding: 0 16px;
      width: 100%;
      border: none;
      outline:none;
      background: #FFFFFF;
      border-radius: 8px;
      height: 46px;
      color: #333;
      font-size: 18px;
      font-weight: 500;
      border: solid 1px transparent;
      transition: all .24s ease;
  }

  .form-button {
      border: none;
      height: 48px;
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-color, #FFFFFF);
      background: var(--btn-bg, #FD5000);
      border-radius: 8px;
      cursor: pointer;
      position: relative;
  }
  .form-button.loading {
    pointer-events: none;
    opacity: 0.8;
  }

  .form-items.checkbox-content {
      margin-bottom: 0;
      margin-top: -4px;
  }
  .form-items.checkbox-content input {
      width: auto;
      height: auto;
      display: none;
  }
  .checkbox-wrapper {
    display: flex;
    flex-grow: 1;
    align-items: flex-start;
    height: inherit;
    cursor: inherit;
    gap: 10px;
    flex-direction: row-reverse;
    justify-content: flex-end;
    cursor: pointer;
    text-align: left;
    font-size: 14px;
    color: rgba(102, 102, 102, 1);
    line-height: 20px;
  }
  .label-text-wrapper a {
    color: rgba(102, 102, 102, 1);
  }
  .label-text-wrapper a:hover {
      color: rgba(253, 80, 0, 1);
  }

  .checkbox-icon {
    border-width: 1px;
    padding: 1px;
    border-color: #999;
    border-style: solid;
    position: relative;
    width: 16px;
    height: 16px;
    border-radius: 2px;
    transition: background .18s cubic-bezier(.4, 0, .2, 1);
    box-sizing: border-box;
    background: transparent;
  }
  .checkbox-icon path {
    fill: none;
    stroke: #fff;
    stroke-width: 3;
    opacity: 0;
    stroke-dasharray: 30;
    stroke-dashoffset: 30;
  }
  .native-wrapper {
    display: flex;
    align-items: center;
    padding-top: 2px;
  }
  .checkbox-wrapper input:checked+.native-wrapper .checkbox-icon {
    border-color: rgba(253, 80, 0, 1);
    background: #fff;
  }
  .checkbox-wrapper input:checked+.native-wrapper .checkbox-icon path {
    opacity: 1;
    stroke: rgba(253, 80, 0, 1);
    stroke-dashoffset: 0;
    transition: stroke-dashoffset 90ms linear 90ms;
  }

  .form-field--error {
      text-align: left;
      font-size: 14px;
      line-height: 22px;
      color: rgba(254, 59, 48, 1);
      font-weight: 400;
      display: none;
  }
  .form-field--error.active {
      display: block;
  }

  @media screen and (max-width: 1024px) {
    .form-items input {
        padding: 0 10px;
        border-radius: 4px;
        height: 44px;
        font-size: 16px;
    }

    .form-button {
        height: 44px;
        font-size: 16px;
        border-radius: 4px;
    }

    .sub-form-title {
        font-weight: 500;
        font-size: 14px;
        color: #FD5000;
        line-height: 18px;
        text-align: center;
        margin-bottom: 8px;
        margin-top: 24px;
    }
  }
`;
var Hr = Object.getOwnPropertyDescriptor, qr = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Hr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = n(s) || s);
  return s;
};
let Me = class extends A {
  render() {
    return u`
    <div class="loading__spinner loading__spinner__dark">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
    </div>
    `;
  }
};
Me.styles = [Mt, k`
    .loading__spinner {
        bottom: auto;
        height: 1.4rem;
        padding: 0;
        position: absolute;
        right: auto;
        top: auto;
        width: 1.4rem;
        z-index: 1;
    }
    .loading__spinner.loading__spinner__dark .spinner {
        animation: rotator 1.4s linear infinite;
    }

    .loading__spinner.loading__spinner__dark .spinner .path {
        stroke-dasharray: 280;
        stroke-dashoffset: 0;
        stroke: #fff;
        animation: dash 1.4s ease-in-out infinite;
        transform-origin: center;
    }

    @keyframes rotator{0%{transform:rotate(0)}to{transform:rotate(270deg)}}
    @keyframes dash{
        0%{stroke-dashoffset: 280;}
        50%{stroke-dashoffset: 75;
            transform: rotate(135deg);}
        100%{    stroke-dashoffset: 280;
            transform: rotate(450deg);}
    }
  `];
Me = qr([
  _("jbc-loading")
], Me);
var Vr = Object.defineProperty, Kr = Object.getOwnPropertyDescriptor, it = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Kr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Vr(e, i, s), s;
};
let K = class extends A {
  constructor() {
    super(...arguments), this.isLogined = !1, this.isDrawing = !1, this.lockSubmit = !1, this.isSubscribed = !1, this.activityInfo = {
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      btnTitleNologin: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      privacyAgreementText: "",
      btnTitleLogin: "",
      bgValue2: "",
      bgValue3: ""
    }, this.subEmail = "", this.agreePrivacy = !1;
  }
  async connectedCallback() {
    super.connectedCallback(), this.agreePrivacy = !!V("__subs_draw_agree"), V("__subs_draw_agree") && (this.subEmail = V("__subs_draw_agree") || "", this.isSubscribed = !0);
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("succes-callback", {
      detail: {
        email: this.subEmail
      },
      bubbles: !0,
      composed: !0
    }));
  }
  handleValidEmail() {
    var t, e;
    return !this.subEmail || !Qt(this.subEmail) ? ((t = this.emailError) == null || t.classList.add("active"), !1) : ((e = this.emailError) == null || e.classList.remove("active"), !0);
  }
  handleValidPrivacy() {
    var t, e;
    return this.agreePrivacy ? ((e = this.privacyError) == null || e.classList.remove("active"), !0) : ((t = this.privacyError) == null || t.classList.add("active"), !1);
  }
  handlePrivacyChange(t) {
    this.agreePrivacy = t.target.checked, this.handleValidPrivacy();
  }
  handleInputEmail(t) {
    this.subEmail = t.target.value;
  }
  async subscribe() {
    var t, e, i, o;
    try {
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.subEmail,
        tags: "GGCJ-Subscribe",
        newUserTags: "NEWGG-Subscribe",
        isSync: !1,
        componentName: "宫格订阅",
        pageUrl: window.location.href
      }, n = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await j.post(n, s, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || "US",
          Lang: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang) || "en"
        }
      }), { code: l, success: d, message: h } = a.data;
      d ? l === 200 && (this.isSubscribed = !0, this.startDraw(), be("__subs_draw_agree", this.subEmail, 1e3 * 60 * 60 * 24)) : $(h, "danger"), this.lockSubmit = !1;
    } catch {
      $(window.i18n.t("pages.error.default"), "danger"), this.lockSubmit = !1;
    }
  }
  handleSubmit(t) {
    if (t.preventDefault(), this.lockSubmit) return;
    const e = this.handleValidEmail(), i = this.handleValidPrivacy();
    e && i && (this.lockSubmit = !0, setTimeout(() => {
      this.subscribe();
    }, 20));
  }
  render() {
    return u`
      <div class="lf-info">
        <div class="title">
          ${this.activityInfo.mainTitleType === 0 ? u`<h2 class="title-content text-overflow-3" .innerHTML="${this.activityInfo.mainTitle}"></h2>` : u`<img src="${this.activityInfo.mainTitle}" loading="lazy"/>`}
        </div>
        <div class="subtitle text-overflow-2" .innerHTML="${this.activityInfo.subTitle}"></div>
        ${u`<div class="tips tips-one text-overflow-2 pc-show" .innerHTML="${this.activityInfo.guideText}"></div>`}
        <div class="form-subs pc-show">
          <form>
            <div class="form-items">
              <input
                @input="${this.handleInputEmail}"
                .value="${this.subEmail}"
                @blur="${this.handleValidEmail}" id="jackery-field-0" class="field__ref native-input sc-jackery-field" placeholder="*${window.i18n.t("form.placeholder.email")}" name="jackery-field-0" spellcheck="false" type="email" autocapitalize="off" autocomplete="valid-field__email" autocorrect="off">
                <div id="email-error" class="form-field--error">${window.i18n.t("form.validation.email")}</div>
            </div>

            <div class="form-items">
              <button
                class="form-button text-overflow-1 ${this.isDrawing ? "loading" : ""}"
                type="submit" 
                @click="${this.handleSubmit}" 
                style="
                  --text-color:${this.isSubscribed ? this.activityInfo.bgValue2 || this.activityInfo.btnColor : this.activityInfo.btnColor};
                  --btn-bg:${this.isSubscribed ? this.activityInfo.bgValue3 || this.activityInfo.btnBgValue : this.activityInfo.btnBgValue};
                "
              >${this.lockSubmit ? u`<jbc-loading></jbc-loading>` : this.isSubscribed ? this.activityInfo.btnTitleLogin || this.activityInfo.btnTitle : this.activityInfo.btnTitle}</button>
            </div>

            <div class="form-items checkbox-content">
              <label class="checkbox-wrapper">
                  <div class="label-text-wrapper" part="label" .innerHTML="${this.activityInfo.privacyAgreementText}"></div>
                  <input .checked=${this.agreePrivacy} @change="${this.handlePrivacyChange}" type="checkbox">
                  <div class="native-wrapper">
                      <svg class="checkbox-icon" viewBox="0 0 24 24" part="container">
                          <path d="M1.73,12.91 8.1,19.28 22.79,4.59" part="mark"></path>
                      </svg>
                  </div>
              </label>

              <div id="privacy-error" class="form-field--error">${window.i18n.t("form.validation.termsOfService")}</div>
            </div>
          </form>
        </div>
        <div class="pc-show">
          <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
          ${this.activityInfo.ruleTitle ? u`
                <div class="rule-text" @click="${this.showRulesDialog}">
                  <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                  <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                    <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                  </svg>
                </div>
              ` : ""}
        </div>
      </div>
    `;
  }
};
K.styles = [Mt, ke, k`
    .pc-show {
      display: block;
    }
    .lf-info{
      width: 420px;
      height: 100%;
      text-align: center;
    }
    .title{
      display: flex;
      justify-content: center;
      align-items: center;
      width: 420px;
      height: auto;
      overflow: hidden;
    }
    .title img{
      width: auto;
      height: 160px;
    }
    .title-content{
      padding: 0 45px;
      font-weight: bold;
      font-style: normal;
      font-size: 56px;
      color: #FD5000;
      line-height: 64px;
      height: auto;
      place-content: center;
    }
    .subtitle{
      margin-top: 8px;
      width: 420px;
      font-weight: bold;
      font-size: 24px;
      color: #FD5000;
      line-height: 1.2;
      font-style: normal;
    }
    .tips{
      width: 420px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      font-style: normal;
    }
    .tips-one{
      margin-top: 4px;
      color: #fd5000;
      font-size: 16px;
      line-height: 1.2;
      font-weight: 500;
    }
    .tips-two{
      margin-top: 22px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: #1A1A1A;
      text-align: center;
    }
    .rule-text {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 6px;
      margin-top: 10px;
      width:100%;
      font-weight: 500;
      font-size: 20px;
      color: #1A1A1A;
      line-height: 20px;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 20px;
      height: 20px;
      color: #1A1A1A;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      .pc-show {
        display: block;
      }
      .lf-info{
        width: 31.9444444444444vw;
        height: 100% ;
        text-align: center;
      }
      .title{
        display: flex;
        justify-content: center;
        align-items: center;
        width: 31.9444444444444vw;
        height: auto;
        overflow: hidden;
      }
      .title img{
        width: 100%;
        height: 16.5277777777778vw;
      }
      .title-content{
        padding: 0 3.125vw;
        font-weight: bold;
        font-style: normal;
        font-size: 3.88888888888889vw;
        color: #FD5000;
        line-height: 4.44444444444444vw;
        height: auto;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.416666666666667vw;
        width: 31.9444444444444vw;
        font-weight: bold;
        font-size: 1.94444444444444vw;
        color: #FD5000;
        line-height: 2.5vw;
        font-style: normal;
      }
      .tips{
        width: 31.9444444444444vw;
        font-weight: 500;
        font-size: 1.11111111111111vw;
        line-height: 1.66666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin-top: 0.555555555555556vw;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 2.77777777777778vw;
        color: #1A1A1A;
        text-align: center;
      }
      .rule-text {
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 0.694444444444444vw;
        margin-top: 1.11111111111111vw;
        width:100%;
        font-weight: 500;
        font-size: 1.38888888888889vw;
        color: #1A1A1A;
        line-height: 1.38888888888889vw;
        font-style: normal;
        cursor: pointer;
      }
      .arrow {
        width: 1.38888888888889vw;
        height: 1.38888888888889vw;
        color: #1A1A1A;
      }
    }

    jbc-loading {
      height: 1.4rem;
      width: 1.4rem;
      display: block;
    }
    @media screen and (max-width: 1023.98px){
      .pc-show {
        display: none;
      }
      .lf-info{
        width: 80.8vw;
        display: flex;
        align-items: center;
        flex-direction: column;
      }
      .title{
        width: 61.3333333333333vw;
        height: 31.7333333333333vw;
        overflow: hidden;
      }
      .title-content{
        padding: 0 6vw;
        font-weight: bold;
        font-style: normal;
        font-size: 7.46666666666667vw;
        color: #FD5000;
        line-height: 8.53333333333333vw;
        height: auto;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.8vw;
        width: 80.8vw;
        font-weight: bold;
        font-size: 4.8vw;
        color: #FD5000;
        line-height: 5.86666666666667vw;
        font-style: normal;
        margin-bottom: 12px;
      }
      .tips{
        width: 61.3333333333333vw;
        font-weight: 500;
        font-size: 3.46666666666667vw;
        line-height: 4.26666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin: 1.33333333333333vw 0;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 4.26666666666667vw;
        color: #1A1A1A;
        font-size: 3.46666666666667vw;
        line-height: 5.33333333333333vw;
      }
      .rule-text{
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 1.6vw;
        margin-top: 3.2vw;
        width:100%;
        font-weight: 500;
        font-size: 4vw;
        color: #1A1A1A;
        line-height: 4vw;
        font-style: normal;
        cursor: pointer;
      }
    }
  `];
it([
  g({ type: Boolean })
], K.prototype, "isLogined", 2);
it([
  g({ type: Boolean })
], K.prototype, "isDrawing", 2);
it([
  g({ type: Boolean })
], K.prototype, "lockSubmit", 2);
it([
  g({ type: Boolean })
], K.prototype, "isSubscribed", 2);
it([
  ft("#privacy-error")
], K.prototype, "privacyError", 2);
it([
  ft("#email-error")
], K.prototype, "emailError", 2);
it([
  g({ type: Object, reflect: !1 })
], K.prototype, "activityInfo", 2);
it([
  m()
], K.prototype, "subEmail", 2);
it([
  m()
], K.prototype, "agreePrivacy", 2);
K = it([
  _("sub-left-info")
], K);
const We = k`
  .tips-two{
    margin-top: 40px;
    color: #1A1A1A;
    text-align: center;
  }
  .rule-text {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
    margin-top: 16px;
    width:100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 1;
    color: #1A1A1A;
    font-style: normal;
    cursor: pointer;
  }
  .grid {
    display: grid;
    gap: 12px;
    position: relative;
    margin: 0;
    text-align: center;
    box-sizing: border-box;
  }
  .prize {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 5px solid #fff;
    border-radius: 12px;
    padding: 8px 8px 6px;
    transition: transform 0.3s ease, border-color 0.3s ease;
    background-color: #fff;
    box-sizing: border-box;
  }
  .prize img {
    margin-bottom: 6px;
    width: 138px;
    height: 92px;
    object-fit: cover;
  }
  .prize .name, 
  .login-btn-tips {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 44px;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    color:#FE651F;
  }
  .prize .name p,
  .login-btn-tips p {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .selected {
    border:5px solid #FD891C;
    background-color:#FFEFB5;
  }
  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position:relative;
    padding: 0 20px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    color: #FFFFFF;
    background-color: #fd5000;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }
  .btn::before{
    content: " ";
    display: block;
    width: 67px;
    height: 43px;
    background-image: url(${H(It)});
    background-size: cover;
    position: absolute;
    top:6px;
    left:6px;
    z-index:1;
  }
  .btn:disabled {
    background-color: #ef6114;
    cursor: wait;
  }
  .btn:active {
    transform: scale(1.1);
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    .tips-two{
      margin-top: 2.77777777777778vw;
    }
    .rule-text {
      column-gap: 0.694444444444444vw;
      margin-top: 1.11111111111111vw;
      font-size: 1.38888888888889vw;
    }
    .grid {
      gap: 0.833333333333333vw;
    }
    .prize {
      border: 0.347222222222222vw solid #fff;
      border-radius: 0.833333333333333vw;
      padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
    }
    .prize img {
      margin-bottom: 0.416666666666667vw;
      width: 9.58333333333333vw;
      height: 6.38888888888889vw;
    }
    .prize .name, 
    .login-btn-tips {
      height: 3.05555555555556vw;
      font-size: 1.11111111111111vw;
      line-height: 1.38888888888889vw;
    }
    .selected {
      border: 0.347222222222222vw solid #FD891C;
    }
    .btn {
      padding: 0 1.38888888888889vw;
      border-radius: 0.555555555555556vw;
      box-shadow: 0 0.138888888888889vw 0.347222222222222vw rgba(0, 0, 0, 0.1);
    }
    .btn::before{
      content: " ";
      display: block;
      width: 4.65277777777778vw;
      height: 2.98611111111111vw;
      background-image: url(${H(It)});
      background-size: cover;
      position: absolute;
      top: 0.416666666666667vw;
      left: 0.416666666666667vw;
      z-index: 1;
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
  @media screen and (max-width: 1023.98px){
    .tips-two{
      margin-top: 4.26666666666667vw;
      color: #1A1A1A;
      font-size: 3.46666666666667vw;
      line-height: 5.33333333333333vw;
    }
    .rule-text{
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.6vw;
      margin-top: 3.2vw;
      width:100%;
      font-weight: 500;
      font-size: 4vw;
      color: #1A1A1A;
      line-height: 4vw;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 4vw;
      height: 4vw;
      color: #1A1A1A;
    }
    .grid {
      gap: 1.6vw;
    }
    .prize {
      border: 0.666666666666667vw solid #fff;
      border-radius: 1.6vw;
      padding: 1.06666666666667vw 1.06666666666667vw 0.8vw;
    }
    .prize img {
      margin-bottom: 0.266666666666667vw;
      width: 18.4vw;
      height: 12.2666666666667vw;
      object-fit: cover;
    }
    .prize .name,
    .login-btn-tips {
      width: 100%;
      height: 10.6666666666667vw;
      font-weight: 600;
      font-size: 3.2vw;
      line-height: 3.73333333333333vw;
      color:#FE651F;
    }
    .prize .name p,
    .login-btn-tips p {
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .selected {
      border:5px solid #FD891C;
      background-color:#FFEFB5;
    }
    .btn {
      position:relative;
      padding: 0 20px;
      border: none;
      border-radius: 8px;
      font-weight: bold;
      color: #FFFFFF;
      background-color: #fd5000;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
    .btn::before{
      content: " ";
      display: block;
      width: 67px;
      height: 43px;
      background-image: url(${H(It)});
      background-size: cover;
      position: absolute;
      top:6px;
      left:6px;
      z-index:1;
    }
    .btn:disabled {
      background-color: #ef6114;
      cursor: wait
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
`;
var Gr = Object.defineProperty, Jr = Object.getOwnPropertyDescriptor, G = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Jr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Gr(e, i, s), s;
};
let U = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.lockSubmit = !1, this.isSubscribed = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: "",
      privacyAgreementText: "",
      btnTitleLogin: "",
      bgValue2: "",
      bgValue3: ""
    }, this.subEmail = "", this.agreePrivacy = !1;
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("succes-callback", {
      detail: {
        email: this.subEmail
      },
      bubbles: !0,
      // 允许事件冒泡到父组件
      composed: !0
      // 允许跨越 Shadow DOM 边界
    }));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  async connectedCallback() {
    super.connectedCallback(), this.agreePrivacy = !!V("__subs_draw_agree"), V("__subs_draw_agree") && (this.subEmail = V("__subs_draw_agree") || "", this.isSubscribed = !0);
  }
  handleValidEmail() {
    var t, e;
    return !this.subEmail || !Qt(this.subEmail) ? ((t = this.emailError) == null || t.classList.add("active"), !1) : ((e = this.emailError) == null || e.classList.remove("active"), !0);
  }
  handleValidPrivacy() {
    var t, e;
    return this.agreePrivacy ? ((e = this.privacyError) == null || e.classList.remove("active"), !0) : ((t = this.privacyError) == null || t.classList.add("active"), !1);
  }
  handlePrivacyChange(t) {
    this.agreePrivacy = t.target.checked, this.handleValidPrivacy();
  }
  handleInputEmail(t) {
    this.subEmail = t.target.value;
  }
  async subscribe() {
    var t, e, i, o;
    try {
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.subEmail,
        tags: "GGCJ-Subscribe",
        newUserTags: "NEWGG-Subscribe",
        isSync: !1,
        componentName: "宫格订阅",
        pageUrl: window.location.href
      }, n = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await j.post(n, s, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || "US",
          Lang: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang) || "en"
        }
      }), { code: l, success: d, message: h } = a.data;
      d ? l === 200 && (this.isSubscribed = !0, this.startDraw(), be("__subs_draw_agree", this.subEmail, 1e3 * 60 * 60 * 24)) : $(h, "danger"), this.lockSubmit = !1;
    } catch {
      $(window.i18n.t("pages.error.default"), "danger"), this.lockSubmit = !1;
    }
  }
  handleSubmit(t) {
    if (t.preventDefault(), this.lockSubmit) return;
    const e = this.handleValidEmail(), i = this.handleValidPrivacy();
    e && i && (this.lockSubmit = !0, setTimeout(() => {
      this.subscribe();
    }, 20));
  }
  render() {
    return u`

      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <button class="btn grid-center--icon" ?disabled="${this.isDrawing}">
          <img class="grid-gift-icon" src="https://cdn.shopify.com/s/files/1/0550/0524/9633/files/gift_6ef1f749-5677-4167-b248-02609884b175.png?v=1745202383" alt="" loading="lazy"/>
        </button>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 3; grid-row: 3;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 2; grid-row: 3;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 6 ? "selected" : ""}" style="grid-column: 1; grid-row: 3;">
          <img src="${this.prizes[6].image}" alt="${this.prizes[6].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[6].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 7 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[7].image}" alt="${this.prizes[7].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[7].name}</p>
          </div>
        </div>
      </div>
      <div class="mb-show">
      <div class="form-subs">
      <div class="sub-form-title" .innerHTML="${this.activityInfo.guideText}"></div>
      <form>
        <div class="form-items">
          <input
            @input="${this.handleInputEmail}"
            .value="${this.subEmail}"
            @blur="${this.handleValidEmail}" id="jackery-field-0" class="field__ref native-input sc-jackery-field" placeholder="*${window.i18n.t("form.placeholder.email")}" name="jackery-field-0" spellcheck="false" type="email" autocapitalize="off" autocomplete="valid-field__email" autocorrect="off">
            <div id="email-error" class="form-field--error">${window.i18n.t("form.validation.email")}</div>
        </div>

        <div class="form-items">
          <button
            class="form-button text-overflow-1 ${this.isDrawing ? "loading" : ""}"
            type="submit"
            @click="${this.handleSubmit}"
            style="
              --text-color:${this.isSubscribed ? this.activityInfo.bgValue2 || this.activityInfo.btnColor : this.activityInfo.btnColor};
              --btn-bg:${this.isSubscribed ? this.activityInfo.bgValue3 || this.activityInfo.btnBgValue : this.activityInfo.btnBgValue};
            "
          >${this.lockSubmit ? u`<jbc-loading></jbc-loading>` : this.isSubscribed ? this.activityInfo.btnTitleLogin || this.activityInfo.btnTitle : this.activityInfo.btnTitle}</button>
        </div>

        <div class="form-items checkbox-content">
          <label class="checkbox-wrapper">
              <div class="label-text-wrapper" part="label" .innerHTML="${this.activityInfo.privacyAgreementText}"></div>
              <input .checked=${this.agreePrivacy} @change="${this.handlePrivacyChange}" type="checkbox">
              <div class="native-wrapper">
                  <svg class="checkbox-icon" viewBox="0 0 24 24" part="container">
                      <path d="M1.73,12.91 8.1,19.28 22.79,4.59" part="mark"></path>
                  </svg>
              </div>
          </label>

          <div id="privacy-error" class="form-field--error">${window.i18n.t("form.validation.termsOfService")}</div>
        </div>
      </form>
    </div>

        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
U.styles = [Mt, ke, We, k`
    :host{
      width: 606px;
    }
    jbc-loading {
      height: 1.4rem;
      width: 1.4rem;
      display: block;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(3, 1fr);
      width: 606px;
      height: 505px;
    }
    .prize{
      padding: 4px 8px 4px;
    }
    .btn {
      --btn-size: 40px;
      grid-column: 2;
      grid-row: 2;
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .btn.grid-center--icon {
      background-color: transparent;
      box-shadow: none;
      cursor: default;
    }
    .btn.grid-center--icon:active {
      transform: scale(1);
    }
    .btn.grid-center--icon::before {
      display: none;
    }
    .btn-txt{
      width: calc(100% + 40px);
      font-size: var(--btn-size);
      line-height: 56px;
      margin-top: 32px;
      margin-bottom: 6px;
    }
    .btn-text-center {
      align-items: center;
      margin-top: 0;
      margin-bottom: 0;
    }
    .grid-gift-icon {
      width: 100%;height: 100%;object-fit: contain;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(3, 1fr);
        width: 42.0833333333333vw;
        height: 35.0694444444444vw;
      }
      .prize{
        padding: 0.277777777777778vw 0.555555555555556vw 0.277777777777778vw;
      }
      .btn {
        --btn-size: 2.77777777777778vw;
        grid-column: 2;
        grid-row: 2;
        border-radius: 1.11111111111111vw;
      }
      .btn-txt{
        width: calc(100% + 2.77777777777778vw);
        font-size: var(--btn-size);
        line-height: 3.88888888888889vw;
        margin-top: 2.22222222222222vw;
        margin-bottom: 0.416666666666667vw;
      }
      .btn-text-center {
        align-items: center;
        margin-top: 0;
        margin-bottom: 0;
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }
      .mb-show {
        display: block;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 80.8vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 4.8vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 25.3333333333333vw;
        border-radius: 2.13333333333333vw;
      }
      .btn-txt{
        width: calc(100% + 5.33333333333333vw);
        font-size: 5.33333333333333vw;
        line-height: 7.46666666666667vw;
        margin-top: 0;
        margin-bottom: 0;
      }
      .btn-txt.btn-text-end {
        margin-top: 4.80000000000003vw;
      }
    }
  `];
G([
  g({ type: Number, reflect: !0 })
], U.prototype, "currentIndex", 2);
G([
  g({ type: Array, reflect: !1 })
], U.prototype, "prizes", 2);
G([
  g({ type: Boolean, reflect: !0 })
], U.prototype, "isDrawing", 2);
G([
  g({ type: Boolean, reflect: !0 })
], U.prototype, "isLogined", 2);
G([
  g({ type: Boolean })
], U.prototype, "lockSubmit", 2);
G([
  g({ type: Boolean })
], U.prototype, "isSubscribed", 2);
G([
  g({ type: Object, reflect: !1 })
], U.prototype, "activityInfo", 2);
G([
  ft("#privacy-error")
], U.prototype, "privacyError", 2);
G([
  ft("#email-error")
], U.prototype, "emailError", 2);
G([
  m()
], U.prototype, "subEmail", 2);
G([
  m()
], U.prototype, "agreePrivacy", 2);
U = G([
  _("sub-right-eight-prizes")
], U);
var Wr = Object.defineProperty, Yr = Object.getOwnPropertyDescriptor, J = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Yr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Wr(e, i, s), s;
};
let F = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.lockSubmit = !1, this.isSubscribed = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: "",
      privacyAgreementText: "",
      btnTitleLogin: "",
      bgValue2: "",
      bgValue3: ""
    }, this.subEmail = "", this.agreePrivacy = !1;
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("succes-callback", {
      detail: {
        email: this.subEmail
      },
      bubbles: !0,
      composed: !0
    }));
  }
  async connectedCallback() {
    super.connectedCallback(), this.agreePrivacy = !!V("__subs_draw_agree"), V("__subs_draw_agree") && (this.subEmail = V("__subs_draw_agree") || "", this.isSubscribed = !0);
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  handleValidEmail() {
    var t, e;
    return !this.subEmail || !Qt(this.subEmail) ? ((t = this == null ? void 0 : this.emailError) == null || t.classList.add("active"), !1) : ((e = this == null ? void 0 : this.emailError) == null || e.classList.remove("active"), !0);
  }
  handleValidPrivacy() {
    var t, e;
    return this.agreePrivacy ? ((e = this.privacyError) == null || e.classList.remove("active"), !0) : ((t = this.privacyError) == null || t.classList.add("active"), !1);
  }
  handlePrivacyChange(t) {
    this.agreePrivacy = t.target.checked, this.handleValidPrivacy();
  }
  handleInputEmail(t) {
    this.subEmail = t.target.value;
  }
  async subscribe() {
    var t, e, i, o;
    try {
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.subEmail,
        tags: "GGCJ-Subscribe",
        newUserTags: "NEWGG-Subscribe",
        isSync: !1,
        componentName: "宫格订阅",
        pageUrl: window.location.href
      }, n = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await j.post(n, s, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || "US",
          Lang: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang) || "en"
        }
      }), { code: l, success: d, message: h } = a.data;
      d ? l === 200 && (this.isSubscribed = !0, this.startDraw(), be("__subs_draw_agree", this.subEmail, 1e3 * 60 * 60 * 24)) : $(h, "danger"), this.lockSubmit = !1;
    } catch {
      $(window.i18n.t("pages.error.default"), "danger"), this.lockSubmit = !1;
    }
  }
  handleSubmit(t) {
    if (t.preventDefault(), this.lockSubmit) return;
    const e = this.handleValidEmail(), i = this.handleValidPrivacy();
    e && i && (this.lockSubmit = !0, setTimeout(() => {
      this.subscribe();
    }, 20));
  }
  render() {
    return u`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
      </div>

      <div class="mb-show">
      <div class="form-subs">
      <div class="sub-form-title" .innerHTML="${this.activityInfo.guideText}"></div>
      <form>
        <div class="form-items">
          <input
            @input="${this.handleInputEmail}"
            .value="${this.subEmail}"
            @blur="${this.handleValidEmail}" id="jackery-field-0" class="field__ref native-input sc-jackery-field" placeholder="*${window.i18n.t("form.placeholder.email")}" name="jackery-field-0" spellcheck="false" type="email" autocapitalize="off" autocomplete="valid-field__email" autocorrect="off">
            <div id="email-error" class="form-field--error">${window.i18n.t("form.validation.email")}</div>
        </div>

        <div class="form-items">
          <button
            class="form-button text-overflow-1 ${this.isDrawing ? "loading" : ""}"
            type="submit"
            @click="${this.handleSubmit}"
            style="
              --text-color:${this.isSubscribed ? this.activityInfo.bgValue2 || this.activityInfo.btnColor : this.activityInfo.btnColor};
              --btn-bg:${this.isSubscribed ? this.activityInfo.bgValue3 || this.activityInfo.btnBgValue : this.activityInfo.btnBgValue};
            "
          >${this.lockSubmit ? u`<jbc-loading></jbc-loading>` : this.isSubscribed ? this.activityInfo.btnTitleLogin || this.activityInfo.btnTitle : this.activityInfo.btnTitle}</button>
        </div>

        <div class="form-items checkbox-content">
          <label class="checkbox-wrapper">
              <div class="label-text-wrapper" part="label" .innerHTML="${this.activityInfo.privacyAgreementText}"></div>
              <input .checked=${this.agreePrivacy} @change="${this.handlePrivacyChange}" type="checkbox">
              <div class="native-wrapper">
                  <svg class="checkbox-icon" viewBox="0 0 24 24" part="container">
                      <path d="M1.73,12.91 8.1,19.28 22.79,4.59" part="mark"></path>
                  </svg>
              </div>
          </label>

          <div id="privacy-error" class="form-field--error">${window.i18n.t("form.validation.termsOfService")}</div>
        </div>
      </form>
    </div>


        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
F.styles = [Mt, ke, We, k`
    :host{
      width: 606px;
    }
    jbc-loading {
      height: 1.4rem;
      width: 1.4rem;
      display: block;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 606px;
      height: 345px;
    }
    .prize {
      padding: 8px 8px 6px;
    }
    .btn {
      --btn-size:36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
      border-radius: 16px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 42.0833333333333vw;
        height: 23.9583333333333vw;
      }
      .prize {
        padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
        border-radius: 1.11111111111111vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }

      .mb-show {
        display: block;width: 100%;
      }

      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 53.3333333333333vw;
      }

      .prize {
        height: 25.3333333333333vw;
      }

      .btn {
        --btn-size: 4.8vw;
        margin-top: 2.13333333333333vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 12.6666666666667vw;
        border-radius: 2.13333333333333vw;
      }

      .btn::before{
        content: " ";
        display: block;
        width: 8.93333333333333vw;
        height: 5.2vw;
        background-image: url('https://f2e-cdn.jackery.com/@jackery/biz-components/latest/dist/assets/lucky-draw/icon-corner.png');
        background-size: cover;
        position: absolute;
        top: 0.666666666666667vw;
        left: 0.8vw;
        z-index:1;
      }

      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
J([
  g({ type: Number, reflect: !0 })
], F.prototype, "currentIndex", 2);
J([
  g({ type: Array, reflect: !1 })
], F.prototype, "prizes", 2);
J([
  g({ type: Boolean, reflect: !0 })
], F.prototype, "isDrawing", 2);
J([
  g({ type: Boolean, reflect: !0 })
], F.prototype, "isLogined", 2);
J([
  g({ type: Boolean })
], F.prototype, "lockSubmit", 2);
J([
  g({ type: Boolean })
], F.prototype, "isSubscribed", 2);
J([
  g({ type: Object, reflect: !1 })
], F.prototype, "activityInfo", 2);
J([
  ft("#privacy-error")
], F.prototype, "privacyError", 2);
J([
  ft("#email-error")
], F.prototype, "emailError", 2);
J([
  m()
], F.prototype, "subEmail", 2);
J([
  m()
], F.prototype, "agreePrivacy", 2);
F = J([
  _("sub-right-six-prizes")
], F);
var Xr = Object.defineProperty, Qr = Object.getOwnPropertyDescriptor, W = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Qr(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Xr(e, i, s), s;
};
let N = class extends A {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.lockSubmit = !1, this.isSubscribed = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: "",
      privacyAgreementText: "",
      btnTitleLogin: "",
      bgValue2: "",
      bgValue3: ""
    }, this.subEmail = "", this.agreePrivacy = !1;
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("succes-callback", {
      detail: {
        email: this.subEmail
      },
      bubbles: !0,
      // 允许事件冒泡到父组件
      composed: !0
      // 允许跨越 Shadow DOM 边界
    }));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  async connectedCallback() {
    super.connectedCallback(), this.agreePrivacy = !!V("__subs_draw_agree"), V("__subs_draw_agree") && (this.subEmail = V("__subs_draw_agree") || "", this.isSubscribed = !0);
  }
  handleValidEmail() {
    var t, e;
    return !this.subEmail || !Qt(this.subEmail) ? ((t = this.emailError) == null || t.classList.add("active"), !1) : ((e = this.emailError) == null || e.classList.remove("active"), !0);
  }
  handleValidPrivacy() {
    var t, e;
    return this.agreePrivacy ? ((e = this.privacyError) == null || e.classList.remove("active"), !0) : ((t = this.privacyError) == null || t.classList.add("active"), !1);
  }
  handlePrivacyChange(t) {
    this.agreePrivacy = t.target.checked, this.handleValidPrivacy();
  }
  handleInputEmail(t) {
    this.subEmail = t.target.value;
  }
  async subscribe() {
    var t, e, i, o;
    try {
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.subEmail,
        tags: "GGCJ-Subscribe,NEWGG-Subscribe",
        isSync: !1,
        componentName: "宫格订阅",
        pageUrl: window.location.href
      }, n = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await j.post(n, s, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || "US",
          Lang: ((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang) || "en"
        }
      }), { code: l, success: d, message: h } = a.data;
      d ? l === 200 && (this.isSubscribed = !0, this.startDraw(), be("__subs_draw_agree", this.subEmail, 1e3 * 60 * 60 * 24)) : $(h, "danger"), this.lockSubmit = !1;
    } catch {
      this.lockSubmit = !1, $(window.i18n.t("pages.error.default"), "danger");
    }
  }
  handleSubmit(t) {
    if (t.preventDefault(), this.lockSubmit) return;
    const e = this.handleValidEmail(), i = this.handleValidPrivacy();
    e && i && (this.lockSubmit = !0, setTimeout(() => {
      this.subscribe();
    }, 20));
  }
  render() {
    return u`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;">
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
      </div>
      <div class="mb-show">
      <div class="form-subs">
      <div class="sub-form-title" .innerHTML="${this.activityInfo.guideText}"></div>
      <form>
        <div class="form-items">
          <input
            @input="${this.handleInputEmail}"
            .value="${this.subEmail}"
            @blur="${this.handleValidEmail}" id="jackery-field-0" class="field__ref native-input sc-jackery-field" placeholder="*${window.i18n.t("form.placeholder.email")}" name="jackery-field-0" spellcheck="false" type="email" autocapitalize="off" autocomplete="valid-field__email" autocorrect="off">
            <div id="email-error" class="form-field--error">${window.i18n.t("form.validation.email")}</div>
        </div>

        <div class="form-items">
          <button
            class="form-button text-overflow-1 ${this.isDrawing ? "loading" : ""}"
            type="submit"
            @click="${this.handleSubmit}"
            style="
              --text-color:${this.isSubscribed ? this.activityInfo.bgValue2 || this.activityInfo.btnColor : this.activityInfo.btnColor};
              --btn-bg:${this.isSubscribed ? this.activityInfo.bgValue3 || this.activityInfo.btnBgValue : this.activityInfo.btnBgValue};
            "
          >${this.lockSubmit ? u`<jbc-loading></jbc-loading>` : this.isSubscribed ? this.activityInfo.btnTitleLogin || this.activityInfo.btnTitle : this.activityInfo.btnTitle}</button>
        </div>

        <div class="form-items checkbox-content">
          <label class="checkbox-wrapper">
              <div class="label-text-wrapper" part="label" .innerHTML="${this.activityInfo.privacyAgreementText}"></div>
              <input .checked=${this.agreePrivacy} @change="${this.handlePrivacyChange}" type="checkbox">
              <div class="native-wrapper">
                  <svg class="checkbox-icon" viewBox="0 0 24 24" part="container">
                      <path d="M1.73,12.91 8.1,19.28 22.79,4.59" part="mark"></path>
                  </svg>
              </div>
          </label>

          <div id="privacy-error" class="form-field--error">${window.i18n.t("form.validation.termsOfService")}</div>
        </div>
      </form>
    </div>
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? u`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
N.styles = [Mt, ke, We, k`
    :host{
      width: 400px;
    }
    .mb-show {
      display: none;
    }
    jbc-loading {
      height: 1.4rem;
      width: 1.4rem;
      display: block;
    }
    .grid {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 400px;
      height: 345px;
    }
    .btn {
      --btn-size: 36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 27.7777777777778vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 27.7777777777778vw;
        height: 23.9583333333333vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      .mb-show {
        display: block;width: 100%;
      }
      :host{
        width: 80.8vw;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 53.3333333333333vw;
        height: 52.2666666666667vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 5.33333333333333vw;
        margin-top: 2.66666666666667vw;
        padding: 0 2.66666666666667vw;
        width: 53.3333333333333vw;
        font-size: var(--btn-size);
        height: 12.8vw;
      }
      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
W([
  g({ type: Number, reflect: !0 })
], N.prototype, "currentIndex", 2);
W([
  g({ type: Array, reflect: !1 })
], N.prototype, "prizes", 2);
W([
  g({ type: Boolean, reflect: !0 })
], N.prototype, "isDrawing", 2);
W([
  g({ type: Boolean, reflect: !0 })
], N.prototype, "isLogined", 2);
W([
  g({ type: Boolean })
], N.prototype, "lockSubmit", 2);
W([
  g({ type: Boolean })
], N.prototype, "isSubscribed", 2);
W([
  g({ type: Object, reflect: !1 })
], N.prototype, "activityInfo", 2);
W([
  ft("#privacy-error")
], N.prototype, "privacyError", 2);
W([
  ft("#email-error")
], N.prototype, "emailError", 2);
W([
  m()
], N.prototype, "subEmail", 2);
W([
  m()
], N.prototype, "agreePrivacy", 2);
N = W([
  _("sub-right-four-prizes")
], N);
var Zr = Object.defineProperty, tn = Object.getOwnPropertyDescriptor, E = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? tn(e, i) : e, r = t.length - 1, n; r >= 0; r--)
    (n = t[r]) && (s = (o ? n(e, i, s) : n(s)) || s);
  return o && s && Zr(e, i, s), s;
};
let z = class extends A {
  constructor() {
    super(...arguments), this.platform = "membership", this.actId = "", this.shopifyShopId = "", this.customTag = "", this.showLuckyDraw = !1, this.prizes = [], this.currentIndex = -1, this.targetIndex = -1, this.isDrawing = !1, this.isLogined = !1, this.showRulesModal = !1, this.showTKSModal = !1, this.showXNKModal = !1, this.showYHQModal = !1, this.showJFModal = !1, this.timer = void 0, this.isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0, this.drawStartTime = 0, this.email = "", this.activityInfo = {
      shopId: "",
      busId: "",
      backgroundColor: "",
      pcBgImg: "",
      mbBgImg: "",
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      balance: 0,
      btnTitleNologin: "",
      guideText: "",
      joinTimes: 0,
      joinDays: 0,
      joinMaxText: "",
      ruleTitle: "",
      ruleText: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      tksTitle: "",
      tksImg: "",
      tipsOther: "",
      btnTitleLogin: "",
      activityInfo: "",
      bgValue2: "",
      bgValue3: ""
    }, this.drawResults = {
      itemId: 1,
      balance: 1,
      btnText: "按钮",
      image: "",
      btnLink: "https://www.jackery.com/",
      tipsLoseWindowTitle: "未中奖-弹窗标题",
      tipsLoseContent: "未中奖-备注文案",
      tipsWindowTitle: "中奖-优惠券和礼品卡标题",
      tipsCopyBtnText: "中奖-复制按钮code",
      code: "",
      tipsCopySuccessText: "中奖-复制成功提示语",
      tipsRuleBtnText: "中奖-规则按钮rules",
      tipsWindowTitleVc: "中奖-虚拟卡标题",
      tipsContentVc: "中奖-虚拟卡备注文案",
      tipsRuleBtnTextVc: "中奖-虚拟卡-规则按钮rules",
      tipsWindowTitlePoint: "中奖-积分标题",
      tipsContentPoint: "中奖-积分备注文案",
      welfareType: "default",
      couponRules: ""
    };
  }
  async connectedCallback() {
    var e;
    super.connectedCallback(), this.isLogined = ct("_jk_user") != null, await this.getActivityInfo();
    const { lucky_act_id: t } = Ke();
    t && setTimeout(() => {
      const i = document.querySelector(`jbc-subscribe-draw[actId=${t}]`) || document.querySelector("jbc-subscribe-draw");
      window.scrollTo({ top: (i == null ? void 0 : i.offsetTop) || 0 });
    }, 1e3);
    try {
      window.i18n.setCurrLanguage(((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.lang) || "en");
    } catch (i) {
      console.error(i);
    }
  }
  // 动画执行
  startDraw() {
    this.isDrawing = !0, this.drawStartTime = Date.now(), this.currentIndex = 0, this.requestUpdate();
    const t = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate();
    }, 200), e = setInterval(() => {
      this.targetIndex != -1 && (clearInterval(t), clearInterval(e), this.finalAnimation());
    }, 200);
    this.joinActivity(t, e);
  }
  handleStart(t) {
    this.email = t.detail.email, this.startDraw();
  }
  // 最后选中动画
  finalAnimation() {
    const t = this.targetIndex, e = this.prizes.length;
    let i = 0;
    t > this.currentIndex ? i = t - this.currentIndex : t === this.currentIndex ? i = 0 : i = e + t - this.currentIndex;
    const o = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate(), i--, i <= 0 && (clearInterval(o), this.currentIndex = t, this.getActivityInfo(), this.requestUpdate(), this.isDrawing = !1, this.showDialog(this.drawResults.welfareType), this.newCustomEvent());
    }, 200);
  }
  // 重置动画
  resetAnimation(t, e) {
    t && clearInterval(t), e && clearInterval(e), this.currentIndex = -1, this.targetIndex = -1, this.requestUpdate();
  }
  // 显示 规则弹窗
  showRulesDialog() {
    var i, o;
    this.showRulesModal = !0;
    const t = (i = this.shadowRoot) == null ? void 0 : i.querySelector("jds-dialog#rulesDialog"), e = (o = t == null ? void 0 : t.querySelector(".dialog-title")) == null ? void 0 : o.innerText;
    P({
      module: "Lottery button",
      // 固定模块文案
      button_name: e,
      // 规则按钮文案
      status: this.isLogined ? "logged_in" : "tourist"
      // 登录状态
    });
  }
  // 关闭所有弹窗，并重置动画 + 移除事件
  closeAllDialog() {
    ["showRulesModal", "showTKSModal", "showYHQModal", "showJFModal", "showXNKModal"].forEach((e) => this[e] = !1), this.resetAnimation(), this.removeRulesEvent();
  }
  // 处理对话框关闭事件
  handleDialogClose() {
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      // "button_name": this.drawResults.btnText, // 奖品按钮文案
      button_name: "close"
      // 关闭按钮文案
    }), this.closeAllDialog();
  }
  // 处理对话框跳转链接
  handleDialogLink() {
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.btnText,
      // 奖品按钮文案
      link: this.drawResults.btnLink
      // 跳转链接
    });
  }
  // 自定义抽奖结束事件用于外部监听
  newCustomEvent() {
    const t = new CustomEvent("luckyDrawDone", {
      detail: {
        status: !0
      }
    });
    document.dispatchEvent(t);
  }
  // 获取活动基本信息
  async getActivityInfo() {
    var s, r, n;
    const e = `${((s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/getCjActivityInfo`, i = {};
    !this.platform || this.platform === "membership" ? i.shopifyShopId = this.shopifyShopId : (i.platform = this.platform, i.actId = this.actId, i.shopifyShopId = this.shopifyShopId, i.platform === "member" && delete i.actId), i.showType = "grid", i.actType = "subscribe";
    const o = ct("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const a = await j.get(e, {
        params: i,
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: o,
          Cr: ((r = window == null ? void 0 : window.pageInfo) == null ? void 0 : r.cr) || "US",
          Lang: ((n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.lang) || "en"
        }
      }), { data: l, success: d } = a.data;
      if (d && l !== null) {
        const { bgValue: h, bgValue1: p, items: w, ...x } = l;
        this.activityInfo = {
          ...x,
          pcBgImg: h,
          mbBgImg: p
        }, this.prizes = w, this.showLuckyDraw = !0;
      } else
        this.showLuckyDraw = !1;
    } catch (a) {
      this.showLuckyDraw = !1, a.code === "ECONNABORTED" ? console.error("请求超时:", a.message) : console.error("接口异常:", a);
    }
  }
  // 抽奖接口
  async joinActivity(t, e) {
    var n, a, l;
    const o = `${((n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/cjActivityJoin`, s = {};
    !this.platform || this.platform === "membership" ? (s.platform = "member", s.actId = this.activityInfo.busId) : (s.platform = this.platform, s.actId = this.actId), s.shopId = this.activityInfo.shopId, s.actType = "subscribe", s.showType = "grid", s.email = this.email;
    const r = ct("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const d = await j.post(o, s, {
        withCredentials: !0,
        // 允许携带 Cookies
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: r,
          Cr: ((a = window == null ? void 0 : window.pageInfo) == null ? void 0 : a.cr) || "US",
          Lang: ((l = window == null ? void 0 : window.pageInfo) == null ? void 0 : l.lang) || "en"
        }
      }), { data: h, code: p, success: w, message: x } = d.data;
      if (w && h !== null)
        if (this.drawResults = h, this.drawResults.welfareType) {
          await et(Math.max(Date.now() - this.drawStartTime, 3e3));
          const f = h.itemId;
          this.targetIndex = this.prizes.findIndex((b) => b.itemId === f);
        } else
          await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
      else
        await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(x, "warning"), this.resetAnimation(t, e), p === 500209 && P({
          module: "Lottery button",
          // 固定模块文案
          button_name: "Lack of points",
          // 积分不足文案
          status: "logged_in"
          // 登录状态
        });
    } catch {
      await et(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, $(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
    }
  }
  // 中奖后，展示对应弹窗
  showDialog(t) {
    const i = {
      coupon: "showYHQModal",
      gift_card: "showYHQModal",
      point: "showJFModal",
      virtual_card: "showXNKModal",
      default: "showTKSModal"
    }[t] || "showTKSModal";
    this[i] = !0, this.registerRulesEvent(), P({
      module: "Awards",
      // 固定模块文案
      position: t !== "default" ? this.prizes[this.currentIndex].name : "Thanks！"
      // 奖品名称
    });
  }
  // 复制 code 码
  copyAndUseCode() {
    this.drawResults.code && (Ve(this.drawResults.code).then(() => $(this.drawResults.tipsCopySuccessText, "success")).catch(() => console.log("Copy Failed")), P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.code
      // 奖品券码
    }));
  }
  // 注册 rules 交互事件
  registerRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (this.mouseEnterEvent = this.mouseEnterEvent.bind(this), this.mouseLeaveEvent = this.mouseLeaveEvent.bind(this), this.clearTimeoutEvent = this.clearTimeoutEvent.bind(this), t.addEventListener("mouseover", this.clearTimeoutEvent), t.addEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.addEventListener("touchend", this.mouseEnterEvent) : o.addEventListener("mouseenter", this.mouseEnterEvent), o.addEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 移除 rules 交互事件
  removeRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (t.removeEventListener("mouseover", this.clearTimeoutEvent), t.removeEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.removeEventListener("touchend", this.mouseEnterEvent) : o.removeEventListener("mouseenter", this.mouseEnterEvent), o.removeEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 清除 timer 事件
  clearTimeoutEvent() {
    clearTimeout(this.timer);
  }
  // Rules 规则，鼠标移入 | touchend
  mouseEnterEvent(t) {
    var l, d, h, p;
    clearTimeout(this.timer);
    const e = t.target, i = (l = this.shadowRoot) == null ? void 0 : l.querySelector(".rules-content");
    i.style.display = "block";
    const { left: o, top: s, bottom: r } = e.getBoundingClientRect(), { width: n, height: a } = i.getBoundingClientRect();
    if (this.isTouchDevice) {
      const w = (h = (d = e.closest("jds-dialog")) == null ? void 0 : d.shadowRoot) == null ? void 0 : h.querySelector('[part="dialog-container"]'), x = Math.max(((p = w == null ? void 0 : w.getBoundingClientRect()) == null ? void 0 : p.left) || 0, 0), f = Math.max(s - a - 5, 0);
      i.style.left = x + "px", i.style.top = f + "px";
    } else
      i.style.left = Math.max(o - n - 20, 0) + "px", i.style.top = Math.max(r - a + 26, 0) + "px";
    P({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.tipsRuleBtnText
      // 奖品规则按钮文案
    });
  }
  // Rules 规则，鼠标移出
  mouseLeaveEvent() {
    var e;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    this.timer = setTimeout(() => {
      t.style.display = "none";
    }, 150);
  }
  // 获取对应抽奖区DOM
  getPrizeContent() {
    switch (this.prizes.length) {
      case 8:
        return u`
        <sub-right-eight-prizes
          @succes-callback=${this.handleStart}
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
         ></sub-right-eight-prizes>`;
      case 6:
        return u`
        <sub-right-six-prizes
          @succes-callback=${this.handleStart}
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
        ></sub-right-six-prizes>`;
      case 4:
        return u`
        <sub-right-four-prizes
          @succes-callback=${this.handleStart}
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
        ></sub-right-four-prizes>`;
      default:
        return u``;
    }
  }
  render() {
    return u`${this.showLuckyDraw ? u`
          <div class="wrapper" style="background-color:${this.activityInfo.backgroundColor};">
            <div class="jds-container">
              <!-- 背景图 -->
              <lucky-bg-img class="bg-img" .issub="${!0}" .pcBgImg="${this.activityInfo.pcBgImg}" .mbBgImg="${this.activityInfo.mbBgImg}" alt="Lucky Draw Background Image"></lucky-bg-img>

              <!-- 左侧 -->
              <sub-left-info
                @succes-callback=${this.handleStart}
                .isDrawing="${this.isDrawing}"
                @show-rules-dialog="${this.showRulesDialog}" .activityInfo="${this.activityInfo}"></sub-left-info>

              <!-- 右侧抽奖 -->
              <div class="rg-box">
                ${this.getPrizeContent()}
              </div>
            </div>

            <!-- 规则弹窗 -->
            <jds-dialog id="rulesDialog" ?open=${this.showRulesModal} style="--jds-dialog-width:var(--jds-rules-dialog-width)" critical @closed="${this.closeAllDialog}">
              <h2 class="dialog-title" .innerHTML="${this.activityInfo.ruleTitle}"></h2>
              <section class="dialog-content">
                <div class="detail jds-rte font-regular" .innerHTML="${this.activityInfo.ruleText}"></div>
              </section>
            </jds-dialog>

            <!-- 中奖弹窗 - 谢谢参与 -->
            <jds-dialog id="thsDialog" ?open=${this.showTKSModal} style="--jds-dialog-width:var(--jds-prize-dialog-width)" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips ths-tips">
                <div class="dialog-title">${this.drawResults.tipsLoseWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsLoseContent}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button name="primary-action" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 虚拟卡 -->
            <jds-dialog id="xnkDialog" ?open=${this.showXNKModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);"  critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips xnk-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitleVc}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  <div class="notes">${this.drawResults.tipsContentVc}</div>
                  ${this.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnTextVc}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 优惠券礼品卡 -->
            <jds-dialog id="yhqDialog" ?open=${this.showYHQModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips yhq-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  ${this.drawResults.code ? u`<div class="code" @click="${this.copyAndUseCode}">${this.drawResults.tipsCopyBtnText}:${this.drawResults.code}</div>` : ""}
                  ${this.drawResults.couponRules ? u`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnText}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button class="btn" href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button class="btn" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 积分 -->
            <jds-dialog id="jfDialog" ?open=${this.showJFModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips jf-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitlePoint}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsContentPoint}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? u`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : u`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 规则 - tooltip -->
            ${this.drawResults.couponRules ? u`
                  <div class="rules-content">
                    <h4 class="title">${this.drawResults.tipsRuleBtnText}</h4>
                    <div class="detail jds-rte font-regular" .innerHTML="${this.drawResults.couponRules}"></div>
                  </div>
                ` : ""}
          </div>
        ` : ""}`;
  }
};
z.styles = [H(Or), Mt, Ur, Fr, Nr];
E([
  g({ type: String, reflect: !0 })
], z.prototype, "platform", 2);
E([
  g({ type: String, reflect: !0 })
], z.prototype, "actId", 2);
E([
  g({ type: String, reflect: !0 })
], z.prototype, "shopifyShopId", 2);
E([
  g({ type: String, reflect: !0 })
], z.prototype, "customTag", 2);
E([
  g({ type: Boolean })
], z.prototype, "showLuckyDraw", 2);
E([
  g({ type: Array })
], z.prototype, "prizes", 2);
E([
  g({ type: Number })
], z.prototype, "currentIndex", 2);
E([
  g({ type: Number })
], z.prototype, "targetIndex", 2);
E([
  g({ type: Boolean })
], z.prototype, "isDrawing", 2);
E([
  g({ type: Boolean })
], z.prototype, "isLogined", 2);
E([
  m()
], z.prototype, "showRulesModal", 2);
E([
  m()
], z.prototype, "showTKSModal", 2);
E([
  m()
], z.prototype, "showXNKModal", 2);
E([
  m()
], z.prototype, "showYHQModal", 2);
E([
  m()
], z.prototype, "showJFModal", 2);
E([
  m()
], z.prototype, "timer", 2);
E([
  m()
], z.prototype, "isTouchDevice", 2);
E([
  m()
], z.prototype, "drawStartTime", 2);
E([
  m()
], z.prototype, "email", 2);
E([
  g({ type: Object })
], z.prototype, "activityInfo", 2);
E([
  g({ type: Object })
], z.prototype, "drawResults", 2);
z = E([
  _("jbc-subscribe-draw")
], z);
export {
  T as JbcLuckyDraw,
  X as JbcLuckyScratch,
  z as JbcSubscribeDraw
};
//# sourceMappingURL=index.es.js.map
